import { useState, useEffect, useRef, useCallback, useMemo, type ChangeEvent } from 'react';
import type { Item, ItemOccurrenceOverride, Tag, CalendarImportSource, GoogleCalendarCandidate } from '@chaos-concierge/shared';
import type { SpaceWithMembers, Invite, SpaceRole, ItemPatch } from './store/types';
import { ApiStore } from './store/ApiStore';
import { OfflineSyncStore } from './store/OfflineSyncStore';
import { useDictation } from './useDictation';
import { useDragReorder } from './useDragReorder';
import { pruneHiddenCalendarKeys } from './calendarVisibility';
import {
  clearTaskAppBadge,
  getNotificationPermission,
  updateTaskAppBadge,
  type AppNotificationPermission,
} from './appBadge';
import { disablePushNotifications, enablePushNotifications, getCurrentPushSubscription, getDeviceId, pushNotificationsSupported } from './pushNotifications';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MON_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const KIND_LABEL: Record<string, string> = { note: 'Note', task: 'Task', event: 'Event', list: 'List' };
const TAG_COLORS = [
  { name: 'Red',     value: '#e74c3c' }, { name: 'Crimson',  value: '#c0392b' },
  { name: 'Orange',  value: '#e67e22' }, { name: 'Amber',    value: '#f39c12' },
  { name: 'Yellow',  value: '#f1c40f' }, { name: 'Lime',     value: '#a8c51e' },
  { name: 'Green',   value: '#27ae60' }, { name: 'Emerald',  value: '#1abc9c' },
  { name: 'Teal',    value: '#16a085' }, { name: 'Cyan',     value: '#00bcd4' },
  { name: 'Sky',     value: '#2980b9' }, { name: 'Blue',     value: '#1565c0' },
  { name: 'Indigo',  value: '#3949ab' }, { name: 'Purple',   value: '#8e44ad' },
  { name: 'Violet',  value: '#6a1b9a' }, { name: 'Magenta',  value: '#ad1457' },
  { name: 'Pink',    value: '#e91e63' }, { name: 'Rose',     value: '#e57373' },
  { name: 'Brown',   value: '#795548' }, { name: 'Slate',    value: '#607d8b' },
];
const LIST_PRESETS = ['Grocery', 'Shopping', 'To-Do', 'Costco'];
const AUTO_REFRESH_INTERVAL_MS = 30_000;
const WAKE_REFRESH_MIN_INTERVAL_MS = 15_000;
const ALERTS_ENABLED_KEY = 'wingmate:alerts-enabled';
const WEEK_START_HOUR_KEY = 'wingmate:week-start-hour';
const THEME_PREFERENCE_KEY = 'wingmate:theme-preference';
const MONTH_SPAN_KEY = 'wingmate:month-span';
const HIDDEN_CHECKED_LISTS_KEY = 'wingmate:hidden-checked-lists';
// One-time migration flag: backfill timed parents' times into existing untimed child tasks.
// Stored per-origin, so prod and staging each run the migration exactly once per browser.
const CHILD_TIME_BACKFILL_KEY = 'wingmate:child-time-backfill:v1';
type ThemePreference = 'system' | 'light' | 'dark';

// Calendar overlay: colour used for personal items, and persistence of which
// calendars the user has hidden from the overlay.
const PERSONAL_CALENDAR_COLOR = '#8a94a6';
const HIDDEN_CALENDARS_KEY = 'wingmate:hidden-calendars';
function loadHiddenCalendars(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_CALENDARS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function saveHiddenCalendars(set: Set<string>): void {
  try { localStorage.setItem(HIDDEN_CALENDARS_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}
function loadHiddenCheckedLists(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_CHECKED_LISTS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function saveHiddenCheckedLists(set: Set<string>): void {
  try { localStorage.setItem(HIDDEN_CHECKED_LISTS_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}
const importCalKey = (importId: string) => `import:${importId}`;

const pad = (n: number) => String(n).padStart(2, '0');
const keyOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const keyOfDate = (dt: Date) => keyOf(dt.getFullYear(), dt.getMonth(), dt.getDate());
const todayKey = () => keyOfDate(new Date());
function parseKey(k: string): Date { const [y, m, d] = k.split('-').map(Number); return new Date(y, m - 1, d); }
function addDaysToKey(k: string, days: number): string {
  const date = parseKey(k);
  date.setDate(date.getDate() + days);
  return keyOfDate(date);
}
function prettyDate(k: string): string { return parseKey(k).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }); }
function shortDate(k: string): string { return parseKey(k).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
function fmtReminder(v: string): string {
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function fmtTime(v: string): string {
  const [h, m] = v.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return v;
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function minutesOfTime(v: string | null | undefined): number | null {
  if (!v) return null;
  const [h, m] = v.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

const REMINDER_PRESETS = [
  { value: 'off', label: 'Off', offsetMinutes: null },
  { value: '0', label: 'At time', offsetMinutes: 0 },
  { value: '5', label: '5 min before', offsetMinutes: 5 },
  { value: '10', label: '10 min before', offsetMinutes: 10 },
  { value: '30', label: '30 min before', offsetMinutes: 30 },
  { value: '60', label: '1 hour before', offsetMinutes: 60 },
  { value: 'custom', label: 'Custom', offsetMinutes: null },
] as const;
const RELATIVE_DUE_OPTIONS = [
  { value: '', label: 'No task date', offsetDays: null },
  { value: '0', label: 'Event day', offsetDays: 0 },
  { value: '1', label: '1 day before', offsetDays: 1 },
  { value: '2', label: '2 days before', offsetDays: 2 },
  { value: '7', label: '1 week before', offsetDays: 7 },
  { value: 'custom', label: 'Custom date', offsetDays: null },
] as const;

function localDateTimeInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function scheduledDateTimeValue(dateKey: string | null | undefined, time: string | null | undefined, offsetMinutes = 0): string | null {
  if (!dateKey || !time) return null;
  const date = parseKey(dateKey);
  const minutes = minutesOfTime(time);
  if (minutes === null) return null;
  date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  date.setMinutes(date.getMinutes() - offsetMinutes);
  return localDateTimeInputValue(date);
}

function eventTimeLabel(item: Pick<Item, 'time' | 'endTime' | 'endDate' | 'date'>): string {
  if (!item.time) return '';
  if (!item.endTime) return `at ${fmtTime(item.time)}`;
  const crossesDate = Boolean(item.endDate && item.endDate !== item.date);
  return `${fmtTime(item.time)}-${fmtTime(item.endTime)}${crossesDate ? ` ${item.endDate}` : ''}`;
}

function fmtSyncTime(v: number): string {
  return new Date(v).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function normalizeItems(data: Item[]): Item[] {
  return data.map((item) => {
    const base = {
      ...item,
      endTime: item.endTime ?? null,
      endDate: item.endDate ?? null,
      tagIds: item.tagIds ?? [],
      dueOffsetDays: item.kind === 'task' ? item.dueOffsetDays ?? null : null,
      needsAttention: item.kind === 'task' ? item.needsAttention ?? false : false,
      hidden: item.hidden ?? false,
      lastCompletedAt: item.lastCompletedAt ?? null,
      completedCount: item.completedCount ?? 0,
      completionHistory: item.completionHistory ?? [],
      priceHistory: item.priceHistory ?? [],
    };
    if (item.labelId && (!item.tagIds || item.tagIds.length === 0)) {
      return { ...base, tagIds: [item.labelId] };
    }
    return base;
  });
}

function loadAlertsEnabled(permission: AppNotificationPermission): boolean {
  try {
    const saved = localStorage.getItem(ALERTS_ENABLED_KEY);
    if (saved !== null) return saved === 'true';
  } catch {
    // Ignore storage failures; the permission state remains the best available signal.
  }
  return permission === 'granted';
}

function saveAlertsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ALERTS_ENABLED_KEY, String(enabled));
  } catch {
    // Alert preferences are helpful, not critical.
  }
}

function loadMonthSpan(): MonthSpan {
  try {
    const saved = localStorage.getItem(MONTH_SPAN_KEY);
    if (saved === '2') return 2;
  } catch {
    // One month is the most compatible fallback.
  }
  return 1;
}

function saveMonthSpan(span: MonthSpan): void {
  try {
    localStorage.setItem(MONTH_SPAN_KEY, String(span));
  } catch {
    // Display density is optional; ignore storage failures.
  }
}

function loadThemePreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(THEME_PREFERENCE_KEY);
    if (saved === 'system' || saved === 'light' || saved === 'dark') return saved;
  } catch {
    // System remains the safest fallback when storage is unavailable.
  }
  return 'system';
}

function systemThemeIsDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function resolveThemePreference(preference: ThemePreference): 'light' | 'dark' {
  return preference === 'system' ? (systemThemeIsDark() ? 'dark' : 'light') : preference;
}

function applyThemePreference(preference: ThemePreference): void {
  const root = document.documentElement;
  const effective = resolveThemePreference(preference);
  root.dataset.themePreference = preference;
  root.dataset.effectiveTheme = effective;
  if (preference === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.dataset.theme = preference;
  }
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', effective === 'dark' ? '#10191B' : '#FCF5EC');
}

const STYLES = `
.pl { --ink:#17363B; --ink-soft:#667679; --line:#DDD4CA; --line-soft:#EEE6DD;
  --paper:#FCF5EC; --paper-raised:#F7F2EC; --surface:#FFFFFF; --surface-raised:#F7F2EC;
  --mark:#FF5C3D; --mark-soft:rgba(255,92,61,.20);
  --brand-teal:#086574; --on-accent:#FFFFFF; --badge-text:#FFFFFF; --placeholder:#9AA3AF;
  --sans:'Inter',system-ui,-apple-system,sans-serif;
  --mono:'Manrope',system-ui,sans-serif;
  --hand:'Caveat',cursive;
  color-scheme:light;
  font-family:var(--sans); color:var(--ink); background:var(--paper);
  min-height:100%; padding:20px; box-sizing:border-box; line-height:1.45;
  padding-top:max(20px,env(safe-area-inset-top));
  padding-bottom:max(20px,env(safe-area-inset-bottom));
  padding-left:max(20px,env(safe-area-inset-left));
  padding-right:max(20px,env(safe-area-inset-right)); }
.pl *, .pl *::before, .pl *::after { box-sizing:border-box; }
.pl button { font-family:inherit; cursor:pointer; }
.pl input { font-family:inherit; }
.pl :focus-visible { outline:2px solid var(--mark); outline-offset:2px; border-radius:3px; }
@media (prefers-color-scheme:dark) {
  .pl { --ink:#F8F0E7; --ink-soft:#B8C4C3; --line:#344548; --line-soft:rgba(52,69,72,.52);
    --paper:#10191B; --paper-raised:#203034; --surface:#182427; --surface-raised:#203034;
    --mark:#FF7A61; --mark-soft:rgba(255,122,97,.18);
    --brand-teal:#7FD7DD; --on-accent:#10191B; --badge-text:#F8F0E7; --placeholder:#829291;
    color-scheme:dark; }
}
[data-theme="dark"] .pl, .pl[data-theme="dark"] { --ink:#F8F0E7; --ink-soft:#B8C4C3; --line:#344548; --line-soft:rgba(52,69,72,.52);
  --paper:#10191B; --paper-raised:#203034; --surface:#182427; --surface-raised:#203034;
  --mark:#FF7A61; --mark-soft:rgba(255,122,97,.18);
  --brand-teal:#7FD7DD; --on-accent:#10191B; --badge-text:#F8F0E7; --placeholder:#829291;
  color-scheme:dark; }
[data-theme="light"] .pl, .pl[data-theme="light"] { --ink:#17363B; --ink-soft:#667679; --line:#DDD4CA; --line-soft:#EEE6DD;
  --paper:#FCF5EC; --paper-raised:#F7F2EC; --surface:#FFFFFF; --surface-raised:#F7F2EC;
  --mark:#FF5C3D; --mark-soft:rgba(255,92,61,.20);
  --brand-teal:#086574; --on-accent:#FFFFFF; --badge-text:#FFFFFF; --placeholder:#9AA3AF;
  color-scheme:light; }

.pl-search-wrap { position:relative; flex:1; min-width:180px; max-width:320px; }
.pl-search-input { width:100%; border:1px solid var(--line); border-radius:9px; padding:8px 12px 8px 32px; font-size:14px; color:var(--ink); background:var(--surface); font-family:inherit; transition:border-color .15s; }
.pl-search-input:focus { border-color:var(--ink); outline:none; }
.pl-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:var(--ink-soft); font-size:14px; pointer-events:none; }
.pl-search-clear { position:absolute; right:8px; top:50%; transform:translateY(-50%); border:none; background:transparent; color:var(--ink-soft); font-size:16px; line-height:1; padding:2px; cursor:pointer; }
.pl-search-clear:hover { color:var(--ink); }
.pl-search-results { position:absolute; top:calc(100% + 6px); left:0; right:0; min-width:340px; background:var(--surface-raised); border:1px solid var(--line); border-radius:12px; box-shadow:0 14px 32px rgba(24,27,32,.16); z-index:50; max-height:420px; overflow-y:auto; }
.pl-search-header { padding:10px 14px 6px; font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.06em; color:var(--ink-soft); }
.pl-search-item { width:100%; text-align:left; border:none; background:transparent; padding:9px 14px; display:grid; grid-template-columns:auto 1fr auto; gap:8px; align-items:center; transition:background .12s; cursor:pointer; }
.pl-search-item:hover { background:var(--line-soft); }
.pl-search-item:not(:last-child) { border-bottom:1px solid var(--line-soft); }
.pl-search-kind { font-family:var(--mono); font-size:10px; letter-spacing:.06em; text-transform:uppercase; color:var(--mark); border:1px solid var(--mark-soft); border-radius:6px; padding:2px 5px; white-space:nowrap; }
.pl-search-text { min-width:0; display:flex; flex-direction:column; gap:1px; }
.pl-search-text strong { font-size:13.5px; font-weight:540; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pl-search-text small { font-size:11px; color:var(--ink-soft); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pl-search-when { font-family:var(--mono); font-size:10px; color:var(--ink-soft); white-space:nowrap; }
.pl-search-empty { padding:18px 14px; color:var(--ink-soft); font-size:13px; }

.pl-places-wrap { position:relative; flex:1; }
.pl-places-suggestions { position:absolute; z-index:40; top:calc(100% + 4px); left:0; right:0; background:var(--surface-raised); border:1px solid var(--line); border-radius:10px; box-shadow:0 8px 24px rgba(24,27,32,.14); overflow:hidden; }
.pl-places-item { width:100%; text-align:left; border:none; background:transparent; padding:9px 12px; font-size:13px; color:var(--ink); cursor:pointer; display:flex; flex-direction:column; gap:1px; transition:background .1s; }
.pl-places-item:hover { background:var(--line-soft); }
.pl-places-item small { font-size:11px; color:var(--ink-soft); }

.pl-shell { max-width:980px; margin:0 auto; }
.pl-shell.wide-month { max-width:1440px; }
.pl-top { display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:18px; }
.pl-brand { display:flex; align-items:flex-end; }
.pl-logo { display:block; width:168px; max-width:42vw; height:auto; }
.pl-brand .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }

.pl-views { display:flex; gap:2px; background:var(--surface); border:1px solid var(--line); border-radius:10px; padding:3px; flex-basis:100%; }
.pl-views button { border:none; background:transparent; color:var(--ink-soft); font-family:var(--mono);
  font-size:10px; letter-spacing:0; text-transform:uppercase; padding:7px 0; border-radius:7px; transition:color .15s,background .15s; flex:1; min-width:0; text-align:center; white-space:nowrap; overflow:hidden; }
.pl-views button[aria-pressed="true"] { background:var(--ink); color:var(--paper); }

.pl-home { display:flex; flex-direction:column; gap:16px; }
.pl-home-hero { display:grid; grid-template-columns:1.1fr .9fr; gap:16px; align-items:stretch; }
.pl-home-intro { border:1px solid var(--line); background:var(--paper); border-radius:12px; padding:17px; display:flex; flex-direction:column; justify-content:flex-start; gap:8px; }
.pl-home-date { font-family:var(--mono); font-size:11px; color:var(--mark); letter-spacing:.08em; text-transform:uppercase; }
.pl-home-title { font-size:28px; line-height:1.05; font-weight:680; letter-spacing:-.02em; max-width:560px; font-family:var(--mono); }
.pl-home-sub { font-size:13px; color:var(--ink-soft); max-width:520px; }
.pl-hero-stack { display:flex; flex-direction:column; gap:8px; align-items:flex-start; }
.pl-hero-primary { display:flex; align-items:center; gap:8px; flex-wrap:wrap; color:var(--ink); }
.pl-hero-primary strong { font-size:14px; }
.pl-hero-primary strong[data-kind="task"] { font-weight:500; }
.pl-hero-primary strong[data-kind="event"] { font-weight:640; }
.pl-hero-primary .pl-hero-when { flex-basis:100%; color:var(--ink-soft); }
.pl-hero-primary .pl-linkbtn { font-family:var(--sans); font-size:12.5px; font-weight:500; padding:0; }
.pl-hero-nested { list-style:none; margin:-2px 0 0; padding:0 0 0 16px; display:flex; flex-direction:column; gap:3px; width:100%; }
.pl-hero-nested li { display:flex; align-items:center; gap:7px; min-width:0; color:var(--ink); }
.pl-hero-nested button { border:none; background:transparent; padding:0; color:var(--ink); font:inherit; font-size:12.5px; font-weight:540; text-align:left; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pl-hero-nested button:hover { color:var(--mark); }
.pl-hero-nested span:last-child { color:var(--ink-soft); font-size:12.5px; white-space:nowrap; }
.pl-hero-more { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:4px; width:100%; }
.pl-hero-more li { min-width:0; color:var(--ink-soft); }
.pl-hero-more-row { display:flex; align-items:center; gap:7px; min-width:0; color:var(--ink-soft); }
.pl-hero-daybreak { display:flex; align-items:center; gap:8px; margin:4px 0 2px; font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.07em; color:var(--ink-soft); }
.pl-hero-daybreak::after { content:''; flex:1; height:1px; background:var(--line); }
.pl-hero-more button { border:none; background:transparent; padding:0; color:var(--ink-soft); font:inherit; font-size:12.5px; text-align:left; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pl-hero-more button:hover { color:var(--mark); }
.pl-home-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
.pl-stat { border:1px solid var(--line); border-radius:10px; background:var(--surface); padding:10px; min-height:72px; display:flex; flex-direction:column; justify-content:space-between; }
.pl-stat b { font-family:var(--mono); font-size:24px; font-weight:650; line-height:1; color:var(--ink); }
.pl-stat span { font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.06em; color:var(--ink-soft); }
.pl-home-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; }
.pl-home-section { border:1px solid var(--line); border-radius:12px; background:var(--surface); padding:14px; min-width:0; }
.pl-home-section.wide { grid-column:1 / -1; }
.pl-home-head { display:flex; align-items:baseline; justify-content:space-between; gap:10px; margin-bottom:10px; }
.pl-home-head h3 { margin:0; font-size:15px; font-weight:660; }
.pl-home-head span { font-family:var(--mono); font-size:10px; color:var(--ink-soft); letter-spacing:.06em; text-transform:uppercase; white-space:nowrap; }
.pl-home-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:7px; }
.pl-home-item { width:100%; border:1px solid var(--line); border-radius:9px; background:var(--paper); padding:9px 10px; display:grid; grid-template-columns:auto 1fr auto; gap:8px; align-items:center; text-align:left; color:var(--ink); transition:border-color .15s,background .15s; }
.pl-home-item:hover { border-color:var(--mark); background:var(--surface); }
.pl-home-kind { font-family:var(--mono); font-size:10px; letter-spacing:.06em; text-transform:uppercase; color:var(--mark); border:1px solid var(--mark-soft); border-radius:6px; padding:3px 5px; }
.pl-home-text { min-width:0; display:flex; flex-direction:column; gap:2px; }
.pl-home-text strong { font-size:13.5px; font-weight:560; line-height:1.25; overflow-wrap:anywhere; }
.pl-home-item[data-kind="task"] .pl-home-text strong { font-weight:500; }
.pl-home-item[data-kind="event"] .pl-home-text strong { font-weight:640; }
.pl-home-text strong[data-done="true"] { color:var(--ink-soft); text-decoration:line-through; }
.pl-home-text small { color:var(--ink-soft); font-size:11px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pl-home-when { font-family:var(--mono); font-size:10px; color:var(--ink-soft); white-space:nowrap; }
.pl-home-empty { color:var(--ink-soft); font-size:13px; padding:14px 2px; }

.pl-nav { display:flex; align-items:center; gap:10px; margin-bottom:16px; }
.pl-nav .title { font-size:19px; font-weight:640; letter-spacing:-.01em; flex:1; font-family:var(--mono); }
.pl-month-span { display:flex; gap:2px; background:var(--surface); border:1px solid var(--line); border-radius:9px; padding:3px; flex:none; }
.pl-month-span button { border:none; background:transparent; color:var(--ink-soft); border-radius:6px; min-width:45px; height:28px; padding:0 9px; font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.04em; transition:background .15s,color .15s; white-space:nowrap; }
.pl-month-span button[aria-pressed="true"] { background:var(--ink); color:var(--paper); }
@media (orientation:portrait) {
  .pl-month-span { display:none; }
}
.pl-cal-legend { display:flex; flex-wrap:wrap; align-items:center; gap:6px; margin-bottom:14px; }
.pl-cal-chip { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border:1px solid var(--line); border-radius:999px; background:var(--surface); color:var(--ink); font-size:12px; font-weight:560; font-family:var(--sans); cursor:pointer; transition:border-color .15s, opacity .15s; }
.pl-cal-chip:hover { border-color:var(--ink-soft); }
.pl-cal-chip[data-off="true"] { color:var(--ink-soft); background:var(--paper); text-decoration:line-through; }
.pl-cal-chip-dot { width:9px; height:9px; border-radius:50%; flex:none; }
.pl-cal-item-dot { display:inline-block; width:8px; height:8px; border-radius:50%; flex:none; margin-right:6px; vertical-align:middle; }
.pl-cal-picker { border:1px solid var(--line); border-radius:8px; padding:6px 9px; font-size:12px; font-family:var(--sans); color:var(--ink); background:var(--surface); }
.pl-step { width:34px; height:34px; border:1px solid var(--line); background:var(--surface); border-radius:9px; color:var(--ink); font-size:15px; line-height:1; transition:border-color .15s; }
.pl-step:hover { border-color:var(--ink-soft); }
.pl-today { border:1px solid var(--line); background:var(--surface); border-radius:9px; padding:0 13px; height:34px; font-family:var(--mono); font-size:12px; text-transform:uppercase; letter-spacing:.04em; color:var(--ink); transition:border-color .15s; }
.pl-today:hover { border-color:var(--ink-soft); }

.pl-main { display:grid; grid-template-columns:1fr; gap:18px; align-items:start; }
.pl-panel { background:var(--surface); border:1px solid var(--line); border-radius:14px; padding:18px; }

.pl-dayhead { display:flex; align-items:baseline; gap:12px; margin-bottom:14px; }
.pl-daynum { font-family:var(--mono); font-size:40px; font-weight:600; letter-spacing:-.03em; line-height:.9; }
.pl-daysub { font-size:13px; color:var(--ink-soft); }
.pl-add { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:14px; }
.pl-add input { flex:1; min-width:160px; border:1px solid var(--line); border-radius:9px; padding:9px 12px; font-size:14px; color:var(--ink); background:var(--paper); }
.pl-add input::placeholder { color:var(--placeholder); }
.pl-kinds { display:flex; border:1px solid var(--line); border-radius:9px; overflow:hidden; }
.pl-kinds button { border:none; background:var(--paper); color:var(--ink-soft); font-size:12px; font-family:var(--mono); text-transform:uppercase; letter-spacing:.03em; padding:0 11px; transition:background .15s,color .15s; }
.pl-kinds button[aria-pressed="true"] { background:var(--ink); color:var(--paper); }
.pl-kinds.sm button { font-size:11px; padding:0 8px; height:32px; }
.pl-addbtn { border:none; background:var(--mark); color:var(--on-accent); border-radius:9px; padding:0 16px; font-size:14px; font-weight:560; transition:filter .15s; }
.pl-addbtn.sm { padding:0 12px; font-size:13px; height:32px; }
.pl-addbtn:hover { filter:brightness(1.06); }

.pl-list { list-style:none; margin:0; padding:0; }
.pl-list > .pl-node + .pl-node { border-top:1px solid var(--line); margin-top:6px; padding-top:6px; }
.pl-children { list-style:none; margin:2px 0 4px 0; padding-left:17px; border-left:1.5px solid var(--line); }
.pl-row { display:grid; grid-template-columns:minmax(0,1fr) auto; align-items:center; column-gap:8px; row-gap:6px; padding:8px 5px; border-radius:8px; transition:background .12s; }
.pl-row:hover { background:var(--line-soft); }
.pl-rowmain { min-width:0; display:grid; grid-template-columns:62px minmax(0,1fr); align-items:start; column-gap:8px; row-gap:4px; }
.pl-rowmain[data-kind="task"] { grid-template-columns:62px minmax(0,1fr); align-items:start; }
.pl-rowmain[data-kind="list-task"] { grid-template-columns:28px minmax(0,1fr); align-items:center; column-gap:8px; }
.pl-kindcell { position:relative; width:62px; min-width:0; display:flex; align-items:center; }
.pl-rowmain[data-kind="list-task"] .pl-kindcell { width:28px; }
.pl-kindcell .pl-disc { position:absolute; left:-18px; top:50%; transform:translateY(-50%); }
.pl-disc { width:18px; height:18px; border:none; background:transparent; color:var(--ink-soft); font-size:10px; padding:0; flex:none; transition:transform .12s; }
.pl-check { width:36px; height:36px; flex:none; border:none; border-radius:9px; background:transparent; padding:0; color:transparent; font-size:0; line-height:1; display:inline-flex; align-items:center; justify-content:center; transition:background .15s; }
.pl-check::before { content:''; width:19px; height:19px; border:1.5px solid var(--line); border-radius:6px; background:var(--surface); box-sizing:border-box; font-size:12px; line-height:1; transition:background .15s,border-color .15s; }
.pl-check:hover { background:var(--line-soft); }
.pl-check[data-done="true"] { background:transparent; }
.pl-check[data-done="true"]::before { content:'✓'; display:inline-flex; align-items:center; justify-content:center; background:var(--ink); border-color:var(--ink); color:var(--paper); }
.pl-check[data-done="true"]:hover { background:var(--line-soft); }
.pl-kindtag { align-self:center; justify-self:start; width:62px; text-align:center; font-family:var(--mono); font-size:10px; letter-spacing:.04em; text-transform:uppercase; color:var(--ink-soft); border:1px solid var(--line); border-radius:5px; padding:2px 4px; flex:none; white-space:nowrap; overflow:hidden; text-overflow:clip; }
.pl-taskkind { width:62px; min-height:28px; border:1px solid var(--line); border-radius:7px; background:var(--surface); color:var(--ink-soft); display:flex; align-items:center; justify-content:center; gap:4px; padding:0 4px; font-family:var(--mono); font-size:10px; letter-spacing:.04em; text-transform:uppercase; }
.pl-taskkind:hover { background:var(--line-soft); }
.pl-taskkind.list-only { width:28px; min-height:28px; padding:0; border:none; background:transparent; }
.pl-taskkind.list-only:hover { background:var(--line-soft); }
.pl-taskkind-box { width:12px; height:12px; border:1.5px solid var(--line); border-radius:4px; background:var(--surface); display:inline-flex; align-items:center; justify-content:center; color:var(--on-accent); font-size:9px; line-height:1; flex:none; box-sizing:border-box; }
.pl-taskkind.list-only .pl-taskkind-box { width:18px; height:18px; border-radius:6px; font-size:12px; }
.pl-taskkind[data-done="true"] .pl-taskkind-box { background:var(--ink); border-color:var(--ink); }
.pl-titlewrap { min-width:0; flex:1 1 150px; display:flex; align-items:center; flex-wrap:wrap; gap:6px; }
.pl-title { border:none; background:transparent; color:var(--ink); padding:2px 3px; margin:-2px -3px; border-radius:5px; font-size:14.5px; text-align:left; line-height:1.3; }
.pl-title:hover { background:var(--surface); box-shadow:inset 0 0 0 1px var(--line); }
.pl-title[data-done="true"] { color:var(--ink-soft); text-decoration:line-through; }
.pl-title-input { flex:1 1 160px; min-width:120px; border:1px solid var(--mark); border-radius:7px; padding:5px 7px; font-size:14.5px; color:var(--ink); background:var(--surface); }
.pl-title-input[data-done="true"] { color:var(--ink-soft); text-decoration:line-through; }
.hl { background-image:linear-gradient(106deg, transparent 1.5%, var(--mark-soft) 1.5%, var(--mark-soft) 98.5%, transparent 98.5%);
  background-repeat:no-repeat; padding:.05em .18em; border-radius:.1em; box-decoration-break:clone; -webkit-box-decoration-break:clone; }
.pl-prog { font-family:var(--mono); font-size:10px; color:var(--ink-soft); flex:none; }
.pl-time { display:inline-flex; align-items:center; gap:5px; font-family:var(--mono); font-size:10px; letter-spacing:.02em; color:var(--ink); border:1px solid var(--line); border-radius:6px; padding:2px 6px; background:var(--surface); }
.pl-rem { display:inline-flex; align-items:center; gap:5px; font-family:var(--mono); font-size:10px; letter-spacing:.02em; color:var(--mark); border:1px solid var(--mark-soft); border-radius:6px; padding:2px 6px; }
.pl-rem button { border:none; background:transparent; color:var(--mark); font-size:12px; line-height:1; padding:0; }
.pl-acts { display:flex; align-items:center; gap:6px; justify-content:flex-end; flex:none; }
.pl-flag { border:1px solid var(--line); background:var(--surface); border-radius:7px; padding:5px 10px; font-family:var(--mono); font-size:11px; letter-spacing:.04em; text-transform:uppercase; color:var(--ink-soft); display:flex; align-items:center; justify-content:center; flex:none; transition:all .15s; }
.pl-flag[aria-pressed="true"] { background:var(--mark); border-color:var(--mark); color:var(--on-accent); }
.pl-iconbtn { border:1px solid var(--line); background:var(--surface); border-radius:7px; width:28px; height:28px; display:inline-flex; align-items:center; justify-content:center; color:var(--ink-soft); font-family:var(--mono); font-size:13px; font-weight:700; line-height:1; flex:none; transition:all .15s; }
.pl-iconbtn:hover { border-color:var(--mark); color:var(--mark); }
.pl-iconbtn[aria-pressed="true"] { background:var(--mark); border-color:var(--mark); color:var(--on-accent); }
.pl-eye-toggle[aria-pressed="true"] { background:var(--surface); border-color:var(--line); color:var(--ink-soft); }
.pl-eye-toggle[aria-pressed="true"]:hover { border-color:var(--mark); color:var(--mark); }
.pl-eye-svg { width:20px; height:18px; display:block; overflow:visible; }
.pl-info { position:relative; border:1px solid var(--line); background:var(--surface); color:var(--ink-soft); width:17px; height:17px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; padding:0; font-family:var(--mono); font-size:10px; font-weight:700; line-height:1; flex:none; }
.pl-info:hover,.pl-info:focus-visible { border-color:var(--mark); color:var(--mark); }
.pl-info-pop { position:absolute; z-index:45; bottom:calc(100% + 7px); left:50%; transform:translateX(-50%) translateY(2px); width:210px; max-width:76vw; border:1px solid var(--line); border-radius:9px; background:var(--ink); color:var(--paper); padding:8px 9px; font-family:var(--sans); font-size:11.5px; font-weight:500; line-height:1.35; text-align:left; text-transform:none; letter-spacing:0; box-shadow:0 10px 26px rgba(24,27,32,.18); opacity:0; pointer-events:none; transition:opacity .12s,transform .12s; }
.pl-info-pop::after { content:''; position:absolute; top:100%; left:50%; transform:translateX(-50%); border:6px solid transparent; border-top-color:var(--ink); }
.pl-info:hover .pl-info-pop,.pl-info:focus-visible .pl-info-pop { opacity:1; transform:translateX(-50%) translateY(0); }
.pl-action-with-info { display:inline-flex; align-items:center; gap:4px; flex:none; }
.pl-action-with-info .pl-info-pop { left:auto; right:0; transform:translateY(2px); }
.pl-action-with-info .pl-info-pop::after { left:auto; right:3px; transform:none; }
.pl-action-with-info .pl-info:hover .pl-info-pop,.pl-action-with-info .pl-info:focus-visible .pl-info-pop { transform:translateY(0); }
.pl-ibtn { border:1px solid var(--line); background:var(--surface); border-radius:7px; width:30px; height:30px; display:flex; align-items:center; justify-content:center; color:var(--ink-soft); font-size:13px; flex:none; transition:border-color .15s,color .15s; }
.pl-ibtn:hover { border-color:var(--ink-soft); color:var(--ink); }
.pl-ibtn[aria-pressed="true"] { background:var(--ink); color:var(--paper); border-color:var(--ink); }
.pl-timebtn { width:auto; min-width:82px; gap:5px; padding:0 9px; font-family:var(--mono); font-size:11px; }
.pl-timebtn[data-empty="true"] { color:var(--placeholder); background:var(--paper); }
.pl-del { border:none; background:transparent; color:#b4bac4; font-size:17px; line-height:1; padding:4px; flex:none; transition:color .15s; }
.pl-del:hover { color:var(--mark); }
.pl-drag-handle { display:inline-flex; align-items:center; justify-content:center; width:30px; height:30px; flex:none; border:1px solid var(--line); background:var(--surface); border-radius:7px; color:var(--ink-soft); font-size:16px; line-height:1; cursor:grab; touch-action:none; -webkit-touch-callout:none; -webkit-user-select:none; user-select:none; transition:all .15s; }
.pl-drag-handle:hover { border-color:var(--mark); color:var(--mark); }
[data-dragging="true"] { opacity:.4; }
[data-dragging="true"] .pl-drag-handle { cursor:grabbing; border-color:var(--mark); color:var(--mark); }
.pl-list-card, .pl-list-row, .pl-list-name, .pl-title { -webkit-user-select:none; user-select:none; -webkit-touch-callout:none; }
.pl-list-card input, .pl-list-card textarea { -webkit-user-select:text; user-select:text; }
.pl-drag-preview { position:fixed; z-index:200; top:0; left:0; pointer-events:none; display:flex; align-items:center; box-sizing:border-box; max-width:320px; max-height:64px; padding:9px 14px; border-radius:11px; background:var(--surface-raised); border:1px solid var(--line); opacity:.82; font-family:var(--sans); font-size:14.5px; font-weight:560; color:var(--ink); line-height:1.3; box-shadow:0 16px 34px rgba(24,27,32,.26); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.pl-inline { display:flex; gap:6px; align-items:center; flex-wrap:wrap; margin:1px 0 7px 26px; }
.pl-inline input[type="text"] { flex:1 1 140px; min-width:120px; border:1px solid var(--line); border-radius:8px; padding:7px 10px; font-size:13px; background:var(--paper); color:var(--ink); }
.pl-inline input[type="datetime-local"] { flex:0 1 230px; min-width:210px; border:1px solid var(--line); border-radius:8px; padding:6px 9px; font-size:13px; background:var(--paper); color:var(--ink); }
.pl-inline input[type="time"], .pl-add input[type="time"] { border:1px solid var(--line); border-radius:8px; padding:7px 10px; font-size:13px; background:var(--paper); color:var(--ink); }
.pl-add input[type="time"] { flex:0 0 116px; min-width:116px; }
.pl-timepick { position:relative; flex:none; }
.pl-backdrop { position:fixed; inset:0; z-index:19; }
.pl-popout { position:absolute; z-index:20; top:calc(100% + 7px); right:0; width:230px; max-width:calc(100vw - 32px); border:1px solid var(--line); border-radius:10px; background:var(--surface-raised); box-shadow:0 14px 32px rgba(24,27,32,.16); padding:10px; display:flex; flex-direction:column; gap:8px; }
.pl-inline .pl-popout { left:0; right:auto; }
.pl-modal-backdrop { position:fixed; inset:0; z-index:80; background:rgba(24,27,32,.22); display:flex; align-items:center; justify-content:center; padding:18px; }
.pl-modal { width:min(420px,100%); border:1px solid var(--line); border-radius:12px; background:var(--surface-raised); box-shadow:0 22px 56px rgba(24,27,32,.24); padding:16px; display:flex; flex-direction:column; gap:12px; }
.pl-modal-title { font-size:17px; line-height:1.2; font-weight:680; }
.pl-modal-copy { margin:0; color:var(--ink-soft); font-size:13px; }
.pl-history-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; max-height:320px; overflow-y:auto; border-top:1px solid var(--line-soft); border-bottom:1px solid var(--line-soft); }
.pl-history-list li { display:flex; align-items:center; gap:8px; padding:9px 1px; border-top:1px solid var(--line-soft); color:var(--ink); font-size:13px; }
.pl-history-list li:first-child { border-top:none; }
.pl-history-section { display:flex; flex-direction:column; gap:8px; }
.pl-history-heading { display:flex; align-items:center; justify-content:space-between; gap:10px; font-size:12px; font-weight:680; color:var(--ink); }
.pl-history-heading small { font-family:var(--mono); font-size:10px; color:var(--ink-soft); font-weight:500; text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; }
.pl-history-title { display:flex; align-items:center; gap:6px; min-width:0; }
.pl-history-toggle, .pl-history-add, .pl-history-spacer { width:24px; height:24px; display:inline-grid; place-items:center; border:none; border-radius:7px; background:transparent; color:var(--ink-soft); font-size:14px; line-height:1; padding:0; transition:background .15s,color .15s; }
.pl-history-toggle:hover, .pl-history-add:hover { background:var(--surface); color:var(--ink); }
.pl-history-add { font-size:18px; font-weight:650; }
.pl-history-spacer { visibility:hidden; }
.pl-history-date { flex:1; min-width:0; border:1px solid var(--line); border-radius:8px; padding:7px 9px; color:var(--ink); background:var(--paper); font-family:var(--sans); font-size:13px; }
.pl-history-price { width:96px; min-width:0; border:1px solid var(--line); border-radius:8px; padding:7px 9px; color:var(--ink); background:var(--paper); font-family:var(--sans); font-size:13px; }
.pl-price-add { display:grid; grid-template-columns:minmax(0,1fr) 104px auto; gap:8px; align-items:center; }
.pl-price-add button { border:1px solid var(--line); border-radius:8px; background:var(--paper); color:var(--ink); padding:7px 10px; font-size:12px; font-weight:650; }
.pl-price-add button:disabled { opacity:.45; }
.pl-history-remove { border:none; background:transparent; color:#b4bac4; font-size:17px; line-height:1; padding:4px 7px; flex:none; transition:color .15s; }
.pl-history-remove:hover { color:var(--mark); }
.pl-modal-actions { display:grid; grid-template-columns:1fr; gap:8px; }
.pl-modal-actions button { width:100%; border:1px solid var(--line); border-radius:9px; background:var(--paper); color:var(--ink); padding:10px 12px; font-size:13px; font-weight:650; text-align:left; }
.pl-modal-actions button:hover { border-color:var(--mark-soft); background:var(--surface); }
.pl-modal-actions button.primary { border-color:var(--mark-soft); background:var(--mark-soft); color:var(--ink); }
.pl-modal-actions button.safe { border-color:var(--brand-teal); background:var(--paper-raised); color:var(--ink); }
.pl-modal-actions button.cancel { background:transparent; color:var(--ink-soft); font-weight:560; text-align:center; }
.pl-poptitle { font-size:12px; font-weight:650; color:var(--ink); }
.pl-pophelp { font-size:11.5px; color:var(--ink-soft); margin-top:-4px; }
.pl-poprow { display:flex; gap:6px; align-items:center; }
.pl-poprow input[type="time"] { flex:1; min-width:116px; border:1px solid var(--line); border-radius:8px; padding:7px 10px; font-size:13px; background:var(--paper); color:var(--ink); }
.pl-linkbtn { border:none; background:transparent; color:var(--ink-soft); font-family:var(--mono); font-size:11px; padding:4px 2px; }
.pl-linkbtn:hover { color:var(--mark); }

.pl-empty { color:var(--ink-soft); font-size:13.5px; padding:18px 4px; border:1px dashed var(--line); border-radius:10px; }

.pl-months { display:grid; grid-template-columns:1fr; gap:18px; }
.pl-months[data-span="2"] { grid-template-columns:repeat(2,minmax(0,1fr)); align-items:start; }
.pl-month-block { min-width:0; }
.pl-month-block-title { display:none; margin:0 0 8px; font-family:var(--mono); font-size:13px; font-weight:640; color:var(--ink); }
.pl-months[data-span="2"] .pl-month-block-title { display:block; }
.pl-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:4px; }
.pl-dowrow { display:grid; grid-template-columns:repeat(7,1fr); gap:4px; margin-bottom:6px; }
.pl-dowrow span { font-family:var(--mono); font-size:10px; letter-spacing:.08em; text-transform:uppercase; color:var(--ink-soft); text-align:center; }
.pl-cell { min-height:90px; border:1px solid var(--line); border-radius:9px; background:var(--paper); padding:6px; display:flex; flex-direction:column; gap:3px; color:var(--ink); transition:border-color .15s,background .15s; text-align:left; cursor:pointer; overflow:hidden; min-width:0; }
.pl-cell:hover { border-color:var(--ink-soft); background:var(--surface); }
.pl-cell[data-empty="true"] { background:transparent; border-color:transparent; cursor:default; pointer-events:none; }
.pl-cell[data-today="true"] { border-color:var(--ink); }
.pl-cell[data-dragover="true"] { border-color:var(--mark); background:var(--mark-soft); }
.pl-cell .n { font-family:var(--mono); font-size:12px; line-height:1; margin-bottom:2px; flex:none; }
.pl-cell[data-today="true"] .n { font-weight:680; color:var(--mark); }
.pl-cell-chips { display:flex; flex-direction:column; gap:2px; flex:1; min-width:0; overflow:hidden; }
.pl-chip { position:relative; font-size:11px; line-height:1.25; padding:2px 9px 2px 7px; border-radius:4px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; background:var(--surface); border:1px solid var(--line); color:var(--ink); display:flex; align-items:center; gap:4px; width:100%; cursor:grab; transition:background .12s,border-color .12s; min-width:0; }
.pl-chip:active { cursor:grabbing; }
.pl-chip[data-imp="true"] { border-color:var(--mark-soft); background:var(--mark-soft); color:var(--mark); font-weight:560; }
.pl-chip[data-done="true"] { color:var(--ink-soft); text-decoration:line-through; }
.pl-chip::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--ink-soft); }
.pl-chip[data-kind="event"]::before { background:var(--mark); }
.pl-chip[data-attention="true"]::before { background:var(--mark); }
.pl-chip-title { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; display:inline-flex; align-items:center; gap:3px; }
.pl-chip.dragging { opacity:.4; }
.pl-google-badge { display:inline-flex; align-items:center; justify-content:center; flex:none; width:12px; height:12px; border-radius:3px; background:var(--google-cal-color,#4285f4); color:var(--badge-text); font-family:var(--mono); font-size:8px; font-weight:800; line-height:1; text-decoration:none; }
.pl-task-glyph { display:inline-flex; align-items:center; justify-content:center; flex:none; width:7px; height:7px; border:1.2px solid var(--ink-soft); border-radius:2px; color:var(--ink-soft); font-size:8px; line-height:1; transform:translateY(.5px); }
.pl-task-glyph[data-done="true"] { width:9px; height:9px; border:none; }
.pl-tag-rail { position:absolute; top:0; right:0; bottom:0; width:4px; background:var(--tag-rail); }
.pl-cell-more { font-family:var(--mono); font-size:10px; color:var(--ink-soft); padding:1px 4px; }
.pl-year { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
.pl-mini { border:1px solid var(--line); border-radius:11px; background:var(--paper); padding:12px; display:flex; flex-direction:column; gap:7px; align-items:flex-start; color:var(--ink); transition:border-color .15s; }
.pl-mini:hover { border-color:var(--ink-soft); }
.pl-mini .mn { font-size:15px; font-weight:600; }
.pl-mini .stat { font-family:var(--mono); font-size:11px; color:var(--ink-soft); }
.pl-mini .stat b { color:var(--mark); font-weight:600; }
.pl-bars { display:flex; gap:3px; height:5px; width:100%; }
.pl-bars i { flex:1; background:var(--line); border-radius:2px; }
.pl-bars i.on { background:var(--mark); }

.pl-rail-head { display:flex; align-items:baseline; justify-content:space-between; margin-bottom:12px; }
.pl-rail-head h3 { margin:0; font-size:15px; font-weight:640; }
.pl-rail-head span { font-family:var(--mono); font-size:11px; color:var(--ink-soft); text-transform:uppercase; letter-spacing:.04em; }
.pl-rail-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:7px; }
.pl-rail-item { width:100%; text-align:left; border:1px solid var(--line); background:var(--paper); border-radius:10px; padding:9px 11px; display:flex; flex-direction:column; gap:3px; transition:border-color .15s; }
.pl-rail-item:hover { border-color:var(--mark); }
.pl-rail-item .when { font-family:var(--mono); font-size:10px; letter-spacing:.05em; text-transform:uppercase; color:var(--mark); }
.pl-rail-item .path { font-size:11px; color:var(--ink-soft); }
.pl-rail-item .what { font-size:13.5px; color:var(--ink); }
.pl-rail-item .what[data-done="true"] { color:var(--ink-soft); text-decoration:line-through; }
.pl-note { margin-top:14px; font-size:11.5px; color:var(--ink-soft); font-family:var(--mono); letter-spacing:.02em; }
.pl-user { display:flex; align-items:center; gap:8px; }
.pl-user-email { font-family:var(--mono); font-size:11px; color:var(--ink-soft); letter-spacing:.02em; }
.pl-build { font-family:var(--mono); font-size:10px; color:var(--ink-soft); opacity:.5; letter-spacing:.04em; white-space:nowrap; }
.pl-build-env { display:inline-block; background:var(--mark); color:var(--on-accent); border-radius:3px; padding:1px 4px; font-size:9px; text-transform:uppercase; letter-spacing:.06em; margin-right:4px; opacity:1; }
.pl-status-pill { display:inline-flex; align-items:center; justify-content:center; background:var(--brand-teal); color:var(--paper); border-radius:5px; padding:3px 7px; font-family:var(--mono); font-size:10px; line-height:1; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; flex:none; }
.pl-signout { border:1px solid var(--line); background:var(--surface); border-radius:7px; padding:5px 10px; font-family:var(--mono); font-size:11px; letter-spacing:.04em; text-transform:uppercase; color:var(--ink-soft); cursor:pointer; transition:border-color .15s,color .15s; }
.pl-signout:hover { border-color:var(--ink-soft); color:var(--ink); }
.pl-refresh { border:1px solid var(--line); background:var(--surface); border-radius:7px; padding:5px 8px; font-size:14px; line-height:1; color:var(--ink-soft); cursor:pointer; transition:border-color .15s,color .15s; display:flex; align-items:center; }
.pl-refresh:hover { border-color:var(--ink-soft); color:var(--ink); }
@keyframes pl-spin { to { transform:rotate(360deg); } }
.pl-refresh.spinning { pointer-events:none; }
.pl-refresh.spinning span { display:inline-block; animation:pl-spin .7s linear infinite; }
.pl-sync-status { font-family:var(--mono); font-size:10px; color:var(--ink-soft); white-space:nowrap; }
.pl-sync-status[data-state="error"] { color:var(--mark); }
.pl-invite-banner { display:grid; grid-template-columns:minmax(0,1fr) auto; gap:12px; align-items:center; border:1px solid var(--mark-soft); background:var(--mark-soft); border-radius:10px; padding:11px 12px; margin-bottom:14px; }
.pl-invite-banner strong { display:block; font-size:13.5px; font-weight:660; }
.pl-invite-banner small { display:block; margin-top:2px; font-size:11.5px; color:var(--ink-soft); }
.pl-invite-actions { display:flex; align-items:center; gap:6px; flex-wrap:wrap; justify-content:flex-end; }
.pl-switch { width:46px; height:26px; border:1px solid var(--line); border-radius:999px; background:var(--line); padding:2px; cursor:pointer; transition:background .15s,border-color .15s; display:flex; align-items:center; flex:none; }
.pl-switch span { width:20px; height:20px; border-radius:50%; background:var(--surface); box-shadow:0 1px 3px rgba(24,27,32,.18); transition:transform .15s; }
.pl-switch[aria-checked="true"] { background:var(--ink); border-color:var(--ink); }
.pl-switch[aria-checked="true"] span { transform:translateX(20px); }
.pl-switch:disabled { cursor:default; opacity:.55; }
.pl-settings { display:flex; flex-direction:column; gap:16px; }
.pl-settings-head { display:flex; align-items:flex-end; justify-content:space-between; gap:14px; border-bottom:1px solid var(--line-soft); padding-bottom:14px; }
.pl-settings-eyebrow { font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.08em; color:var(--mark); margin-bottom:4px; }
.pl-settings-title { font-size:21px; line-height:1.15; font-weight:680; letter-spacing:-.01em; }
.pl-settings-email { font-family:var(--mono); font-size:11px; color:var(--ink-soft); word-break:break-word; text-align:right; }
.pl-settings-list { display:flex; flex-direction:column; gap:8px; }
.pl-settings-row { display:grid; grid-template-columns:minmax(0,1fr) auto; gap:12px; align-items:center; border:1px solid var(--line); border-radius:10px; background:var(--paper); padding:12px 13px; }
.pl-settings-row strong { display:block; font-size:14px; font-weight:650; }
.pl-settings-row small { display:block; margin-top:2px; color:var(--ink-soft); font-size:12px; }
.pl-settings-button { width:100%; text-align:left; cursor:pointer; color:var(--ink); transition:border-color .15s,background .15s; }
.pl-settings-button:hover { border-color:var(--mark); background:var(--surface); }
.pl-settings-chevron { font-family:var(--mono); color:var(--ink-soft); font-size:18px; line-height:1; }
.pl-settings-back { border:1px solid var(--line); background:var(--surface); border-radius:7px; padding:5px 9px; font-family:var(--mono); font-size:11px; color:var(--ink-soft); cursor:pointer; }
.pl-settings-back:hover { border-color:var(--ink-soft); color:var(--ink); }
.pl-library-list { display:flex; flex-direction:column; gap:12px; }
.pl-library-group { border:1px solid var(--line); border-radius:10px; background:var(--paper); overflow:hidden; }
.pl-library-group-head { display:flex; align-items:baseline; justify-content:space-between; gap:10px; padding:11px 13px; border-bottom:1px solid var(--line-soft); }
.pl-library-group-head strong { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:14px; font-weight:660; }
.pl-library-group-head span { font-family:var(--mono); font-size:10px; color:var(--ink-soft); text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; }
.pl-library-items { list-style:none; margin:0; padding:0; }
.pl-library-item { display:grid; grid-template-columns:minmax(0,1fr) auto; gap:10px; align-items:center; padding:10px 13px; border-bottom:1px solid var(--line-soft); }
.pl-library-item:last-child { border-bottom:none; }
.pl-library-main { min-width:0; display:flex; flex-direction:column; gap:3px; }
.pl-library-main strong { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:14px; font-weight:620; }
.pl-library-main small { color:var(--ink-soft); font-size:11.5px; }
.pl-library-actions { display:flex; align-items:center; justify-content:flex-end; gap:6px; }
.pl-library-remove { border:1px solid var(--line); background:var(--surface); border-radius:7px; padding:4px 8px; min-height:28px; font-family:var(--mono); font-size:10px; letter-spacing:.04em; text-transform:uppercase; color:var(--mark); flex:none; cursor:pointer; transition:border-color .15s,background .15s; }
.pl-library-remove:hover { border-color:var(--mark); background:var(--mark-soft); }
.pl-theme-row { align-items:flex-start; }
.pl-theme-options { display:flex; gap:2px; background:var(--surface); border:1px solid var(--line); border-radius:10px; padding:3px; }
.pl-theme-options button { border:none; background:transparent; color:var(--ink-soft); border-radius:7px; min-width:58px; height:30px; padding:0 10px; font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.04em; transition:background .15s,color .15s; }
.pl-theme-options button[aria-pressed="true"] { background:var(--mark); color:var(--on-accent); font-weight:700; }

.pl-lists { display:flex; flex-direction:column; gap:14px; }
.pl-lists-bar { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.pl-lists-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(min(260px,100%),1fr)); gap:14px; }
.pl-list-section { display:flex; flex-direction:column; gap:10px; }
.pl-list-section-head { display:flex; align-items:baseline; justify-content:space-between; gap:10px; border-top:1px solid var(--line-soft); padding-top:14px; }
.pl-list-section-head h3 { margin:0; font-size:13px; font-weight:660; }
.pl-list-section-head span { font-family:var(--mono); font-size:10px; color:var(--ink-soft); text-transform:uppercase; letter-spacing:.04em; }
.pl-list-card { min-width:0; background:var(--surface); border:1px solid var(--line); border-radius:12px; padding:14px; display:flex; flex-direction:column; gap:8px; }
.pl-list-card[data-hidden="true"] { opacity:.74; background:var(--paper); }
.pl-list-card-head { display:grid; grid-template-columns:auto minmax(0,1fr) auto; align-items:center; column-gap:10px; row-gap:8px; padding-bottom:8px; border-bottom:1px solid var(--line-soft); min-width:0; }
.pl-list-card-controls { display:flex; align-items:center; gap:7px; min-width:0; }
.pl-list-card-title { min-width:0; display:flex; align-items:baseline; gap:9px; }
.pl-list-card-title .pl-prog { margin-left:auto; }
.pl-list-card-actions { display:flex; align-items:center; justify-content:flex-end; gap:6px; flex-wrap:wrap; min-width:0; }
.pl-list-card[data-hidden="true"] .pl-list-card-head { grid-template-columns:minmax(0,1fr) auto; }
.pl-list-name { min-width:0; font-size:15px; font-weight:640; flex:1 1 auto; text-align:left; border:none; background:transparent; color:var(--ink); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pl-list-name:hover { background:var(--paper); box-shadow:inset 0 0 0 1px var(--line); border-radius:5px; }
.pl-list-add { border:none; background:transparent; color:var(--ink-soft); font-size:13px; padding:5px 2px; text-align:left; cursor:pointer; }
.pl-list-add:hover { color:var(--mark); }
.pl-list-edit-btn { border:1px solid var(--line); background:var(--surface); border-radius:7px; padding:4px 8px; min-height:28px; font-family:var(--mono); font-size:10px; letter-spacing:.04em; text-transform:uppercase; color:var(--ink-soft); flex:none; transition:border-color .15s,color .15s; }
.pl-list-edit-btn:hover { border-color:var(--mark); color:var(--mark); }
.pl-list-quickadd-wrap { position:relative; }
.pl-list-quickadd { width:100%; border:1px solid var(--line); border-radius:9px; padding:8px 11px; font-size:14px; color:var(--ink); background:var(--paper); font-family:inherit; transition:border-color .15s; }
.pl-list-quickadd::placeholder { color:var(--placeholder); }
.pl-list-quickadd:focus { border-color:var(--mark); outline:none; }
.pl-list-suggestions { position:absolute; z-index:30; left:0; right:0; top:calc(100% + 4px); background:var(--surface-raised); border:1px solid var(--line); border-radius:10px; box-shadow:0 10px 26px rgba(24,27,32,.15); overflow:hidden; }
.pl-list-suggestion { width:100%; display:grid; grid-template-columns:minmax(0,1fr) auto; gap:8px; align-items:center; border:none; border-bottom:1px solid var(--line-soft); background:transparent; color:var(--ink); padding:9px 11px; text-align:left; cursor:pointer; }
.pl-list-suggestion:last-child { border-bottom:none; }
.pl-list-suggestion:hover,.pl-list-suggestion:focus-visible { background:var(--paper); outline:none; }
.pl-list-suggestion strong { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:13.5px; font-weight:620; }
.pl-list-suggestion small { font-family:var(--mono); font-size:10px; color:var(--ink-soft); white-space:nowrap; }
.pl-list-edit { display:flex; flex-direction:column; gap:9px; }
.pl-list-textarea { width:100%; border:1px solid var(--mark); border-radius:9px; padding:10px 12px; font-size:14.5px; line-height:1.7; color:var(--ink); background:var(--surface); font-family:inherit; resize:vertical; min-height:96px; }
.pl-list-textarea::placeholder { color:#b4bac4; }
.pl-list-textarea:focus { outline:none; box-shadow:0 0 0 3px var(--mark-soft); }
.pl-list-edit-actions { display:flex; align-items:center; gap:10px; }
.pl-list-edit-hint { font-family:var(--mono); font-size:10px; color:var(--ink-soft); letter-spacing:.02em; margin-left:auto; }

.pl-lists-home { display:grid; grid-template-columns:repeat(auto-fill,minmax(190px,1fr)); gap:10px; margin-top:2px; }
.pl-lists-home-card { border:1px solid var(--line); border-radius:10px; background:var(--paper); padding:10px 12px; cursor:pointer; transition:border-color .15s; display:flex; flex-direction:column; gap:5px; }
.pl-lists-home-card:hover { border-color:var(--mark); }
.pl-lists-home-head { display:flex; align-items:baseline; justify-content:space-between; gap:6px; }
.pl-lists-home-name { font-size:14px; font-weight:600; }
.pl-lists-home-prog { font-family:var(--mono); font-size:10px; color:var(--ink-soft); }
.pl-lists-home-items { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:3px; }
.pl-lists-home-item { font-size:12px; color:var(--ink-soft); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.pl-lists-home-item[data-imp="true"] { color:var(--mark); font-weight:560; }
.pl-lists-home-more { font-size:11px; color:var(--ink-soft); font-family:var(--mono); }

.pl-task-page { display:flex; flex-direction:column; gap:14px; }
.pl-task-head { display:flex; align-items:flex-end; justify-content:space-between; gap:14px; border-bottom:1px solid var(--line-soft); padding-bottom:14px; }
.pl-task-eyebrow { font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.08em; color:var(--mark); margin-bottom:4px; }
.pl-task-title { font-size:21px; line-height:1.15; font-weight:680; letter-spacing:-.01em; }
.pl-task-copy { margin:5px 0 0; max-width:560px; font-size:13px; color:var(--ink-soft); }
.pl-task-stat { border:1px solid var(--line); border-radius:10px; padding:9px 11px; min-width:84px; text-align:right; background:var(--paper); }
.pl-task-stat b { display:block; font-family:var(--mono); font-size:22px; line-height:1; }
.pl-task-stat span { display:block; margin-top:3px; font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.06em; color:var(--ink-soft); }
.pl-task-add { display:grid; grid-template-columns:1fr; gap:8px; align-items:center; border:1px solid var(--line); border-radius:10px; background:var(--surface); padding:10px; }
.pl-task-add input { min-width:0; border:1px solid var(--line); border-radius:9px; padding:9px 12px; font-size:14px; color:var(--ink); background:var(--paper); }
.pl-task-add input:focus { border-color:var(--mark); outline:none; }
.pl-task-create-actions { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); align-items:center; gap:8px; min-width:0; width:100%; }
.pl-task-helpwrap { position:relative; display:grid; grid-template-columns:minmax(0,1fr) 24px; align-items:center; min-width:0; border:1px solid var(--line); background:var(--paper); border-radius:9px; height:48px; overflow:visible; }
.pl-task-helpwrap:has(.pl-task-toggle[aria-pressed="true"]) { background:var(--mark); border-color:var(--mark); }
.pl-task-helpwrap .pl-info { justify-self:center; background:transparent; z-index:1; }
.pl-task-helpwrap:has(.pl-task-toggle[aria-pressed="true"]) .pl-info { border-color:color-mix(in srgb, var(--on-accent) 68%, transparent); color:var(--on-accent); font-weight:700; }
.pl-task-helpwrap .pl-info-pop { left:auto; right:-7px; transform:translateY(2px); }
.pl-task-helpwrap .pl-info-pop::after { left:auto; right:9px; transform:none; }
.pl-task-helpwrap .pl-info:hover .pl-info-pop,.pl-task-helpwrap .pl-info:focus-visible .pl-info-pop { transform:translateY(0); }
.pl-task-toggle { border:none; background:transparent; color:var(--ink-soft); border-radius:9px 0 0 9px; padding:0 4px 0 11px; height:46px; width:100%; display:inline-flex; align-items:center; justify-content:center; gap:6px; font-family:var(--mono); font-size:10px; letter-spacing:.04em; text-transform:uppercase; text-align:center; line-height:1.08; white-space:normal; overflow:hidden; }
.pl-task-toggle[aria-pressed="true"] { background:var(--mark); border-color:var(--mark); color:var(--on-accent); font-weight:700; }
.pl-task-toggle-dot { width:8px; height:8px; border-radius:2px; background:var(--line); flex:none; }
.pl-task-toggle[aria-pressed="true"] .pl-task-toggle-dot { background:var(--on-accent); }
.pl-task-toggle-label { display:block; min-width:0; }
.pl-task-create-actions .pl-addbtn { width:100%; height:48px; }
.pl-task-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:8px; }
.pl-task-row { border:1px solid var(--line); border-radius:10px; background:var(--paper); padding:10px 11px; display:grid; grid-template-columns:auto minmax(0,1fr) auto; gap:9px; align-items:center; }
.pl-task-row[data-attention="true"] { border-color:var(--mark-soft); background:var(--mark-soft); }
.pl-task-row[data-done="true"] { opacity:.62; }
.pl-task-main { min-width:0; display:flex; flex-direction:column; gap:3px; }
.pl-task-name { border:none; background:transparent; padding:0; text-align:left; color:var(--ink); font-size:14px; font-weight:580; line-height:1.3; overflow-wrap:anywhere; }
.pl-task-name:hover { color:var(--mark); }
.pl-task-name[data-done="true"] { color:var(--ink-soft); text-decoration:line-through; }
.pl-task-meta { display:flex; align-items:center; gap:6px; flex-wrap:wrap; font-size:11.5px; color:var(--ink-soft); }
.pl-task-badge { font-family:var(--mono); font-size:10px; letter-spacing:.05em; text-transform:uppercase; border:1px solid var(--line); border-radius:6px; padding:2px 5px; background:var(--surface); color:var(--ink-soft); }
.pl-task-badge.attention { border-color:var(--mark); background:var(--mark); color:var(--on-accent); }
.pl-task-actions { display:grid; grid-template-columns:repeat(2,max-content); gap:6px; align-items:center; justify-content:end; }

.pl-organize { display:grid; grid-template-columns:minmax(0,1fr) 260px; gap:18px; align-items:start; }
.pl-organize-main { display:flex; flex-direction:column; gap:16px; min-width:0; }
.pl-organize-head { display:flex; justify-content:space-between; align-items:flex-start; gap:14px; border-bottom:1px solid var(--line-soft); padding-bottom:14px; }
.pl-organize-eyebrow { font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.08em; color:var(--mark); margin-bottom:4px; }
.pl-organize-title { font-size:21px; line-height:1.15; font-weight:680; letter-spacing:-.01em; }
.pl-organize-copy { margin:5px 0 0; max-width:540px; font-size:13px; color:var(--ink-soft); }
.pl-organize-stat { border:1px solid var(--line); border-radius:10px; padding:9px 11px; min-width:84px; text-align:right; background:var(--paper); }
.pl-organize-stat b { display:block; font-family:var(--mono); font-size:22px; line-height:1; }
.pl-organize-stat span { display:block; margin-top:3px; font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:.06em; color:var(--ink-soft); }
.pl-tag-list { display:flex; flex-direction:column; gap:8px; }
.pl-tag-row { display:flex; align-items:center; gap:10px; min-width:0; padding:9px 11px; border:1px solid var(--line); border-radius:10px; background:var(--paper); }
.pl-tag-row[data-editing="true"] { align-items:flex-start; flex-wrap:wrap; }
.pl-tag-dot { width:14px; height:14px; border-radius:50%; flex:none; display:inline-block; }
.pl-tag-name { flex:1; min-width:0; font-size:14px; font-weight:580; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pl-tag-count { font-family:var(--mono); font-size:10px; color:var(--ink-soft); white-space:nowrap; }
.pl-color-grid { display:flex; gap:5px; flex-wrap:wrap; }
.pl-color-swatch { width:22px; height:22px; border-radius:50%; cursor:pointer; padding:0; flex:none; background:var(--swatch); border:2px solid transparent; }
.pl-color-swatch[aria-pressed="true"] { border-color:var(--ink); }
.pl-tag-new { padding:13px 14px; border:1px dashed var(--line); border-radius:10px; background:var(--surface); }
.pl-tag-new-title { font-family:var(--mono); font-size:10px; color:var(--ink-soft); text-transform:uppercase; letter-spacing:.08em; margin-bottom:10px; }
.pl-tag-new-row { display:flex; gap:8px; align-items:center; margin-top:10px; }
.pl-organize-side { border:1px solid var(--line); border-radius:12px; background:var(--paper); padding:13px; }
.pl-organize-side h3 { margin:0 0 8px; font-size:14px; font-weight:660; }
.pl-organize-side p { margin:0; font-size:12.5px; color:var(--ink-soft); }
.pl-organize-menu { display:flex; flex-direction:column; gap:6px; margin-top:12px; }
.pl-organize-menu button { border:1px solid var(--line); border-radius:9px; background:var(--surface); color:var(--ink); padding:9px 10px; text-align:left; font-size:13px; font-weight:560; }
.pl-organize-menu button[aria-pressed="true"] { border-color:var(--ink); box-shadow:inset 3px 0 0 var(--ink); }

.pl-listpick { position:relative; }
.pl-kindpick { position:relative; }
.pl-recur { border:1px solid var(--line); border-radius:8px; padding:0 9px; height:36px; font-size:13px; background:var(--paper); color:var(--ink); font-family:inherit; cursor:pointer; }
.pl-recur.sm { height:32px; font-size:12px; padding:0 7px; }
.pl-recur-badge { font-family:var(--mono); font-size:10px; letter-spacing:.04em; text-transform:uppercase; color:var(--mark); border:1px solid var(--mark-soft); border-radius:5px; padding:2px 5px; flex:none; }
.pl-location { font-size:11.5px; color:var(--ink-soft); text-decoration:none; display:inline-flex; align-items:center; gap:3px; border-radius:4px; padding:1px 3px; }
.pl-label-badge { display:inline-flex; align-items:center; gap:3px; font-size:10.5px; border-radius:4px; padding:1px 5px; font-weight:560; color:var(--badge-text); }
.pl-label-dot-sm { display:inline-block; width:7px; height:7px; border-radius:50%; flex:none; }
.pl-home-label { display:inline-block; width:8px; height:8px; border-radius:50%; flex:none; margin-right:3px; }
.pl-location:hover { color:var(--mark); background:var(--mark-soft); }
.pl-detail-panel { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin:2px 0 6px 44px; padding:8px 10px; background:var(--paper); border:1px solid var(--line); border-radius:9px; }

.pl-quickadd { border:1px solid var(--line); border-radius:12px; background:var(--surface); padding:14px; min-width:0; }
.pl-quickadd-head { display:flex; align-items:center; gap:8px; margin-bottom:10px; }
.pl-quickadd-head h3 { margin:0; font-size:15px; font-weight:660; }
.pl-quickadd-badge { font-family:var(--mono); font-size:10px; color:var(--ink-soft); letter-spacing:.06em; text-transform:uppercase; }
.pl-quickadd-row { display:flex; flex-direction:column; gap:8px; align-items:stretch; }
.pl-quickadd-input { width:100%; min-height:92px; border:1px solid var(--line); border-radius:9px; padding:10px 13px; font-size:14px; color:var(--ink); background:var(--paper); font-family:inherit; transition:border-color .15s; resize:none; }
.pl-quickadd-input:focus { border-color:var(--mark); outline:none; }
.pl-quickadd-btns { display:grid; grid-template-columns:auto auto minmax(118px,1fr); gap:6px; align-items:center; }
.pl-quickadd-btn { border:none; border-radius:9px; background:var(--mark); color:var(--on-accent); padding:0 18px; font-size:13px; font-weight:600; cursor:pointer; transition:opacity .15s; white-space:nowrap; height:38px; width:100%; }
.pl-quickadd-btn:disabled { opacity:.5; cursor:default; }
.pl-quickadd-attach { border:1px solid var(--line); border-radius:9px; background:var(--paper); font-size:16px; cursor:pointer; height:38px; width:44px; display:flex; align-items:center; justify-content:center; transition:border-color .15s; }
.pl-quickadd-attach:hover { border-color:var(--mark); }
.pl-quickadd-attach:disabled { opacity:.5; cursor:default; }
.pl-quickadd-mic.is-recording { border-color:#c0392b; background:#fdecea; animation:pl-mic-pulse 1.3s ease-in-out infinite; }
@keyframes pl-mic-pulse { 0%,100% { box-shadow:0 0 0 0 rgba(192,57,43,.45); } 50% { box-shadow:0 0 0 5px rgba(192,57,43,0); } }
.pl-quickadd-listening { margin:8px 0 0; font-size:12.5px; color:#c0392b; font-weight:550; }
.pl-quickadd-attachment { display:flex; align-items:center; gap:8px; margin-top:8px; padding:8px 10px; border:1px solid var(--line); border-radius:9px; background:var(--paper); }
.pl-quickadd-thumb { height:48px; width:auto; max-width:120px; border-radius:6px; object-fit:cover; }
.pl-quickadd-file-icon { font-size:28px; line-height:1; }
.pl-quickadd-file-name { flex:1; font-size:12px; color:var(--ink-soft); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pl-quickadd-remove { border:none; background:none; color:var(--ink-soft); cursor:pointer; font-size:14px; padding:2px 4px; border-radius:4px; }
.pl-quickadd-remove:hover { color:var(--mark); }
.pl-quickadd-preview { margin-top:12px; display:flex; flex-direction:column; gap:6px; }
.pl-quickadd-preview-item { display:flex; align-items:center; gap:8px; padding:8px 10px; border:1px solid var(--line); border-radius:9px; background:var(--paper); font-size:13px; }
.pl-quickadd-preview-kind { font-family:var(--mono); font-size:10px; letter-spacing:.06em; text-transform:uppercase; color:var(--mark); border:1px solid var(--mark-soft); border-radius:6px; padding:2px 5px; flex:none; }
.pl-quickadd-preview-kind.update { color:#b45309; border-color:#fde68a; background:#fffbeb; }
.pl-quickadd-preview-item.is-update { background:#fffbeb; border-color:#fde68a; }
.pl-quickadd-preview-title { flex:1; font-weight:560; }
.pl-quickadd-preview-date { font-family:var(--mono); font-size:10px; color:var(--ink-soft); }
.pl-quickadd-preview-changes { font-size:11px; color:#b45309; font-style:italic; }
.pl-quickadd-actions { display:flex; gap:8px; margin-top:4px; }
.pl-quickadd-confirm { border:none; border-radius:9px; background:var(--mark); color:var(--on-accent); padding:8px 18px; font-size:13px; font-weight:600; cursor:pointer; }
.pl-quickadd-cancel { border:1px solid var(--line); border-radius:9px; background:transparent; color:var(--ink-soft); padding:8px 14px; font-size:13px; cursor:pointer; }
.pl-quickadd-error { color:var(--mark); font-size:13px; margin-top:6px; }
.pl-quickadd-success { font-size:13px; margin-top:6px; color:#15803d; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:8px; padding:8px 12px; }

.pl-sticky-bar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:16px; }
.pl-sticky-bar input { flex:1; min-width:200px; border:1px solid var(--line); border-radius:9px; padding:9px 12px; font-size:14px; color:var(--ink); background:var(--paper); font-family:inherit; }
.pl-sticky-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:14px; }
.pl-sticky-card { background:#fef9c3; border:1px solid #e9d92a; border-radius:4px; padding:0; display:flex; flex-direction:column; box-shadow:2px 3px 7px rgba(0,0,0,.12); position:relative; transition:box-shadow .15s,transform .1s; }
.pl-sticky-card:hover { box-shadow:3px 5px 14px rgba(0,0,0,.18); transform:translateY(-2px); }
.pl-sticky-tab { height:28px; background:#f9e73a; border-radius:3px 3px 0 0; border-bottom:1px solid #e9d92a; display:flex; align-items:center; justify-content:flex-end; padding:0 6px; gap:4px; }
.pl-sticky-flag { border:none; background:transparent; font-size:13px; line-height:1; padding:3px; cursor:pointer; opacity:.5; transition:opacity .15s; }
.pl-sticky-flag:hover,.pl-sticky-flag[aria-pressed="true"] { opacity:1; }
.pl-sticky-del { border:none; background:transparent; color:#a08000; font-size:16px; line-height:1; padding:3px 5px; cursor:pointer; opacity:.5; transition:opacity .15s; }
.pl-sticky-del:hover { opacity:1; color:#c0392b; }
.pl-sticky-body { padding:12px; flex:1; }
.pl-sticky-text { width:100%; border:none; background:transparent; resize:none; font-family:inherit; font-size:14px; line-height:1.5; color:#3b2f00; min-height:80px; outline:none; }
.pl-sticky-text::placeholder { color:#b8a840; }
.pl-sticky-imp { position:absolute; top:6px; left:8px; font-size:11px; color:#b8860b; font-weight:700; display:none; }
.pl-sticky-card[data-imp="true"] .pl-sticky-imp { display:block; }
.pl-sticky-card[data-imp="true"] { background:#fde68a; border-color:#f59e0b; }
.pl-sticky-card[data-imp="true"] .pl-sticky-tab { background:#fbbf24; border-color:#f59e0b; }
.pl-undo-toast { position:fixed; bottom:24px; left:50%; transform:translateX(-50%); background:var(--ink); color:var(--paper); border-radius:10px; padding:11px 16px; display:flex; align-items:center; gap:12px; font-size:13.5px; box-shadow:0 8px 24px rgba(0,0,0,.28); z-index:100; white-space:nowrap; }
.pl-undo-btn { border:none; background:color-mix(in srgb, var(--paper) 15%, transparent); color:var(--paper); border-radius:6px; padding:4px 10px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit; transition:background .15s; }
.pl-undo-btn:hover { background:color-mix(in srgb, var(--paper) 28%, transparent); }
@keyframes pl-toast-in { from { opacity:0; transform:translateX(-50%) translateY(10px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }
.pl-undo-toast { animation:pl-toast-in .2s ease; }

.pl-sticky-home { display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); gap:8px; margin-top:2px; }
.pl-sticky-home-card { background:#fef9c3; border:1px solid #e9d92a; border-radius:4px; padding:8px 10px; font-size:12px; color:#3b2f00; line-height:1.4; overflow:hidden; box-shadow:1px 2px 4px rgba(0,0,0,.1); text-align:left; cursor:pointer; transition:box-shadow .12s,transform .1s; display:flex; flex-direction:column; gap:4px; }
.pl-sticky-home-card:hover { box-shadow:2px 4px 8px rgba(0,0,0,.15); transform:translateY(-1px); }
.pl-sticky-home-card[data-imp="true"] { background:#fde68a; border-color:#f59e0b; }
.pl-sticky-home-text { overflow:hidden; text-overflow:ellipsis; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; }
.pl-sticky-home-more { font-family:var(--mono); font-size:10px; color:#b8a840; }

.pl-week-outer { display:flex; flex-direction:column; border:1px solid var(--line); border-radius:14px; clip-path:inset(0 round 14px); background:var(--surface); }
.pl-week-sticky { position:sticky; top:0; z-index:5; background:var(--surface); border-radius:14px 14px 0 0; }
.pl-week-head { display:grid; grid-template-columns:36px repeat(7,1fr); border-bottom:1px solid var(--line); background:var(--surface); border-radius:14px 14px 0 0; }
.pl-week-gutter { border-right:1px solid var(--line-soft); }
.pl-week-dh { background:transparent; border:none; border-left:1px solid var(--line-soft); cursor:pointer; padding:6px 2px; display:flex; flex-direction:column; align-items:center; gap:2px; transition:background .12s; min-width:0; }
.pl-week-dh:hover { background:var(--line-soft); }
.pl-week-dh[data-today="true"] .pl-week-dnum-wrap { background:var(--mark); border-radius:50%; width:22px; height:22px; display:flex; align-items:center; justify-content:center; }
.pl-week-dh[data-today="true"] .pl-week-dnum { color:var(--on-accent); }
.pl-week-dletter { font-family:var(--mono); font-size:9px; text-transform:uppercase; letter-spacing:.06em; color:var(--ink-soft); line-height:1; }
.pl-week-dnum { font-family:var(--mono); font-size:13px; font-weight:600; color:var(--ink); line-height:1; }
.pl-week-allday { display:grid; grid-template-columns:36px repeat(7,1fr); border-bottom:2px solid var(--line); background:var(--paper); }
.pl-week-allday-label { font-family:var(--mono); font-size:8px; text-transform:uppercase; letter-spacing:.04em; color:var(--ink-soft); display:flex; align-items:flex-start; justify-content:flex-end; padding:5px 4px 4px 0; border-right:1px solid var(--line-soft); }
.pl-week-allday-col { padding:3px 2px; min-height:24px; cursor:pointer; border-left:1px solid var(--line-soft); min-width:0; }
.pl-week-allday-col:hover { background:var(--surface); }
.pl-week-adc { position:relative; display:flex; align-items:center; gap:3px; font-size:9px; line-height:1.3; padding:1px 8px 1px 5px; border-radius:3px; background:var(--line-soft); margin-bottom:1px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:var(--ink); }
.pl-week-adc::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--ink-soft); }
.pl-week-adc[data-kind="event"]::before { background:var(--mark); }
.pl-week-adc[data-attention="true"]::before { background:var(--mark); }
.pl-week-adc[data-imp="true"] { background:var(--mark-soft); color:var(--mark); }
.pl-week-adc[data-done="true"] { text-decoration:line-through; color:var(--ink-soft); }
.pl-week-adc-title { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pl-week-adc[data-kind="task"] { border:1px solid var(--line); border-radius:999px; background:var(--surface); padding:1px 8px 1px 6px; }
.pl-week-adc[data-kind="task"] .pl-week-adc-title { font-size:8.5px; font-weight:500; color:var(--ink-soft); }
.pl-week-adc[data-kind="task"][data-attention="true"] .pl-week-adc-title { color:var(--ink); }
.pl-week-adc-more { display:block; font-family:var(--mono); font-size:8px; color:var(--ink-soft); padding:1px 3px; }
.pl-week-scroll { overflow-y:auto; max-height:calc(100vh - 260px); }
.pl-week-timegrid { display:grid; grid-template-columns:36px 1fr; }
.pl-week-timelabels { display:flex; flex-direction:column; background:var(--surface); border-right:1px solid var(--line-soft); }
.pl-week-hlabel { font-family:var(--mono); font-size:9px; color:var(--ink-soft); display:flex; align-items:flex-start; justify-content:flex-end; padding:0 6px 0 0; transform:translateY(-6px); flex:none; }
.pl-week-daycols { display:grid; grid-template-columns:repeat(7,1fr); position:relative; }
.pl-week-hline { position:absolute; left:0; right:0; border-top:1px solid var(--line-soft); pointer-events:none; z-index:0; }
.pl-week-hline-half { position:absolute; left:0; right:0; border-top:1px dashed var(--line-soft); pointer-events:none; z-index:0; opacity:.5; }
.pl-week-now { position:absolute; left:0; right:0; height:2px; background:var(--mark); z-index:3; pointer-events:none; }
.pl-week-now::before { content:''; position:absolute; left:-1px; top:-4px; width:9px; height:9px; border-radius:50%; background:var(--mark); }
.pl-week-dc { position:relative; border-left:1px solid var(--line-soft); cursor:pointer; min-width:0; }
.pl-week-dc:hover { background:var(--line-soft); }
.pl-week-dc[data-today="true"] { background:rgba(255,92,61,.028); }
.pl-week-dc[data-dragover="true"] { background:var(--mark-soft); }
.pl-week-ev { position:absolute; left:2px; right:2px; border-radius:5px; padding:3px 4px 3px 6px; overflow:hidden; cursor:pointer; background:var(--surface); border:1px solid var(--line); z-index:2; transition:filter .12s; }
.pl-week-ev::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--ink-soft); }
.pl-week-ev:hover { filter:brightness(.97); }
.pl-week-ev[data-imp="true"] { background:var(--mark-soft); }
.pl-week-ev[data-done="true"] { opacity:.5; }
.pl-week-ev[data-kind="event"]::before { background:var(--mark); }
.pl-week-ev[data-kind="task"] { border-radius:999px; display:grid; grid-template-columns:auto minmax(0,1fr); column-gap:3px; align-items:center; padding-top:3px; padding-bottom:3px; }
.pl-week-ev[data-attention="true"]::before { background:var(--mark); }
.pl-week-ev-time { display:none; }
.pl-week-ev-title { display:block; min-width:0; font-size:10px; font-weight:620; line-height:1.18; overflow:hidden; text-overflow:ellipsis; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; color:var(--ink); }
.pl-week-task-check { grid-row:1 / span 2; display:inline-flex; align-items:center; justify-content:center; width:6px; height:6px; border:1.2px solid var(--ink-soft); border-radius:2px; color:var(--ink-soft); font-size:8px; line-height:1; margin-left:1px; transform:translateY(.5px); }
.pl-week-task-check[data-done="true"] { width:8px; height:8px; border:none; }
.pl-week-ev[data-kind="task"] .pl-week-ev-title { grid-column:2; font-size:10px; font-weight:560; line-height:1.1; -webkit-line-clamp:1; }
.pl-week-ev[data-imp="true"] .pl-week-ev-title,.pl-week-ev[data-imp="true"] .pl-week-ev-time { color:var(--mark); }
@media (max-width:760px) {
  .pl-week-scroll { max-height:none; overflow-y:visible; }
  .pl-popout { left:50%; right:auto; transform:translateX(-50%); width:min(230px, calc(100vw - 32px)); }
  .pl-inline .pl-popout { left:50%; right:auto; }
  .pl-time-popout { position:fixed; left:50%; right:auto; top:auto; bottom:calc(18px + env(safe-area-inset-bottom, 0px)); transform:translateX(-50%); width:min(320px, calc(100vw - 28px)); z-index:70; box-shadow:0 20px 52px rgba(0,0,0,.34); }
  .pl-week-ev { padding:3px 2px 3px 5px; }
  .pl-week-ev-title { font-size:9px; line-height:1.08; -webkit-line-clamp:4; }
}

@media (max-width:760px) {
  .pl-home-hero, .pl-home-grid { grid-template-columns:1fr; }
  .pl-organize { grid-template-columns:1fr; }
  .pl-invite-banner { grid-template-columns:1fr; }
  .pl-invite-actions { justify-content:flex-start; }
  .pl-home-section.wide { grid-column:auto; }
  .pl-home-stats { grid-template-columns:repeat(3,minmax(0,1fr)); }
  .pl-home-title { font-size:24px; }
  .pl-home-item { grid-template-columns:1fr auto; }
  .pl-home-kind { grid-column:1 / -1; width:max-content; }
  .pl-task-head { align-items:flex-start; }
  .pl-task-add { grid-template-columns:1fr; }
  .pl-task-create-actions { grid-template-columns:1.34fr 1fr .9fr; gap:6px; }
  .pl-task-helpwrap { grid-template-columns:minmax(0,1fr) 20px; height:48px; }
  .pl-task-toggle { justify-content:center; height:46px; padding:0 2px 0 8px; gap:5px; font-size:9.5px; }
  .pl-task-toggle-dot { width:7px; height:7px; }
  .pl-task-create-actions .pl-addbtn { height:48px; padding:0 10px; }
  .pl-theme-row { grid-template-columns:1fr; }
  .pl-theme-options { width:100%; }
  .pl-theme-options button { flex:1; min-width:0; }
  .pl-task-row { grid-template-columns:auto minmax(0,1fr); }
  .pl-task-actions { grid-column:2; justify-content:flex-start; }
  .pl-row { grid-template-columns:1fr; }
  .pl-acts { justify-content:flex-start; flex-wrap:wrap; }
  .pl-list-card { padding:12px; }
  .pl-list-card-head { grid-template-columns:auto minmax(0,1fr); align-items:center; column-gap:10px; row-gap:7px; }
  .pl-list-card-controls { align-self:start; padding-top:1px; }
  .pl-list-card-title { align-items:center; min-height:34px; }
  .pl-list-card-actions { grid-column:2; justify-content:flex-start; }
  .pl-list-card[data-hidden="true"] .pl-list-card-head { grid-template-columns:minmax(0,1fr); }
  .pl-list-card[data-hidden="true"] .pl-list-card-actions { grid-column:1; }
  .pl-list-name { min-width:0; font-size:14px; }
  .pl-list-edit-btn { padding:4px 7px; font-size:9.5px; }
  .pl-list-row { grid-template-columns:minmax(0,1fr) auto; }
  .pl-list-row .pl-acts { justify-content:flex-end; flex-wrap:nowrap; }
  .pl-main { grid-template-columns:1fr; }
  .pl-nav.has-month-span { flex-wrap:wrap; }
  .pl-nav.has-month-span .title { flex-basis:100%; order:4; }
  .pl-nav.has-month-span .pl-month-span { order:3; margin-left:auto; }
  .pl-months[data-span="2"] { grid-template-columns:1fr; }
  .pl-year { grid-template-columns:repeat(2,1fr); }
  .pl-daynum { font-size:34px; }
  .pl input, .pl textarea, .pl select { font-size:16px !important; }
  .pl { height:100dvh; overflow:hidden; display:flex; flex-direction:column; padding-bottom:0; }
  .pl-shell { flex:1; min-height:0; display:flex; flex-direction:column; overflow:hidden; }
  .pl-top { flex-shrink:0; }
  .pl-nav { flex-shrink:0; }
  .pl-main { flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; padding-bottom:max(20px,env(safe-area-inset-bottom)); }
}
@media (prefers-reduced-motion:reduce) { .pl * { transition:none !important; } }
`;

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

let mapsScriptLoaded = false;
function loadMapsScript() {
  if (!MAPS_KEY || mapsScriptLoaded) return;
  mapsScriptLoaded = true;
  const s = document.createElement('script');
  s.id = 'gmap-script';
  s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&loading=async&libraries=places`;
  s.async = true;
  document.head.appendChild(s);
}
loadMapsScript();

interface PlaceSuggestion { description: string; place_id: string; mainText: string; secondaryText: string }

type PlacesLib = {
  AutocompleteSuggestion: { fetchAutocompleteSuggestions: (req: { input: string; sessionToken?: unknown }) => Promise<{ suggestions: Array<{ placePrediction: { placeId: string; text: { toString(): string }; mainText: { toString(): string }; secondaryText: { toString(): string } } }> }> };
  AutocompleteSessionToken: new () => unknown;
};
type GMaps = { maps: { importLibrary: (lib: string) => Promise<PlacesLib> } };

function PlacesInput({ value, onChange, onBlur, onKeyDown, placeholder, style, 'aria-label': ariaLabel }: {
  value: string;
  onChange: (val: string) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
}) {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTokenRef = useRef<unknown>(null);

  useEffect(() => {
    function close(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  async function fetchSuggestions(input: string) {
    if (!MAPS_KEY || !input.trim()) { setSuggestions([]); setOpen(false); return; }
    try {
      const g = (window as unknown as { google?: GMaps }).google;
      if (!g?.maps?.importLibrary) { setSuggestions([]); setOpen(false); return; }
      const { AutocompleteSuggestion, AutocompleteSessionToken } = await g.maps.importLibrary('places');
      if (!sessionTokenRef.current) sessionTokenRef.current = new AutocompleteSessionToken();
      const { suggestions: raw } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({ input, sessionToken: sessionTokenRef.current });
      setSuggestions(raw.map(s => ({
        description: s.placePrediction.text.toString(),
        place_id: s.placePrediction.placeId,
        mainText: s.placePrediction.mainText.toString(),
        secondaryText: s.placePrediction.secondaryText.toString(),
      })));
      setOpen(true);
    } catch (err) {
      console.error('[PlacesInput]', err);
      setSuggestions([]); setOpen(false);
    }
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v.trim()) { setSuggestions([]); setOpen(false); sessionTokenRef.current = null; return; }
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 200);
  }

  function pick(s: PlaceSuggestion) {
    onChange(s.description);
    setSuggestions([]);
    setOpen(false);
    sessionTokenRef.current = null;
  }

  return (
    <div ref={wrapRef} className="pl-places-wrap" style={style}>
      <input
        type="text"
        placeholder={placeholder}
        aria-label={ariaLabel}
        value={value}
        onChange={handleChange}
        onBlur={() => { setTimeout(() => { setOpen(false); onBlur?.(); }, 150); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); }
          onKeyDown?.(e);
        }}
        style={{width:'100%',border:'1px solid var(--line)',borderRadius:'6px',padding:'4px 7px',fontSize:'12px',background:'var(--paper)',color:'var(--ink)'}}
      />
      {open && suggestions.length > 0 && (
        <div className="pl-places-suggestions">
          {suggestions.map((s) => (
            <button key={s.place_id} className="pl-places-item" onMouseDown={() => pick(s)}>
              <span>{s.mainText}</span>
              <small>{s.secondaryText}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

type View = 'home' | 'day' | 'week' | 'month' | 'year' | 'tasks' | 'lists' | 'notes' | 'organize' | 'settings';
type Kind = 'note' | 'task' | 'event' | 'list';
type MonthSpan = 1 | 2;

type HomeEntry = {
  item: Item;
  rootDate: string;
  ancestors: Item[];
};

type TaskEntry = {
  item: Item;
  rootDate: string | null;
  ancestors: Item[];
  list: Item | null;
};
type HeroFollowItem = {
  id: string;
  title: string;
  kind: string;
  when: string;
  dateKey?: string | null;
  dateLabel?: string;
  timeLabel?: string;
  tagIds: string[];
  homeEntry?: HomeEntry;
  taskEntry?: TaskEntry;
};

type OccurrencePatch = ItemOccurrenceOverride;
type OccurrencePromptMode = 'series' | 'future';
type OccurrenceScopeChoice = 'occurrence' | 'future' | 'series' | 'cancel';
type OccurrenceScopePrompt = {
  action: string;
  occurrenceKey: string;
  mode: OccurrencePromptMode;
  resolve: (choice: OccurrenceScopeChoice) => void;
};

interface PlannerProps {
  email: string;
  getToken: () => Promise<string>;
  onSignOut: () => void;
}

export default function Planner({ email, getToken, onSignOut }: PlannerProps) {
  const store = useMemo(() => new OfflineSyncStore(new ApiStore(getToken)), [getToken]);
  const deviceId = useMemo(() => getDeviceId(), []);
  const [items, setItems] = useState<Item[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState<View>('home');
  const [monthSpan, setMonthSpan] = useState<MonthSpan>(() => loadMonthSpan());
  const [isPortrait, setIsPortrait] = useState(false);
  const [cursor, setCursor] = useState(() => new Date());
  const [selKey, setSelKey] = useState(() => todayKey());
  const [draft, setDraft] = useState('');
  const [draftKind, setDraftKind] = useState<Kind>('event');
  const [draftTime, setDraftTime] = useState('');
  const [draftEndTime, setDraftEndTime] = useState('');
  const [taskDraft, setTaskDraft] = useState('');
  const [taskDraftImportant, setTaskDraftImportant] = useState(false);
  const [taskDraftFlagged, setTaskDraftFlagged] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set<string>());
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [childDraft, setChildDraft] = useState('');
  const [childKind, setChildKind] = useState<Kind>('task');
  const [childTime, setChildTime] = useState('');
  const [childEndTime, setChildEndTime] = useState('');
  const [childDate, setChildDate] = useState('');
  const [childDueMode, setChildDueMode] = useState('');
  const [remEditing, setRemEditing] = useState<string | null>(null);
  const [remCustomFor, setRemCustomFor] = useState<string | null>(null);
  const [timeEditing, setTimeEditing] = useState<string | null>(null);
  const [draftTimeOpen, setDraftTimeOpen] = useState(false);
  const [childTimeOpen, setChildTimeOpen] = useState(false);
  const [titleEditing, setTitleEditing] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [occurrencePrompt, setOccurrencePrompt] = useState<OccurrenceScopePrompt | null>(null);
  const [addToListFor, setAddToListFor] = useState<string | null>(null);
  const [newListDraft, setNewListDraft] = useState('');
  // Lists view: which list is in "edit as text" mode, and the textarea buffer.
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [listText, setListText] = useState('');
  // Per-list buffer for the one-off quick-add row on the checkbox (shop) view.
  const [quickAddDrafts, setQuickAddDrafts] = useState<Record<string, string>>({});
  const [hiddenCheckedLists, setHiddenCheckedLists] = useState<Set<string>>(() => loadHiddenCheckedLists());
  const listDrag = useDragReorder();
  const [historyItemId, setHistoryItemId] = useState<string | null>(null);
  const [libraryRemoveItemId, setLibraryRemoveItemId] = useState<string | null>(null);
  const [priceDraft, setPriceDraft] = useState('');
  const [priceDateDraft, setPriceDateDraft] = useState('');
  const [checkoffHistoryOpen, setCheckoffHistoryOpen] = useState(false);
  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false);
  const [priceAddOpen, setPriceAddOpen] = useState(false);
  const [draftRecurrence, setDraftRecurrence] = useState('');
  const [childRecurrence, setChildRecurrence] = useState('');
  const [locDraft, setLocDraft] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0].value);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('');
  const [tagError, setTagError] = useState('');
  const [stickyDraft, setStickyDraft] = useState('');
  const [stickyEditing, setStickyEditing] = useState<string | null>(null);
  const [stickyEditDraft, setStickyEditDraft] = useState('');
  const [undoNote, setUndoNote] = useState<Item | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [qaText, setQaText] = useState('');
  const [qaLoading, setQaLoading] = useState(false);
  const [qaError, setQaError] = useState('');
  const [qaSuccess, setQaSuccess] = useState('');
  const qaSuccessTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  type QaItem = { action: 'create' | 'update'; id?: string | null; kind: string; title: string; date?: string | null; time?: string | null; endTime?: string | null; endDate?: string | null; location?: string | null; recurrence?: string | null; parentId?: string | null; important?: boolean; needsAttention?: boolean; tagIds?: string[]; listName?: string | null; listId?: string | null; changes?: string | null };
  const [qaPreview, setQaPreview] = useState<QaItem[] | null>(null);
  const [qaKindOverrides, setQaKindOverrides] = useState<Record<number, string>>({});
  const [qaAttachment, setQaAttachment] = useState<{ data: string; mediaType: string; name: string; previewUrl?: string } | null>(null);
  const qaFileRef = useRef<HTMLInputElement>(null);
  const dictation = useDictation(setQaText);
  const mountedRef = useRef(true);
  const refreshInFlightRef = useRef(false);
  const lastRefreshRef = useRef(0);
  const googleConnectLeavingRef = useRef(false);
  const googleCallbackHandledRef = useRef(false);
  const notificationRouteHandledRef = useRef(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [syncError, setSyncError] = useState('');
  const [notificationPermission, setNotificationPermission] = useState<AppNotificationPermission>(() => getNotificationPermission());
  const [alertsEnabled, setAlertsEnabled] = useState(() => loadAlertsEnabled(getNotificationPermission()));
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');
  const [settingsPane, setSettingsPane] = useState<'home' | 'notifications' | 'library' | 'shared' | 'imports'>('home');
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => loadThemePreference());
  const [weekStartHour, setWeekStartHour] = useState<number>(() => {
    try { const v = localStorage.getItem(WEEK_START_HOUR_KEY); return v !== null ? parseInt(v, 10) : 7; } catch { return 7; }
  });

  useEffect(() => {
    const portraitQuery = window.matchMedia?.('(orientation: portrait)');
    if (!portraitQuery) return;
    const syncMonthSpanToOrientation = () => setIsPortrait(portraitQuery.matches);
    syncMonthSpanToOrientation();
    portraitQuery.addEventListener('change', syncMonthSpanToOrientation);
    return () => portraitQuery.removeEventListener('change', syncMonthSpanToOrientation);
  }, []);

  useEffect(() => {
    saveMonthSpan(monthSpan);
  }, [monthSpan]);

  // Shared calendars (spaces) state
  const [spaces, setSpaces] = useState<SpaceWithMembers[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [spacesLoaded, setSpacesLoaded] = useState(false);
  const [spaceBusy, setSpaceBusy] = useState(false);
  const [spaceError, setSpaceError] = useState('');
  const [newSpaceName, setNewSpaceName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitingSpaceId, setInvitingSpaceId] = useState<string | null>(null);

  // One-way calendar imports
  const [calendarImports, setCalendarImports] = useState<CalendarImportSource[]>([]);
  const [googleCalendarCandidates, setGoogleCalendarCandidates] = useState<GoogleCalendarCandidate[]>([]);
  const [importsLoaded, setImportsLoaded] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState('');
  // Calendar overlay: which calendar new items go on, and which are hidden.
  const [draftSpaceId, setDraftSpaceId] = useState<string | null>(null);
  const [hiddenCalendars, setHiddenCalendars] = useState<Set<string>>(() => loadHiddenCalendars());

  // Personal calendar is represented by the 'personal' sentinel key.
  const calKey = (spaceId: string | null | undefined) => spaceId ?? 'personal';
  const pruneHiddenCalendars = useCallback((group: 'spaces' | 'imports', availableIds: string[]) => {
    setHiddenCalendars((prev) => {
      const next = pruneHiddenCalendarKeys(prev, group, availableIds);
      if (next === prev) return prev;
      saveHiddenCalendars(next);
      return next;
    });
  }, []);

  useEffect(() => {
    applyThemePreference(themePreference);
    try { localStorage.setItem(THEME_PREFERENCE_KEY, themePreference); } catch {}
    if (themePreference !== 'system') return;

    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const updateSystemTheme = () => applyThemePreference('system');
    media.addEventListener?.('change', updateSystemTheme);
    return () => media.removeEventListener?.('change', updateSystemTheme);
  }, [themePreference]);
  const removeHiddenCalendar = useCallback((spaceId: string) => {
    setHiddenCalendars((prev) => {
      if (!prev.has(spaceId)) return prev;
      const next = new Set(prev);
      next.delete(spaceId);
      saveHiddenCalendars(next);
      return next;
    });
  }, []);
  const calendars = useMemo(
    () => [{ id: null as string | null, name: 'Personal', color: PERSONAL_CALENDAR_COLOR }, ...spaces.map((s) => ({ id: s.id as string | null, name: s.name, color: s.color }))],
    [spaces],
  );
  const importById = useMemo(() => Object.fromEntries(calendarImports.map((source) => [source.id, source])), [calendarImports]);
  const itemCalendarKey = (item: Item) => item.externalSource ? importCalKey(item.externalSource.importId) : calKey(item.spaceId);
  const isCalendarVisible = (spaceId: string | null | undefined) => !hiddenCalendars.has(calKey(spaceId));
  const isItemVisible = (item: Item) => !hiddenCalendars.has(itemCalendarKey(item));
  const toggleCalendar = (spaceId: string | null) => {
    setHiddenCalendars((prev) => {
      const next = new Set(prev);
      const k = calKey(spaceId);
      if (next.has(k)) next.delete(k); else next.add(k);
      saveHiddenCalendars(next);
      return next;
    });
  };
  const toggleCalendarKey = (key: string) => {
    setHiddenCalendars((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveHiddenCalendars(next);
      return next;
    });
  };
  // Only items on currently-visible calendars are shown in the views.
  const visibleItems = useMemo(() => items.filter((i) => isItemVisible(i)), [items, hiddenCalendars, calendarImports]);
  const googleAccountEmails = useMemo(() => {
    const emails = new Set<string>();
    googleCalendarCandidates.forEach((candidate) => emails.add(candidate.accountEmail));
    calendarImports.forEach((source) => { if (source.accountEmail) emails.add(source.accountEmail); });
    return [...emails].sort();
  }, [calendarImports, googleCalendarCandidates]);

  const applyDeviceReminders = useCallback(async (baseItems: Item[]): Promise<Item[]> => {
    let reminders: Awaited<ReturnType<typeof store.listDeviceReminders>> = [];
    try {
      reminders = await store.listDeviceReminders(deviceId);
    } catch {
      reminders = [];
    }
    const reminderByItem = new Map(reminders.filter((entry) => entry.reminder).map((entry) => [entry.itemId, entry.reminder]));
    return baseItems.map((item) => ({ ...item, reminder: reminderByItem.get(item.id) ?? null }));
  }, [deviceId, store]);

  const refreshSpaces = useCallback(async () => {
    const [sp, inv] = await Promise.all([store.listSpaces(), store.listInvites()]);
    if (!mountedRef.current) return;
    setSpaces(sp);
    setInvites(inv);
    pruneHiddenCalendars('spaces', sp.map((space) => space.id));
    setDraftSpaceId((current) => (current && !sp.some((s) => s.id === current) ? null : current));
    setSpacesLoaded(true);
  }, [pruneHiddenCalendars, store]);

  const refreshCalendarImports = useCallback(async () => {
    const sources = await store.listCalendarImports();
    let candidates: GoogleCalendarCandidate[] = [];
    try {
      candidates = await store.listGoogleCalendarCandidates();
    } catch {
      // Passive settings load should not show an error before the user chooses
      // to connect Google. Connect/import actions surface their own failures.
      candidates = [];
    }
    if (!mountedRef.current) return;
    setCalendarImports(sources);
    setGoogleCalendarCandidates(candidates);
    setImportsLoaded(true);
    pruneHiddenCalendars('imports', sources.map((source) => source.id));
  }, [pruneHiddenCalendars, store]);

  const refreshData = useCallback(async (options?: { showSpinner?: boolean; minIntervalMs?: number }) => {
    const now = Date.now();
    if (refreshInFlightRef.current) return;
    if (options?.minIntervalMs && now - lastRefreshRef.current < options.minIntervalMs) return;

    refreshInFlightRef.current = true;
    if (options?.showSpinner) setRefreshing(true);

    try {
      const data = navigator.onLine ? await store.refresh() : store.cachedList();
      const itemsWithDeviceReminders = await applyDeviceReminders(data);
      if (!mountedRef.current) return;
      setItems(normalizeItems(itemsWithDeviceReminders));
      lastRefreshRef.current = Date.now();
      setLastSyncedAt(lastRefreshRef.current);
      setSyncError('');

      store.listTags()
        .then((fetchedTags) => {
          if (mountedRef.current) setTags(fetchedTags);
        })
        .catch(() => undefined);

      refreshSpaces().catch(() => {
        if (mountedRef.current) setSpacesLoaded(true);
      });
      refreshCalendarImports().catch(() => {
        if (mountedRef.current) setImportsLoaded(true);
      });
    } catch (err) {
      if (mountedRef.current) {
        setSyncError(err instanceof Error ? err.message : 'Sync failed');
      }
    } finally {
      refreshInFlightRef.current = false;
      if (mountedRef.current && options?.showSpinner) setRefreshing(false);
    }
  }, [applyDeviceReminders, refreshCalendarImports, refreshSpaces, store]);

  // ── Shared calendars (spaces) ───────────────────────────────────────────────

  const loadSpaces = useCallback(async () => {
    try {
      await refreshSpaces();
    } catch {
      // offline or not signed in yet — leave prior state
      if (mountedRef.current) setSpacesLoaded(true);
    }
  }, [refreshSpaces]);

  const loadCalendarImports = useCallback(async () => {
    try {
      await refreshCalendarImports();
    } catch (e) {
      if (mountedRef.current && !googleConnectLeavingRef.current) {
        setImportsLoaded(true);
        setCalendarImports((prev) => {
          if (prev.length === 0) setImportError('');
          else setImportError(e instanceof Error ? e.message : 'Could not load calendar imports');
          return prev;
        });
      }
    }
  }, [refreshCalendarImports]);

  async function handleConnectGoogleCalendar() {
    googleConnectLeavingRef.current = false;
    setImportBusy(true); setImportError('');
    try {
      const { authUrl } = await store.startGoogleCalendarConnect();
      googleConnectLeavingRef.current = true;
      setImportError('');
      window.location.assign(authUrl);
    } catch (e) {
      googleConnectLeavingRef.current = false;
      const message = e instanceof Error ? e.message : '';
      setImportError(
        message.includes('Google Calendar imports are not configured')
          ? 'Google Calendar is not configured on staging yet. Once the Google OAuth client is added, this button will open Google consent.'
          : message || 'Could not start Google Calendar connection',
      );
    } finally {
      if (!googleConnectLeavingRef.current) setImportBusy(false);
    }
  }

  async function handleCreateGoogleCalendarImport(candidate: GoogleCalendarCandidate) {
    setImportBusy(true); setImportError('');
    try {
      const created = await store.createGoogleCalendarImport({ accountEmail: candidate.accountEmail, calendarId: candidate.calendarId });
      setCalendarImports((prev) => prev.some((source) => source.id === created.id) ? prev : [...prev, created]);
      setGoogleCalendarCandidates((prev) => prev.map((cal) => (
        cal.accountEmail === candidate.accountEmail && cal.calendarId === candidate.calendarId ? { ...cal, selected: true } : cal
      )));
      const synced = await store.syncCalendarImport(created.id);
      setCalendarImports((prev) => prev.map((source) => source.id === created.id ? synced : source));
      await refreshData({ minIntervalMs: 0 });
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Could not import that Google calendar');
    } finally {
      setImportBusy(false);
    }
  }

  async function handleUpdateCalendarImport(importId: string, patch: Partial<Pick<CalendarImportSource, 'privacy' | 'includeDeclined' | 'includeAllDay'>>) {
    setImportBusy(true); setImportError('');
    try {
      const updated = await store.updateCalendarImport(importId, patch);
      setCalendarImports((prev) => prev.map((source) => source.id === importId ? updated : source));
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Could not update calendar import');
    } finally {
      setImportBusy(false);
    }
  }

  async function handleSyncCalendarImport(importId: string) {
    setImportBusy(true); setImportError('');
    try {
      const updated = await store.syncCalendarImport(importId);
      setCalendarImports((prev) => prev.map((source) => source.id === importId ? updated : source));
      await refreshData({ minIntervalMs: 0 });
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Could not sync calendar import');
    } finally {
      setImportBusy(false);
    }
  }

  async function handleRemoveCalendarImport(importId: string) {
    const source = calendarImports.find((s) => s.id === importId);
    if (!window.confirm(`Disconnect ${source?.calendarName ?? 'this calendar import'}? Imported Google events from this calendar will be removed from Wingmate.`)) return;
    setImportBusy(true); setImportError('');
    try {
      await store.removeCalendarImport(importId);
      setCalendarImports((prev) => prev.filter((s) => s.id !== importId));
      if (source) {
        setGoogleCalendarCandidates((prev) => prev.map((candidate) => (
          candidate.accountEmail === source.accountEmail && candidate.calendarId === source.calendarId
            ? { ...candidate, selected: false }
            : candidate
        )));
      }
      removeHiddenCalendar(importCalKey(importId));
      await refreshData({ minIntervalMs: 0 });
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Could not disconnect calendar import');
    } finally {
      setImportBusy(false);
    }
  }

  async function handleDisconnectGoogleAccount(accountEmail: string) {
    const importCount = calendarImports.filter((source) => source.accountEmail === accountEmail).length;
    const detail = importCount > 0
      ? ` This will also remove ${importCount} imported calendar${importCount === 1 ? '' : 's'} and their imported events from Wingmate.`
      : ' No calendars have been imported from this account yet.';
    if (!window.confirm(`Disconnect ${accountEmail} from Google Calendar?${detail}`)) return;
    setImportBusy(true); setImportError('');
    try {
      await store.disconnectGoogleCalendarAccount(accountEmail);
      setGoogleCalendarCandidates((prev) => prev.filter((candidate) => candidate.accountEmail !== accountEmail));
      const removedImportIds = new Set(calendarImports.filter((source) => source.accountEmail === accountEmail).map((source) => source.id));
      setCalendarImports((prev) => prev.filter((source) => source.accountEmail !== accountEmail));
      setItems((prev) => prev.filter((item) => !item.externalSource?.importId || !removedImportIds.has(item.externalSource.importId)));
      setHiddenCalendars((prev) => {
        const next = new Set([...prev].filter((key) => ![...removedImportIds].some((id) => key === importCalKey(id))));
        saveHiddenCalendars(next);
        return next;
      });
      await refreshData({ minIntervalMs: 0 });
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Could not disconnect Google account');
    } finally {
      setImportBusy(false);
    }
  }

  async function handleCreateSpace() {
    const name = newSpaceName.trim();
    if (!name) return;
    setSpaceBusy(true); setSpaceError('');
    try {
      const space = await store.createSpace(name, '#086574');
      setNewSpaceName('');
      setSpaces((prev) => [...prev, space]);
    } catch (e) {
      setSpaceError(e instanceof Error ? e.message : 'Could not create calendar');
    } finally {
      setSpaceBusy(false);
    }
  }

  async function handleInvite(spaceId: string) {
    const email = inviteEmail.trim();
    if (!email) return;
    setSpaceBusy(true); setSpaceError('');
    try {
      const invite = await store.inviteToSpace(spaceId, email);
      setInviteEmail('');
      setInvitingSpaceId(null);
      setSpaces((prev) => prev.map((s) => (
        s.id === spaceId
          ? { ...s, pendingInvites: [...(s.pendingInvites ?? []).filter((i) => i.email !== invite.email), invite] }
          : s
      )));
    } catch (e) {
      setSpaceError(e instanceof Error ? e.message : 'Could not send invite');
    } finally {
      setSpaceBusy(false);
    }
  }

  async function handleRevokeInvite(spaceId: string, email: string) {
    setSpaceBusy(true); setSpaceError('');
    try {
      await store.revokeSpaceInvite(spaceId, email);
      setSpaces((prev) => prev.map((s) => (
        s.id === spaceId
          ? { ...s, pendingInvites: (s.pendingInvites ?? []).filter((i) => i.email !== email) }
          : s
      )));
    } catch (e) {
      setSpaceError(e instanceof Error ? e.message : 'Could not revoke invite');
    } finally {
      setSpaceBusy(false);
    }
  }

  async function handleRemoveMember(spaceId: string, userId: string) {
    setSpaceBusy(true); setSpaceError('');
    try {
      await store.removeSpaceMember(spaceId, userId);
      setSpaces((prev) => prev.map((s) => (
        s.id === spaceId
          ? { ...s, members: s.members.filter((m) => m.userId !== userId) }
          : s
      )));
      void refreshData();
    } catch (e) {
      setSpaceError(e instanceof Error ? e.message : 'Could not remove member');
    } finally {
      setSpaceBusy(false);
    }
  }

  async function handleSetMemberRole(spaceId: string, userId: string, role: Exclude<SpaceRole, 'owner'>) {
    setSpaceBusy(true); setSpaceError('');
    try {
      const member = await store.updateSpaceMemberRole(spaceId, userId, role);
      setSpaces((prev) => prev.map((s) => (
        s.id === spaceId
          ? { ...s, members: s.members.map((m) => (m.userId === userId ? member : m)) }
          : s
      )));
    } catch (e) {
      setSpaceError(e instanceof Error ? e.message : 'Could not update member role');
    } finally {
      setSpaceBusy(false);
    }
  }

  async function handleAcceptInvite(spaceId: string) {
    setSpaceBusy(true); setSpaceError('');
    try {
      const space = await store.acceptInvite(spaceId);
      setInvites((prev) => prev.filter((i) => i.spaceId !== spaceId));
      setSpaces((prev) => prev.some((s) => s.id === space.id) ? prev : [...prev, space]);
      void refreshData();
    } catch (e) {
      setSpaceError(e instanceof Error ? e.message : 'Could not accept invite');
    } finally {
      setSpaceBusy(false);
    }
  }

  async function handleDeclineInvite(spaceId: string) {
    setSpaceBusy(true); setSpaceError('');
    try {
      await store.declineInvite(spaceId);
      setInvites((prev) => prev.filter((i) => i.spaceId !== spaceId));
    } catch (e) {
      setSpaceError(e instanceof Error ? e.message : 'Could not decline invite');
    } finally {
      setSpaceBusy(false);
    }
  }

  async function handleLeaveSpace(spaceId: string) {
    setSpaceBusy(true); setSpaceError('');
    try {
      await store.leaveSpace(spaceId);
      setSpaces((prev) => prev.filter((s) => s.id !== spaceId));
      removeHiddenCalendar(spaceId);
      if (draftSpaceId === spaceId) setDraftSpaceId(null);
      void refreshData();
    } catch (e) {
      setSpaceError(e instanceof Error ? e.message : 'Could not leave calendar');
    } finally {
      setSpaceBusy(false);
    }
  }

  async function handleDeleteSpace(spaceId: string) {
    const space = spaces.find((s) => s.id === spaceId);
    if (!window.confirm(`Delete ${space?.name ?? 'this shared calendar'}? This removes the calendar and all of its shared items for everyone.`)) return;
    setSpaceBusy(true); setSpaceError('');
    try {
      await store.deleteSpace(spaceId);
      setSpaces((prev) => prev.filter((s) => s.id !== spaceId));
      removeHiddenCalendar(spaceId);
      if (draftSpaceId === spaceId) setDraftSpaceId(null);
      void refreshData();
    } catch (e) {
      setSpaceError(e instanceof Error ? e.message : 'Could not delete calendar');
    } finally {
      setSpaceBusy(false);
    }
  }

  useEffect(() => { void loadSpaces(); void loadCalendarImports(); }, [loadCalendarImports, loadSpaces]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state || googleCallbackHandledRef.current) return;
    googleCallbackHandledRef.current = true;
    window.history.replaceState({}, document.title, window.location.pathname);
    setView('settings');
    setSettingsPane('imports');
    setImportBusy(true);
    setImportError('');
    store.finishGoogleCalendarConnect(code, state)
      .then(({ calendars }) => {
        if (!mountedRef.current) return;
        setGoogleCalendarCandidates(calendars);
        setImportsLoaded(true);
        void loadCalendarImports();
      })
      .catch((e) => {
        if (!mountedRef.current) return;
        const message = e instanceof Error ? e.message : '';
        setImportError(
          message.includes('Google authorization expired')
            ? 'Google authorization expired. Please tap Connect Google Calendar and try again.'
            : message || 'Could not finish Google Calendar connection',
        );
      })
      .finally(() => {
        if (mountedRef.current) setImportBusy(false);
      });
  }, [loadCalendarImports, store]);

  useEffect(() => {
    mountedRef.current = true;
    const cached = store.cachedList();
    if (cached.length > 0) {
      setItems(normalizeItems(cached.map((item) => ({ ...item, reminder: null }))));
      setLoaded(true);
    }

    refreshData()
      .catch(() => undefined)
      .finally(() => {
        if (mountedRef.current) setLoaded(true);
      });

    const refreshOnWake = () => {
      if (document.visibilityState === 'visible') {
        void refreshData({ minIntervalMs: WAKE_REFRESH_MIN_INTERVAL_MS });
      }
    };
    const refreshOnOnline = () => {
      void refreshData({ minIntervalMs: WAKE_REFRESH_MIN_INTERVAL_MS });
    };
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        void refreshData({ minIntervalMs: AUTO_REFRESH_INTERVAL_MS - 1_000 });
      }
    }, AUTO_REFRESH_INTERVAL_MS);

    document.addEventListener('visibilitychange', refreshOnWake);
    window.addEventListener('focus', refreshOnWake);
    window.addEventListener('pageshow', refreshOnWake);
    window.addEventListener('online', refreshOnOnline);

    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshOnWake);
      window.removeEventListener('focus', refreshOnWake);
      window.removeEventListener('pageshow', refreshOnWake);
      window.removeEventListener('online', refreshOnOnline);
    };
  }, [store, refreshData]);

  useEffect(() => {
    if (alertsEnabled) {
      void updateTaskAppBadge(items, todayKey());
    } else {
      void clearTaskAppBadge();
    }
  }, [alertsEnabled, items]);

  // One-time migration: push each timed parent's time into its existing untimed nested child
  // tasks. Runs once per browser/origin (gated by localStorage), against synced server data, so
  // after it runs a child task left blank under a timed parent stays blank.
  const backfillInFlight = useRef(false);
  useEffect(() => {
    if (!loaded || !lastSyncedAt || backfillInFlight.current) return;
    try {
      if (localStorage.getItem(CHILD_TIME_BACKFILL_KEY)) return;
    } catch {
      // If storage is unavailable, run once for this session rather than skipping the migration.
    }
    const fixes = items
      .filter((i) => i.kind === 'task' && i.parentId && i.time == null)
      .map((i) => ({ id: i.id, time: inheritedTime(byId.current[i.parentId!]) }))
      .filter((f): f is { id: string; time: string } => f.time != null);
    backfillInFlight.current = true;
    (async () => {
      try {
        if (fixes.length > 0) {
          const results = await Promise.all(fixes.map((f) => store.update(f.id, { time: f.time })));
          const updated = new Map(results.map((r) => [r.id, r]));
          if (mountedRef.current) setItems((p) => p.map((i) => updated.get(i.id) ?? i));
        }
        try { localStorage.setItem(CHILD_TIME_BACKFILL_KEY, '1'); } catch { /* best effort */ }
      } finally {
        backfillInFlight.current = false;
      }
    })();
  }, [loaded, lastSyncedAt, items, store]);

  useEffect(() => {
    const updatePermission = () => {
      const permission = getNotificationPermission();
      setNotificationPermission(permission);
      getCurrentPushSubscription()
        .then((subscription) => {
          setPushSubscribed(Boolean(subscription));
          if (alertsEnabled && permission === 'granted' && subscription) {
            void enablePushNotifications(store).catch(() => undefined);
          }
        })
        .catch(() => setPushSubscribed(false));
    };
    window.addEventListener('focus', updatePermission);
    document.addEventListener('visibilitychange', updatePermission);
    updatePermission();
    return () => {
      window.removeEventListener('focus', updatePermission);
      document.removeEventListener('visibilitychange', updatePermission);
    };
  }, [alertsEnabled, store]);

  async function setAlertsPreference(enabled: boolean) {
    setPushError('');
    if (!enabled) {
      setPushBusy(true);
      try {
        await disablePushNotifications(store);
        setPushSubscribed(false);
      } catch (e) {
        setPushError((e as Error).message || 'Could not disable notifications on this device');
      } finally {
        setPushBusy(false);
      }
      setAlertsEnabled(false);
      saveAlertsEnabled(false);
      await clearTaskAppBadge();
      return;
    }

    setPushBusy(true);
    try {
      const status = await enablePushNotifications(store);
      const permission = getNotificationPermission();
      setNotificationPermission(permission);
      if (status !== 'subscribed') {
        const message = status === 'unsupported'
          ? 'Push notifications are not available in this browser. On iPhone, open WingMate from the Home Screen.'
          : status === 'unconfigured'
            ? 'Push notifications are not configured on the server yet.'
            : 'Notifications are blocked in iOS settings for this WingMate app.';
        setPushError(message);
        setAlertsEnabled(false);
        saveAlertsEnabled(false);
        return;
      }
      setPushSubscribed(true);
      setAlertsEnabled(true);
      saveAlertsEnabled(true);
      await updateTaskAppBadge(items, todayKey());
    } catch (e) {
      setPushError((e as Error).message || 'Could not enable notifications on this device');
      setAlertsEnabled(false);
      saveAlertsEnabled(false);
    } finally {
      setPushBusy(false);
    }
  }

  async function handleRefresh() {
    await refreshData({ showSpinner: true });
  }

  const byId = useRef<Record<string, Item>>({});
  const skipTitleSave = useRef(false);
  byId.current = {};
  items.forEach((i) => { byId.current[i.id] = i; });
  const tagById = useMemo(() => Object.fromEntries(tags.map((t) => [t.id, t])), [tags]);
  const spaceById = useMemo(() => Object.fromEntries(spaces.map((s) => [s.id, s])), [spaces]);

  useEffect(() => {
    if (!loaded || notificationRouteHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get('item') && !params.get('view')) return;
    notificationRouteHandledRef.current = true;
    openNotificationTarget(params);
  }, [loaded, items]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | undefined;
      if (data?.type !== 'WINGMATE_NOTIFICATION_CLICK' || !data.url) return;
      const url = new URL(data.url);
      openNotificationTarget(url.searchParams);
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [items]);

  function canManageSpaceId(spaceId: string | null | undefined): boolean {
    if (!spaceId) return true;
    const role = spaceById[spaceId]?.role;
    return role === 'owner' || role === 'admin';
  }

  function canEditItem(item: Item | undefined): boolean {
    if (!item) return false;
    if (item.externalSource) return false;
    if (!item.spaceId) return true;
    const myMember = spaceById[item.spaceId]?.members.find((m) => (m.email ?? '').toLowerCase() === email.toLowerCase());
    return canManageSpaceId(item.spaceId) || item.ownerId === myMember?.userId;
  }

  function canDeleteSubtree(item: Item | undefined): boolean {
    if (!item) return false;
    if (item.externalSource) return false;
    if (!item.spaceId) return true;
    if (canManageSpaceId(item.spaceId)) return true;
    const kill = new Set<string>();
    const collect = (id: string) => {
      kill.add(id);
      items.filter((i) => i.parentId === id).forEach((c) => collect(c.id));
    };
    collect(item.id);
    const myMember = spaceById[item.spaceId]?.members.find((m) => (m.email ?? '').toLowerCase() === email.toLowerCase());
    return items.every((i) => !kill.has(i.id) || i.ownerId === myMember?.userId);
  }

  function isListItem(item: Item | undefined): boolean {
    if (!item) return false;
    return Boolean(item.listId || (item.parentId && byId.current[item.parentId]?.kind === 'list'));
  }

  function toggleHideCheckedForList(listId: string) {
    setHiddenCheckedLists((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) next.delete(listId); else next.add(listId);
      saveHiddenCheckedLists(next);
      return next;
    });
  }

  async function setListHidden(list: Item, hidden: boolean) {
    if (!canEditItem(list) || list.kind !== 'list') return;
    const updated = await store.update(list.id, { hidden });
    setItems((p) => p.map((i) => (i.id === list.id ? updated : i)));
  }

  async function uncheckListItems(listId: string) {
    const kids = listItemsOf(listId).filter((kid) => kid.done && canEditItem(kid));
    const updatedKids = await Promise.all(kids.map((kid) => store.update(kid.id, { done: false })));
    const updatedById = new Map(updatedKids.map((kid) => [kid.id, kid]));
    setItems((p) => p.map((i) => updatedById.get(i.id) ?? i));
  }

  function fmtCompletionDateTime(v: number): string {
    return new Date(v).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  function timestampToLocalInput(v: number): string {
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 16);
  }

  function formatPrice(amountCents: number): string {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(amountCents / 100);
  }

  function centsToInput(amountCents: number): string {
    return (amountCents / 100).toFixed(2);
  }

  function parsePriceCents(value: string): number | null {
    const cleaned = value.replace(/[$,\s]/g, '');
    if (!cleaned) return null;
    const amount = Number(cleaned);
    if (!Number.isFinite(amount) || amount < 0) return null;
    return Math.round(amount * 100);
  }

  async function saveCompletionHistory(item: Item, history: Item['completionHistory']) {
    const sorted = [...history].sort((a, b) => b.at - a.at);
    const updated = await store.update(item.id, {
      completionHistory: sorted,
      completedCount: sorted.length,
      lastCompletedAt: sorted[0]?.at ?? null,
    });
    setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
  }

  async function editCompletionHistoryAt(item: Item, index: number, value: string) {
    const at = new Date(value).getTime();
    if (!Number.isFinite(at)) return;
    const history = [...(item.completionHistory ?? [])];
    if (!history[index]) return;
    history[index] = { ...history[index], at };
    await saveCompletionHistory(item, history);
  }

  async function removeCompletionHistoryAt(item: Item, index: number) {
    const history = [...(item.completionHistory ?? [])];
    if (!history[index]) return;
    history.splice(index, 1);
    await saveCompletionHistory(item, history);
  }

  async function savePriceHistory(item: Item, history: Item['priceHistory']) {
    const sorted = [...history].sort((a, b) => b.at - a.at);
    const updated = await store.update(item.id, { priceHistory: sorted });
    setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
  }

  async function addPriceHistory(item: Item) {
    const amountCents = parsePriceCents(priceDraft);
    if (amountCents === null) return;
    const at = priceDateDraft ? new Date(priceDateDraft).getTime() : Date.now();
    if (!Number.isFinite(at)) return;
    await savePriceHistory(item, [
      { at, amountCents },
      ...(item.priceHistory ?? []),
    ]);
    setPriceDraft('');
    setPriceDateDraft(timestampToLocalInput(Date.now()));
    setPriceHistoryOpen(true);
    setPriceAddOpen(false);
  }

  async function editPriceHistoryDateAt(item: Item, index: number, value: string) {
    const at = new Date(value).getTime();
    if (!Number.isFinite(at)) return;
    const history = [...(item.priceHistory ?? [])];
    if (!history[index]) return;
    history[index] = { ...history[index], at };
    await savePriceHistory(item, history);
  }

  async function editPriceHistoryAmountAt(item: Item, index: number, value: string) {
    const amountCents = parsePriceCents(value);
    if (amountCents === null) return;
    const history = [...(item.priceHistory ?? [])];
    if (!history[index]) return;
    history[index] = { ...history[index], amountCents };
    await savePriceHistory(item, history);
  }

  async function removePriceHistoryAt(item: Item, index: number) {
    const history = [...(item.priceHistory ?? [])];
    if (!history[index]) return;
    history.splice(index, 1);
    await savePriceHistory(item, history);
  }

  useEffect(() => {
    if (!historyItemId) return;
    setPriceDraft('');
    setPriceDateDraft(timestampToLocalInput(Date.now()));
    setCheckoffHistoryOpen(false);
    setPriceHistoryOpen(false);
    setPriceAddOpen(false);
  }, [historyItemId]);

  const childrenOf = useCallback(
    (id: string | null) => items.filter((i) => i.parentId === id).sort((a, b) => a.createdAt - b.createdAt),
    [items],
  );

  async function addTop() {
    const t = draft.trim();
    if (!t) return;
    const canRepeat = draftKind === 'event' || draftKind === 'task';
    const item = await store.create({ kind: draftKind, title: t, parentId: null, date: selKey, time: (draftKind === 'event' || draftKind === 'task') ? draftTime || null : null, endTime: draftKind === 'event' ? draftEndTime || null : null, recurrence: canRepeat ? draftRecurrence || null : null, spaceId: draftSpaceId });
    setItems((p) => [...p, item]);
    setDraft('');
    setDraftTime('');
    setDraftEndTime('');
    setDraftTimeOpen(false);
    setDraftRecurrence('');
  }

  // Nearest time on this item or any ancestor (used so nested children share their parent's time).
  function inheritedTime(item: Item | undefined): string | null {
    let cur = item;
    while (cur) {
      if (cur.time) return cur.time;
      cur = cur.parentId ? byId.current[cur.parentId] : undefined;
    }
    return null;
  }

  function allOccurrenceDates(root: Item): string[] {
    return root.date ? [root.date, ...recurringDates(root)] : [];
  }

  function occurrenceHidden(item: Item, occurrenceKey?: string | null): boolean {
    return Boolean(occurrenceKey && item.occurrenceOverrides?.[occurrenceKey]?.hidden);
  }

  function visibleChildrenOf(parentId: string, occurrenceKey?: string | null): Item[] {
    return childrenOf(parentId).filter((child) => !occurrenceHidden(child, occurrenceKey));
  }

  function occurrenceVisibilityOverrides(root: Item, occurrenceKey: string, scope: OccurrenceScopeChoice): Item['occurrenceOverrides'] {
    const hiddenDates = allOccurrenceDates(root).filter((dateKey) => {
      if (scope === 'occurrence') return dateKey !== occurrenceKey;
      if (scope === 'future') return dateKey < occurrenceKey;
      return false;
    });
    return Object.fromEntries(hiddenDates.map((dateKey) => [dateKey, { hidden: true }]));
  }

  function relativeDueDate(anchorDate: string | null | undefined, offsetDays: number | null): string | null {
    if (!anchorDate || offsetDays === null) return null;
    return addDaysToKey(anchorDate, -offsetDays);
  }

  function nearestAnchorDate(item: Item | undefined, occurrenceKey?: string | null): string | null {
    if (occurrenceKey) return occurrenceKey;
    let cur = item;
    while (cur) {
      if (cur.date) return cur.date;
      cur = cur.parentId ? byId.current[cur.parentId] : undefined;
    }
    return null;
  }

  function dueOffsetLabel(offsetDays: number): string {
    if (offsetDays === 0) return 'Event day';
    if (offsetDays === 1) return '1 day before';
    if (offsetDays === 7) return '1 week before';
    return `${offsetDays} days before`;
  }

  async function addChild(parentId: string, occurrenceKey?: string | null) {
    const t = childDraft.trim();
    if (!t) return;
    const parent = byId.current[parentId];
    const root = recurringOccurrenceRoot(parent, occurrenceKey);
    let occurrenceOverrides: Item['occurrenceOverrides'] | undefined;
    if (root && occurrenceKey) {
      const scope = await chooseOccurrenceScope(`Add "${t}"`, occurrenceKey, 'future');
      if (scope === 'cancel') return;
      occurrenceOverrides = occurrenceVisibilityOverrides(root, occurrenceKey, scope);
    }
    const canTime = childKind === 'event' || childKind === 'task';
    const time = canTime ? (childTime || inheritedTime(parent) || null) : null;
    const dueOffsetDays = childKind === 'task' && childDueMode !== '' && childDueMode !== 'custom' ? Number(childDueMode) : null;
    const taskDate = dueOffsetDays !== null
      ? relativeDueDate(nearestAnchorDate(parent, occurrenceKey), dueOffsetDays)
      : childDate || null;
    const created = await store.create({ kind: childKind, title: t, parentId, date: childKind === 'task' ? taskDate : null, dueOffsetDays, time, endTime: childKind === 'event' ? childEndTime || null : null, recurrence: childKind === 'event' ? childRecurrence || null : null, spaceId: parent?.spaceId ?? null });
    const item = occurrenceOverrides && Object.keys(occurrenceOverrides).length > 0
      ? await store.update(created.id, { occurrenceOverrides })
      : created;
    setItems((p) => [...p, item]);
    setChildDraft('');
    setChildTime('');
    setChildEndTime('');
    setChildDate('');
    setChildDueMode('');
    setChildTimeOpen(false);
    setChildRecurrence('');
    setAddingTo(null);
  }

  function recurringOccurrenceRoot(item: Item | undefined, occurrenceKey?: string | null): Item | null {
    if (!item || !occurrenceKey) return null;
    const root = chainUp(item)[0];
    if (!root?.recurrence || !root.date) return null;
    if (root.date === occurrenceKey || recurringDates(root).includes(occurrenceKey)) return root;
    return null;
  }

  function effectiveItem(item: Item, occurrenceKey?: string | null): Item {
    const root = recurringOccurrenceRoot(item, occurrenceKey);
    if (!root) return item;
    const override = item.occurrenceOverrides?.[occurrenceKey!] ?? null;
    return override ? { ...item, ...override } : item;
  }

  function scheduleDateForItem(item: Item, occurrenceKey?: string | null): string | null {
    if (item.kind === 'task' && item.date) return item.date;
    if (occurrenceKey) return occurrenceKey;
    if (item.date) return item.date;
    return chainUp(item)[0]?.date ?? null;
  }

  function reminderPresetForItem(item: Item, occurrenceKey?: string | null): string {
    if (!item.reminder) return 'off';
    const scheduleDate = scheduleDateForItem(item, occurrenceKey);
    for (const preset of REMINDER_PRESETS) {
      if (preset.offsetMinutes === null) continue;
      if (scheduledDateTimeValue(scheduleDate, item.time, preset.offsetMinutes) === item.reminder) return preset.value;
    }
    return 'custom';
  }

  function chooseOccurrenceScope(action: string, occurrenceKey: string, mode: OccurrencePromptMode = 'series'): Promise<OccurrenceScopeChoice> {
    return new Promise((resolve) => {
      setOccurrencePrompt({ action, occurrenceKey, mode, resolve });
    });
  }

  function resolveOccurrencePrompt(choice: OccurrenceScopeChoice) {
    occurrencePrompt?.resolve(choice);
    setOccurrencePrompt(null);
  }

  async function updateOccurrenceItem(id: string, occurrenceKey: string, patch: OccurrencePatch): Promise<Item> {
    const item = byId.current[id];
    if (!item) throw new Error(`Item ${id} not found`);
    const occurrenceOverrides = {
      ...(item.occurrenceOverrides ?? {}),
      [occurrenceKey]: {
        ...(item.occurrenceOverrides?.[occurrenceKey] ?? {}),
        ...patch,
      },
    };
    const updated = await store.update(id, { occurrenceOverrides });
    const withOverrides = { ...updated, occurrenceOverrides };
    setItems((p) => p.map((i) => (i.id === id ? withOverrides : i)));
    return withOverrides;
  }

  async function updateItemForScope(id: string, patch: OccurrencePatch, action: string, occurrenceKey?: string | null): Promise<Item> {
    const item = byId.current[id];
    if (!item) throw new Error(`Item ${id} not found`);
    const root = recurringOccurrenceRoot(item, occurrenceKey);
    if (root && occurrenceKey) {
      const scope = await chooseOccurrenceScope(action, occurrenceKey);
      if (scope === 'cancel') return item;
      if (scope === 'occurrence') return updateOccurrenceItem(id, occurrenceKey, patch);
    }
    const updated = await store.update(id, patch);
    setItems((p) => p.map((i) => (i.id === id ? updated : i)));
    return updated;
  }

  async function cloneAsSingleOccurrence(id: string, occurrenceKey: string, patch: ItemPatch = {}): Promise<Item[]> {
    const source = byId.current[id];
    if (!source) throw new Error(`Item ${id} not found`);
    const cloned: Item[] = [];
    const cloneOne = async (item: Item, parentId: string | null): Promise<Item> => {
      const current = effectiveItem(item, occurrenceKey);
      const nextDate = patch.date !== undefined && item.id === id
        ? patch.date
        : item.id === id
          ? occurrenceKey
          : item.kind === 'task' && item.dueOffsetDays !== null && item.dueOffsetDays !== undefined
            ? relativeDueDate((patch.date as string | null | undefined) ?? occurrenceKey, item.dueOffsetDays)
            : current.date;
      let created = await store.create({
        kind: (item.id === id ? patch.kind : undefined) ?? current.kind,
        title: (item.id === id ? patch.title : undefined) ?? current.title,
        parentId,
        date: nextDate ?? null,
        dueOffsetDays: current.dueOffsetDays ?? null,
        time: (item.id === id ? patch.time : undefined) ?? current.time ?? null,
        endTime: current.kind === 'event' ? ((item.id === id ? patch.endTime : undefined) ?? current.endTime ?? null) : null,
        endDate: current.kind === 'event' ? ((item.id === id ? patch.endDate : undefined) ?? current.endDate ?? null) : null,
        recurrence: null,
        recurrenceEnd: null,
        listId: current.listId ?? null,
        location: (item.id === id ? patch.location : undefined) ?? current.location ?? null,
        important: (item.id === id ? patch.important : undefined) ?? current.important,
        needsAttention: current.kind === 'task' ? ((item.id === id ? patch.needsAttention : undefined) ?? current.needsAttention) : false,
        spaceId: current.spaceId ?? null,
      });
      const extraPatch: ItemPatch = {
        tagIds: (item.id === id ? patch.tagIds : undefined) ?? current.tagIds ?? [],
        done: (item.id === id ? patch.done : undefined) ?? current.done,
        reminder: (item.id === id ? patch.reminder : undefined) ?? current.reminder ?? null,
      };
      created = await store.update(created.id, extraPatch);
      cloned.push(created);
      for (const child of childrenOf(item.id)) {
        if (!occurrenceHidden(child, occurrenceKey)) await cloneOne(child, created.id);
      }
      return created;
    };
    await cloneOne(source, null);
    return cloned;
  }

  async function materializeOccurrenceEdit(id: string, occurrenceKey: string, patch: ItemPatch): Promise<Item[]> {
    await updateOccurrenceItem(id, occurrenceKey, { hidden: true });
    const cloned = await cloneAsSingleOccurrence(id, occurrenceKey, patch);
    setItems((p) => [...p, ...cloned]);
    return cloned;
  }

  async function toggleImportant(id: string, occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (!item || item.externalSource) return;
    const current = effectiveItem(item, occurrenceKey);
    await updateItemForScope(id, { important: !current.important }, current.important ? 'Remove flag' : 'Flag this item', occurrenceKey);
  }

  async function toggleNeedsAttention(id: string, occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (!canEditItem(item) || item.kind !== 'task') return;
    const current = effectiveItem(item, occurrenceKey);
    await updateItemForScope(id, { needsAttention: !current.needsAttention }, current.needsAttention ? 'Remove Needs Attention' : 'Mark Needs Attention', occurrenceKey);
  }

  async function toggleDone(id: string, occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (!canEditItem(item)) return;
    const current = effectiveItem(item, occurrenceKey);
    const nowDone = !current.done;
    const patch: OccurrencePatch = nowDone && current.needsAttention ? { done: true, needsAttention: false } : { done: nowDone };
    if (nowDone && isListItem(item)) {
      const completedAt = Date.now();
      patch.lastCompletedAt = completedAt;
      patch.completedCount = (item.completedCount ?? 0) + 1;
      patch.completionHistory = [
        { at: completedAt, by: email || null },
        ...(item.completionHistory ?? []),
      ].slice(0, 50);
    }
    await updateItemForScope(id, patch, nowDone ? 'Mark done' : 'Mark not done', occurrenceKey);
  }

  async function setReminder(id: string, v: string, _occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (!item || item.externalSource) return;
    const reminder = v || null;
    await store.setDeviceReminder(deviceId, id, reminder);
    setItems((p) => p.map((i) => (i.id === id ? { ...i, reminder } : i)));
  }

  async function setReminderPreset(id: string, presetValue: string, occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (!item || item.externalSource) return;
    if (presetValue === 'off') {
      setRemCustomFor(null);
      await setReminder(id, '', occurrenceKey);
      return;
    }
    if (presetValue === 'custom') {
      setRemCustomFor(id);
      return;
    }
    setRemCustomFor(null);
    const offsetMinutes = Number(presetValue);
    const current = effectiveItem(item, occurrenceKey);
    const reminder = scheduledDateTimeValue(scheduleDateForItem(current, occurrenceKey), current.time, offsetMinutes);
    if (!reminder) return;
    await setReminder(id, reminder, occurrenceKey);
  }

  async function setEventTime(id: string, v: string, occurrenceKey?: string | null) {
    if (!canEditItem(byId.current[id])) return;
    const item = byId.current[id];
    if (recurringOccurrenceRoot(item, occurrenceKey) && occurrenceKey) {
      const scope = await chooseOccurrenceScope('Change time', occurrenceKey);
      if (scope === 'cancel') return;
      if (scope === 'occurrence') {
        await updateOccurrenceItem(id, occurrenceKey, { time: v || null });
        return;
      }
    }
    const prev = byId.current[id]?.time ?? null;
    const next = v || null;
    const updated = await store.update(id, { time: next });
    // Cascade to descendants that were inheriting this time (no time of their own,
    // or sharing the parent's previous time) so nested children stay in sync.
    const synced = new Set<string>();
    const collect = (parent: string) => {
      for (const c of items) {
        if (c.parentId !== parent) continue;
        const inheriting = c.time === null || c.time === prev;
        // A child with its own distinct time owns its subtree's time — don't touch it or recurse in.
        if (!inheriting) continue;
        if (c.kind === 'event' || c.kind === 'task') synced.add(c.id);
        collect(c.id);
      }
    };
    collect(id);
    const results = await Promise.all([...synced].map((cid) => store.update(cid, { time: next })));
    const byUpdated = new Map(results.map((r) => [r.id, r]));
    setItems((p) => p.map((i) => (i.id === id ? updated : byUpdated.get(i.id) ?? i)));
  }

  async function setEventEndTime(id: string, v: string, occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (!canEditItem(item) || item.kind !== 'event') return;
    await updateItemForScope(id, { endTime: v || null, endDate: null }, 'Change end time', occurrenceKey);
  }

  async function setItemTimeFromPicker(id: string, value: string, endValue: string | undefined, occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (!canEditItem(item)) return;
    const patch: OccurrencePatch = item.kind === 'event'
      ? { time: value || null, endTime: endValue || null, endDate: null }
      : { time: value || null };
    if (recurringOccurrenceRoot(item, occurrenceKey) && occurrenceKey) {
      const scope = await chooseOccurrenceScope('Change time', occurrenceKey);
      if (scope === 'cancel') return;
      if (scope === 'occurrence') {
        await updateOccurrenceItem(id, occurrenceKey, patch);
        return;
      }
    }
    await setEventTime(id, value, null);
    if (item.kind === 'event') await setEventEndTime(id, endValue ?? '', null);
  }

  async function saveTitle(id: string, occurrenceKey?: string | null) {
    if (skipTitleSave.current) {
      skipTitleSave.current = false;
      return;
    }
    const item = byId.current[id];
    const title = titleDraft.trim();
    setTitleEditing(null);
    if (!canEditItem(item)) return;
    if (!item) return;
    const current = effectiveItem(item, occurrenceKey);
    if (!title || title === current.title) return;
    await updateItemForScope(id, { title }, 'Rename this item', occurrenceKey);
  }

  async function remove(id: string, occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (!canDeleteSubtree(item)) return;
    const root = recurringOccurrenceRoot(item, occurrenceKey);
    if (root && occurrenceKey) {
      const scope = await chooseOccurrenceScope(`Delete this ${item.kind === 'task' ? 'task' : item.kind === 'event' ? 'event' : 'item'}`, occurrenceKey);
      if (scope === 'cancel') return;
      if (scope === 'occurrence') {
        await updateOccurrenceItem(id, occurrenceKey, { hidden: true });
        return;
      }
    }
    const before = items;
    await store.remove(id);
    const kill = new Set<string>();
    const collect = (x: string) => {
      kill.add(x);
      items.filter((i) => i.parentId === x).forEach((c) => collect(c.id));
    };
    collect(id);
    setItems((p) => p.filter((i) => !kill.has(i.id)));
    try {
      await refreshData({ minIntervalMs: 0 });
    } catch {
      setItems(before);
    }
  }

  async function archiveListItem(item: Item) {
    if (!canEditItem(item) || !isListItem(item)) return;
    const updated = await store.update(item.id, { hidden: true });
    setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
  }

  async function createList(title: string) {
    const t = title.trim();
    if (!t) return;
    const item = await store.create({ kind: 'list', title: t, parentId: null, date: null });
    setItems((p) => [...p, item]);
    setNewListDraft('');
    setView('lists');
  }

  // Materialize a manual drag order: assigns sequential sortOrder to every id
  // in `orderedIds` and persists only the ones that actually moved.
  async function persistOrder(orderedIds: string[]) {
    const changed: Record<string, number> = {};
    orderedIds.forEach((id, idx) => {
      if (byId.current[id]?.sortOrder !== idx) changed[id] = idx;
    });
    if (Object.keys(changed).length === 0) return;
    setItems((p) => p.map((i) => (i.id in changed ? { ...i, sortOrder: changed[i.id] } : i)));
    await Promise.all(Object.entries(changed).map(([id, sortOrder]) => store.update(id, { sortOrder })));
  }

  // Reordering list items only drags the currently-visible rows (hidden
  // "checked" ones may be filtered out); this splices the new visible order
  // back into the full list so hidden items keep their relative position.
  async function persistListItemOrder(listId: string, newVisibleOrder: string[]) {
    const kids = listItemsOf(listId);
    const visibleSet = new Set(newVisibleOrder);
    let vi = 0;
    const merged = kids.map((k) => (visibleSet.has(k.id) ? newVisibleOrder[vi++] : k.id));
    await persistOrder(merged);
  }

  async function addNoDateTask() {
    const title = taskDraft.trim();
    if (!title) return;
    const item = await store.create({ kind: 'task', title, parentId: null, date: null, needsAttention: taskDraftImportant, important: taskDraftFlagged });
    setItems((p) => [...p, item]);
    setTaskDraft('');
    setTaskDraftImportant(false);
    setTaskDraftFlagged(false);
  }

  async function addStickyNote(text: string) {
    const t = text.trim();
    if (!t) return;
    const item = await store.create({ kind: 'note', title: t, parentId: null, date: null });
    setItems((p) => [...p, item]);
    setStickyDraft('');
  }

  async function saveStickyNote(id: string, title: string) {
    const t = title.trim();
    setStickyEditing(null);
    if (!t || t === byId.current[id]?.title) return;
    const updated = await store.update(id, { title: t });
    setItems((p) => p.map((i) => (i.id === id ? updated : i)));
  }

  function deleteStickyNote(note: Item) {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndoNote(note);
    undoTimer.current = setTimeout(async () => {
      await store.remove(note.id);
      setItems((p) => p.filter((i) => i.id !== note.id));
      setUndoNote(null);
    }, 4500);
  }

  function undoDeleteStickyNote() {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndoNote(null);
  }

  async function addItemToList(listId: string, itemId: string) {
    const updated = await store.update(itemId, { listId });
    setItems((p) => p.map((i) => (i.id === itemId ? updated : i)));
  }

  async function changeItemCalendar(item: Item, spaceId: string | null) {
    if (item.externalSource) return;
    const updated = await store.update(item.id, { spaceId });
    const moveIds = new Set<string>();
    const collect = (id: string) => {
      moveIds.add(id);
      items.filter((i) => i.parentId === id).forEach((c) => collect(c.id));
    };
    collect(item.id);
    setItems((p) => p.map((i) => (
      i.id === item.id
        ? updated
        : moveIds.has(i.id)
          ? { ...i, spaceId, updatedAt: updated.updatedAt }
          : i
    )));
  }

  // One-off quick add on the checkbox (shop) view. Each Enter adds a row and
  // keeps the input focused; pasting several lines adds them all at once.
  function archivedListItemsOf(listId: string) {
    return items
      .filter((i) => i.hidden && i.kind === 'task' && (i.listId === listId || (i.parentId === listId && !i.date)))
      .sort((a, b) => (b.lastCompletedAt ?? 0) - (a.lastCompletedAt ?? 0) || a.title.localeCompare(b.title));
  }

  function allArchivedListItems() {
    return items
      .filter((i) => i.hidden && i.kind === 'task' && (i.listId || (i.parentId && byId.current[i.parentId]?.kind === 'list')))
      .sort((a, b) => (b.lastCompletedAt ?? 0) - (a.lastCompletedAt ?? 0) || a.title.localeCompare(b.title));
  }

  function listLibrarySuggestions(listId: string, query: string) {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return archivedListItemsOf(listId)
      .filter((i) => i.title.toLowerCase().includes(q))
      .sort((a, b) => {
        const at = a.title.toLowerCase().startsWith(q) ? 0 : 1;
        const bt = b.title.toLowerCase().startsWith(q) ? 0 : 1;
        return at - bt || (b.lastCompletedAt ?? 0) - (a.lastCompletedAt ?? 0) || a.title.localeCompare(b.title);
      })
      .slice(0, 5);
  }

  async function restoreListItem(item: Item, listId: string) {
    if (!canEditItem(item)) return;
    const updated = await store.update(item.id, {
      hidden: false,
      done: false,
      listId,
      parentId: null,
      date: null,
    });
    setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
    setQuickAddDrafts((d) => ({ ...d, [listId]: '' }));
  }

  async function permanentlyRemoveLibraryItem(item: Item) {
    if (!canDeleteSubtree(item) || !item.hidden || !isListItem(item)) return;
    await remove(item.id);
    if (historyItemId === item.id) setHistoryItemId(null);
    setLibraryRemoveItemId(null);
  }

  async function addQuickItem(listId: string) {
    const lines = (quickAddDrafts[listId] ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    const created: Item[] = [];
    const restored: Item[] = [];
    const archived = archivedListItemsOf(listId);
    for (const title of lines) {
      const reusable = archived.find((item) => item.title.trim().toLowerCase() === title.toLowerCase() && !restored.some((r) => r.id === item.id));
      if (reusable && canEditItem(reusable)) {
        restored.push(await store.update(reusable.id, { hidden: false, done: false, listId, parentId: null, date: null }));
      } else {
        created.push(await store.create({ kind: 'task', title, parentId: null, date: null, listId }));
      }
    }
    const restoredById = new Map(restored.map((item) => [item.id, item]));
    setItems((p) => [...p.map((i) => restoredById.get(i.id) ?? i), ...created]);
    setQuickAddDrafts((d) => ({ ...d, [listId]: '' }));
  }

  function openListEditor(list: Item) {
    setEditingListId(list.id);
    setListText(listItemsOf(list.id).map((k) => k.title).join('\n'));
  }

  function cancelListEditor() {
    setEditingListId(null);
    setListText('');
  }

  // Commit the edited text back to items, preserving done/important state.
  // Pass 1 keeps lines whose title still exactly matches an existing item.
  // Leftover lines and items are paired positionally (a rename keeps state);
  // any extra lines become new items, any extra items are deleted.
  async function saveListEditor(listId: string) {
    const lines = listText.split('\n').map((l) => l.trim()).filter(Boolean);
    const kids = listItemsOf(listId);

    const remaining = [...kids];
    const unmatched: string[] = [];
    for (const line of lines) {
      const idx = remaining.findIndex((k) => k.title === line);
      if (idx >= 0) remaining.splice(idx, 1);
      else unmatched.push(line);
    }

    const creates: string[] = [];
    const updates: { id: string; title: string }[] = [];
    const deletes: string[] = [];
    let ri = 0;
    for (const line of unmatched) {
      while (ri < remaining.length && !canEditItem(remaining[ri])) ri++;
      if (ri < remaining.length) updates.push({ id: remaining[ri++].id, title: line });
      else creates.push(line);
    }
    for (let j = ri; j < remaining.length; j++) {
      if (canEditItem(remaining[j])) deletes.push(remaining[j].id);
    }

    const updatedMap = new Map<string, Item>();
    for (const u of updates) updatedMap.set(u.id, await store.update(u.id, { title: u.title }));
    const archived: Item[] = [];
    for (const id of deletes) {
      const item = byId.current[id];
      if (item) archived.push(await store.update(id, { hidden: true }));
    }
    const created: Item[] = [];
    for (const title of creates) {
      created.push(await store.create({ kind: 'task', title, parentId: null, date: null, listId }));
    }
    const archivedById = new Map(archived.map((item) => [item.id, item]));
    setItems((p) => [
      ...p.map((i) => archivedById.get(i.id) ?? updatedMap.get(i.id) ?? i),
      ...created,
    ]);
    setEditingListId(null);
    setListText('');
  }

  function readFileAsBase64(file: File): Promise<{ data: string; mediaType: string; name: string; previewUrl?: string }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const data = result.split(',')[1];
        const previewUrl = file.type.startsWith('image/') ? result : undefined;
        resolve({ data, mediaType: file.type || 'application/octet-stream', name: file.name, previewUrl });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function handleQaFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    readFileAsBase64(file).then(setQaAttachment);
    e.target.value = '';
  }

  function handleQaPaste(e: React.ClipboardEvent) {
    const imageItem = Array.from(e.clipboardData.items).find((item) => item.type.startsWith('image/'));
    if (!imageItem) return;
    const file = imageItem.getAsFile();
    if (!file) return;
    e.preventDefault();
    readFileAsBase64(file).then((att) => setQaAttachment({ ...att, name: 'pasted-image' }));
  }

  async function handleQaParse() {
    dictation.stop();
    const t = qaText.trim();
    if (!t && !qaAttachment) return;
    setQaLoading(true);
    setQaError('');
    setQaPreview(null);
    try {
      const token = await getToken();
      const windowStart = new Date(); windowStart.setDate(windowStart.getDate() - 14);
      const windowEnd = new Date(); windowEnd.setDate(windowEnd.getDate() + 84);
      const winS = windowStart.toISOString().split('T')[0];
      const winE = windowEnd.toISOString().split('T')[0];
      const existingEvents = items
        .filter((i) => i.kind === 'event' && (i.recurrence || (i.date && i.date >= winS && i.date <= winE)))
        .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))
        .slice(0, 50)
        .map((i) => ({ id: i.id, title: i.title, date: i.date, recurrence: i.recurrence, time: i.time, endTime: i.endTime, endDate: i.endDate, location: i.location }));
      const existingTags = tags.map((t) => ({ id: t.id, name: t.name }));
      const existingLists = items
        .filter((i) => i.kind === 'list' && !i.parentId)
        .map((i) => ({ id: i.id, title: i.title }));
      const body: Record<string, unknown> = { text: t, existingEvents, existingTags, existingLists };
      if (qaAttachment) body.attachment = { data: qaAttachment.data, mediaType: qaAttachment.mediaType, name: qaAttachment.name };
      const res = await fetch(`${import.meta.env.VITE_API_URL}ai/parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json() as QaItem[];
      setQaPreview(data);
      setQaKindOverrides({});
    } catch (e) {
      setQaError(e instanceof Error ? e.message : 'Something went wrong. Try rephrasing your input.');
    } finally {
      setQaLoading(false);
    }
  }

  async function handleQaConfirm() {
    if (!qaPreview) return;
    setQaLoading(true);
    setQaError('');
    const snapshot = qaPreview;
    // Resolve list names to ids for this batch; created lists are reused across items.
    const listIdByName = new Map<string, string>();
    for (const i of items) if (i.kind === 'list' && !i.parentId) listIdByName.set(i.title.trim().toLowerCase(), i.id);
    try {
      for (const [idx, parsed] of snapshot.entries()) {
        const effectiveKind = (qaKindOverrides[idx] ?? parsed.kind) as 'event' | 'task' | 'note';
        const listName = parsed.action !== 'update' ? parsed.listName?.trim() : undefined;
        if (listName) {
          const key = listName.toLowerCase();
          let listId = parsed.listId ?? listIdByName.get(key) ?? null;
          if (!listId) {
            const newList = await store.create({ kind: 'list', title: listName, parentId: null, date: null });
            listId = newList.id;
            listIdByName.set(key, listId);
          }
          const created = await store.create({ kind: 'task', title: parsed.title, parentId: null, date: null, listId });
          if (parsed.tagIds?.length || parsed.important || parsed.needsAttention) await store.update(created.id, { tagIds: parsed.tagIds ?? [], important: !!parsed.important, needsAttention: !!parsed.needsAttention });
          continue;
        }
        if (parsed.action === 'update' && parsed.id) {
          const patch: Record<string, unknown> = {};
          if (parsed.title !== undefined) patch.title = parsed.title;
          if (parsed.date !== undefined) patch.date = parsed.date;
          if (parsed.time !== undefined) patch.time = parsed.time;
          if (parsed.endTime !== undefined) patch.endTime = parsed.endTime;
          if (parsed.endDate !== undefined) patch.endDate = parsed.endDate;
          if (parsed.location !== undefined) patch.location = parsed.location;
          if (parsed.recurrence !== undefined) patch.recurrence = parsed.recurrence;
          if (parsed.tagIds?.length) patch.tagIds = parsed.tagIds;
          if (parsed.important !== undefined) patch.important = parsed.important;
          if (parsed.needsAttention !== undefined) patch.needsAttention = parsed.needsAttention;
          const updated = await store.update(parsed.id, patch as Parameters<typeof store.update>[1]);
          setItems((p) => p.map((i) => (i.id === parsed.id ? updated : i)));
        } else {
          let created = await store.create({
            kind: effectiveKind,
            title: parsed.title,
            parentId: parsed.parentId ?? null,
            date: parsed.date ?? null,
            time: parsed.time ?? null,
            endTime: effectiveKind === 'event' ? parsed.endTime ?? null : null,
            endDate: effectiveKind === 'event' ? parsed.endDate ?? null : null,
            location: parsed.location ?? null,
            recurrence: parsed.recurrence ?? null,
          });
          if (parsed.tagIds?.length || parsed.important || parsed.needsAttention) {
            created = await store.update(created.id, { tagIds: parsed.tagIds ?? [], important: !!parsed.important, needsAttention: !!parsed.needsAttention });
          }
          setItems((p) => [...p, created]);
        }
      }
      const data = await store.list();
      setItems(normalizeItems(await applyDeviceReminders(data)));
      setQaText('');
      setQaAttachment(null);
      setQaPreview(null);

      const updates = snapshot.filter(p => p.action === 'update');
      const listAdds = snapshot.filter(p => p.action !== 'update' && p.listName?.trim());
      const creates = snapshot.filter(p => p.action !== 'update' && !p.listName?.trim());
      const parts: string[] = [];
      if (updates.length) {
        parts.push(updates.length === 1
          ? `"${updates[0].title || 'Event'}" updated${updates[0].changes ? ` — ${updates[0].changes}` : ''}`
          : `${updates.length} events updated`);
      }
      if (listAdds.length) {
        const listName = listAdds[0].listName?.trim();
        const sameList = listAdds.every(p => p.listName?.trim().toLowerCase() === listName?.toLowerCase());
        parts.push(listAdds.length === 1
          ? `"${listAdds[0].title}" added to ${listName}`
          : sameList
            ? `${listAdds.length} items added to ${listName}`
            : `${listAdds.length} items added to lists`);
      }
      if (creates.length) {
        parts.push(creates.length === 1
          ? `${creates[0].kind === 'event' ? 'Event' : creates[0].kind === 'task' ? 'Task' : 'Note'} "${creates[0].title}" added`
          : `${creates.length} items added`);
      }
      const msg = parts.join(' · ');
      setQaSuccess(msg);
      if (qaSuccessTimer.current) clearTimeout(qaSuccessTimer.current);
      qaSuccessTimer.current = setTimeout(() => setQaSuccess(''), 4000);
    } catch (e) {
      setQaError(e instanceof Error ? e.message : 'Something went wrong saving items. Please try again.');
    } finally {
      setQaLoading(false);
    }
  }

  function toggleCollapse(id: string) {
    setCollapsed((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function expandIds(ids: string[]) {
    setCollapsed((prev) => { const n = new Set(prev); ids.forEach((i) => n.delete(i)); return n; });
  }

  function openAdd(id: string) {
    if (addingTo === id) {
      setAddingTo(null);
      setChildDraft('');
      setChildTime('');
      setChildEndTime('');
      setChildDate('');
      setChildDueMode('');
      setChildTimeOpen(false);
      return;
    }
    expandIds([id]);
    setAddingTo(id);
    setChildKind('task');
    setChildDraft('');
    // Pre-fill the parent's time so a nested child task adopts it by default (blank if the parent has none).
    setChildTime(inheritedTime(byId.current[id]) ?? '');
    setChildEndTime('');
    setChildDate('');
    setChildDueMode('');
    setChildTimeOpen(false);
  }

  function chainUp(item: Item): Item[] {
    const c: Item[] = [];
    let cur: Item | undefined = item;
    while (cur) { c.unshift(cur); cur = cur.parentId ? byId.current[cur.parentId] : undefined; }
    return c;
  }

  function rootDateOf(item: Item): string | null {
    if (item.kind === 'task' && item.date) return item.date;
    let cur: Item | undefined = item;
    while (cur && cur.parentId) cur = byId.current[cur.parentId];
    return cur ? cur.date : null;
  }

  function parentRootDateOf(item: Item): string | null {
    let cur: Item | undefined = item;
    while (cur && cur.parentId) cur = byId.current[cur.parentId];
    return cur ? cur.date : null;
  }

  function recurringDates(item: Item): string[] {
    if (!item.recurrence || !item.date || item.parentId) return [];
    const dates: string[] = [];
    const start = parseKey(item.date);
    const end = item.recurrenceEnd
      ? parseKey(item.recurrenceEnd)
      : new Date(start.getFullYear() + 2, start.getMonth(), start.getDate());
    const cur = new Date(start);
    while (true) {
      switch (item.recurrence) {
        case 'daily':   cur.setDate(cur.getDate() + 1); break;
        case 'weekly':  cur.setDate(cur.getDate() + 7); break;
        case 'monthly': cur.setMonth(cur.getMonth() + 1); break;
        case 'yearly':  cur.setFullYear(cur.getFullYear() + 1); break;
        default: return dates;
      }
      if (cur > end || dates.length >= 730) break;
      dates.push(keyOfDate(cur));
    }
    return dates;
  }

  function homeEntry(item: Item): HomeEntry | null {
    const chain = chainUp(item);
    const rootDate = chain[0]?.date;
    if (!rootDate) return null;
    return { item, rootDate, ancestors: chain.slice(0, -1) };
  }

  function homeEntryForDate(item: Item, dateKey: string): HomeEntry | null {
    if (occurrenceHidden(item, dateKey)) return null;
    const chain = chainUp(item);
    const root = chain[0];
    if (item.kind === 'task' && item.date === dateKey) {
      if (chain.some((ancestor) => occurrenceHidden(ancestor, dateKey))) return null;
      return {
        item,
        rootDate: dateKey,
        ancestors: chain.slice(0, -1),
      };
    }
    if (!root?.date) return null;
    if (root.date !== dateKey && !recurringDates(root).includes(dateKey)) return null;
    if (chain.some((ancestor) => occurrenceHidden(ancestor, dateKey))) return null;
    return {
      item: effectiveItem(item, dateKey),
      rootDate: dateKey,
      ancestors: chain.slice(0, -1).map((ancestor) => effectiveItem(ancestor, dateKey)),
    };
  }

  function taskEntry(item: Item): TaskEntry {
    const chain = chainUp(item);
    const root = chain[0];
    const listId = item.listId ?? (item.parentId && byId.current[item.parentId]?.kind === 'list' ? item.parentId : null);
    return {
      item,
      rootDate: item.date ?? root?.date ?? null,
      ancestors: chain.slice(0, -1).filter((a) => a.kind !== 'list'),
      list: listId ? byId.current[listId] ?? null : null,
    };
  }

  function taskDateSortValue(entry: TaskEntry): string {
    return entry.rootDate ?? '9999-12-31';
  }

  function homeEntryRoot(entry: HomeEntry): Item {
    return entry.ancestors[0] ?? entry.item;
  }

  function homeEntryKey(entry: HomeEntry): string {
    return `${entry.item.id}:${entry.rootDate}`;
  }

  function sameHomeRoot(a: HomeEntry, b: HomeEntry): boolean {
    return homeEntryRoot(a).id === homeEntryRoot(b).id && a.rootDate === b.rootDate;
  }

  function sortHomeEntries(a: HomeEntry, b: HomeEntry): number {
    const rootA = homeEntryRoot(a);
    const rootB = homeEntryRoot(b);
    const sameRoot = rootA.id === rootB.id;
    if (sameRoot) {
      return (a.ancestors.length - b.ancestors.length)
        || ((a.item.time ?? '99:99').localeCompare(b.item.time ?? '99:99'))
        || (Number(b.item.important) - Number(a.item.important))
        || (a.item.createdAt - b.item.createdAt);
    }
    return ((rootA.time ?? '99:99').localeCompare(rootB.time ?? '99:99'))
      || (Number(b.item.important) - Number(a.item.important))
      || (rootA.createdAt - rootB.createdAt)
      || ((a.item.time ?? '99:99').localeCompare(b.item.time ?? '99:99'))
      || (a.item.createdAt - b.item.createdAt);
  }

  function sortTaskEntries(a: TaskEntry, b: TaskEntry): number {
    return (Number(b.item.needsAttention && !b.item.done) - Number(a.item.needsAttention && !a.item.done))
      || (Number(b.item.important && !b.item.done) - Number(a.item.important && !a.item.done))
      || (Number(a.item.done) - Number(b.item.done))
      || taskDateSortValue(a).localeCompare(taskDateSortValue(b))
      || ((a.item.time ?? '99:99').localeCompare(b.item.time ?? '99:99'))
      || (a.item.createdAt - b.item.createdAt);
  }

  function anyFlagged(id: string): boolean {
    if (byId.current[id]?.important) return true;
    return items.some((c) => c.parentId === id && anyFlagged(c.id));
  }

  function navTo(rootKey: string, ancestorIds?: string[]) {
    setSelKey(rootKey); setCursor(parseKey(rootKey)); setView('day');
    if (ancestorIds?.length) expandIds(ancestorIds);
  }

  function step(delta: number) {
    if (view === 'tasks' || view === 'lists') return;
    const c = new Date(view === 'home' ? parseKey(selKey) : cursor);
    if (view === 'home' || view === 'day') { c.setDate(c.getDate() + delta); setSelKey(keyOfDate(c)); if (view === 'home') setView('day'); }
    else if (view === 'week') { c.setDate(c.getDate() + delta * 7); }
    else if (view === 'month') { c.setMonth(c.getMonth() + delta); }
    else { c.setFullYear(c.getFullYear() + delta); }
    setCursor(c);
  }

  function goToday() { const n = new Date(); setCursor(n); setSelKey(todayKey()); setView('day'); }

  const dayTops = visibleItems
    .filter((i) => (
      !i.parentId && !occurrenceHidden(i, selKey) && (i.date === selKey || recurringDates(i).includes(selKey))
    ) || (
      i.parentId && i.kind === 'task' && i.date === selKey && parentRootDateOf(i) !== selKey && !occurrenceHidden(i, selKey)
    ))
    .sort((a, b) => {
      const ea = effectiveItem(a, selKey);
      const eb = effectiveItem(b, selKey);
      return (Number(eb.important) - Number(ea.important)) || ((ea.time ?? '99:99').localeCompare(eb.time ?? '99:99')) || (a.createdAt - b.createdAt);
    });

  const today = todayKey();
  const tomorrowDate = parseKey(today);
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrow = keyOfDate(tomorrowDate);
  const pendingInvite = invites[0] ?? null;

  const nowTime = `${pad(new Date().getHours())}:${pad(new Date().getMinutes())}`;
  const todayEntries = visibleItems
    .map((item) => homeEntryForDate(item, today))
    .filter((f): f is HomeEntry => f !== null);
  const tomorrowEntries = visibleItems
    .map((item) => homeEntryForDate(item, tomorrow))
    .filter((f): f is HomeEntry => f !== null);
  const todayEvents = todayEntries
    .filter((f) => f.item.kind === 'event')
    .sort((a, b) => (a.item.time ?? '99:99').localeCompare(b.item.time ?? '99:99') || (a.item.createdAt - b.item.createdAt));
  // Today's remaining items: past-timed events are dropped, done tasks are dropped
  const todayRemaining = todayEntries
    .filter((f) => {
      if (f.item.kind === 'event' && f.item.time) return (f.item.endTime ?? f.item.time) >= nowTime;
      if (f.item.kind === 'task') return !f.item.done;
      return true;
    })
    .sort(sortHomeEntries);
  const nextUp = todayRemaining.find((f) => f.item.kind === 'event' && f.item.time) ?? todayRemaining[0] ?? null;
  const tomorrowFirstEvents = tomorrowEntries
    .filter((f) => f.item.kind === 'event')
    .sort((a, b) => (a.item.time ?? '99:99').localeCompare(b.item.time ?? '99:99') || (a.item.createdAt - b.item.createdAt))
    .slice(0, 3);

  const taskEntries = visibleItems
    .filter((i) => i.kind === 'task')
    .map(taskEntry)
    .sort(sortTaskEntries);
  const realTaskEntries = taskEntries.filter((t) => !t.list);
  const openTaskEntries = realTaskEntries.filter((t) => !t.item.done);
  const needsAttentionTasks = realTaskEntries
    .filter((t) => t.item.needsAttention && !t.item.done)
    .sort((a, b) => a.item.createdAt - b.item.createdAt);
  const heroAttention = needsAttentionTasks[0] ?? null;
  const secondaryAttentionTasks = needsAttentionTasks.slice(1, 4);
  const heroPrimaryNested = !heroAttention && nextUp
    ? todayRemaining
        .filter((f) => f.item.id !== nextUp.item.id && sameHomeRoot(f, nextUp))
        .slice(0, 3)
        .map(heroFollowFromHome)
    : [];
  const heroPrimaryNestedKeys = new Set(heroPrimaryNested.map((item) => item.id));
  const heroScheduleFollows = [
    ...todayRemaining.filter((f) => f.item.id !== nextUp?.item.id && f.item.id !== heroAttention?.item.id),
    ...tomorrowFirstEvents.filter((f) => f.item.id !== heroAttention?.item.id),
  ]
    .filter((f) => !heroPrimaryNestedKeys.has(homeEntryKey(f)))
    .slice(0, 3)
    .map(heroFollowFromHome);
  const heroFollowItems = heroAttention
    ? [...secondaryAttentionTasks.map(heroFollowFromTask), ...heroScheduleFollows].slice(0, 3)
    : nextUp
      ? heroScheduleFollows
      : [];


  const allTopLevelLists = visibleItems.filter((i) => i.kind === 'list' && !i.parentId);
  const listsManuallyOrdered = allTopLevelLists.some((i) => i.sortOrder != null);
  const sortedLists = [...allTopLevelLists].sort((a, b) => listsManuallyOrdered
    ? ((a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER)) || (a.createdAt - b.createdAt)
    : (a.createdAt - b.createdAt));
  const allLists = sortedLists.filter((i) => !i.hidden);
  const hiddenLists = sortedLists.filter((i) => i.hidden);

  const stickyNotes = visibleItems
    .filter((i) => i.kind === 'note' && !i.date && !i.parentId && !i.listId)
    .sort((a, b) => (Number(b.important) - Number(a.important)) || (b.createdAt - a.createdAt));

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return visibleItems
      .filter((i) => !i.hidden || i.kind === 'list')
      .filter((i) => i.title.toLowerCase().includes(q) || (i.tagIds ?? []).some((id) => tagById[id]?.name.toLowerCase().includes(q)))
      .map((i) => {
        const entry = homeEntry(i);
        const listId = i.listId ?? (i.kind === 'list' ? i.id : null) ?? (i.parentId && byId.current[i.parentId]?.kind === 'list' ? i.parentId : null);
        return { item: i, entry, listId };
      })
      .sort((a, b) => {
        const da = a.entry?.rootDate ?? '';
        const db = b.entry?.rootDate ?? '';
        return da.localeCompare(db) || a.item.createdAt - b.item.createdAt;
      })
      .slice(0, 40);
  }, [searchQuery, visibleItems, tagById]);

  useEffect(() => {
    if (!searchOpen) return;
    function handle(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [searchOpen]);

  // Items belonging to a list — by listId (new) or legacy parentId with no date.
  // Once any item in a list has been manually dragged, sortOrder takes over
  // completely for that list; new items fall to the bottom until dragged too.
  const listItemsOf = (listId: string) => {
    const kids = items.filter((i) => !i.hidden && (i.listId === listId || (i.parentId === listId && !i.date)));
    const manual = kids.some((i) => i.sortOrder != null);
    return kids.sort((a, b) => manual
      ? ((a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER)) || (a.createdAt - b.createdAt)
      : (Number(b.important) - Number(a.important)) || (a.createdAt - b.createdAt));
  };

  const archivedListItems = allArchivedListItems();
  const libraryGroups = (() => {
    const grouped = new Map<string, Item[]>();
    for (const item of archivedListItems) {
      const listId = item.listId ?? (item.parentId && byId.current[item.parentId]?.kind === 'list' ? item.parentId : '__missing__');
      grouped.set(listId, [...(grouped.get(listId) ?? []), item]);
    }
    return [...grouped.entries()]
      .map(([listId, entries]) => ({
        listId,
        list: byId.current[listId] ?? null,
        entries,
      }))
      .sort((a, b) => (a.list?.title ?? 'Missing list').localeCompare(b.list?.title ?? 'Missing list'));
  })();

  function dayMeta(key: string) {
    const tops = visibleItems
      .filter((i) => (
        !i.parentId && !occurrenceHidden(i, key) && (i.date === key || recurringDates(i).includes(key))
      ) || (
        i.parentId && i.kind === 'task' && i.date === key && parentRootDateOf(i) !== key && !occurrenceHidden(i, key)
      ))
      .map((i) => effectiveItem(i, key))
      .sort((a, b) => (Number(b.important) - Number(a.important)) || ((a.time ?? '99:99').localeCompare(b.time ?? '99:99')) || (a.createdAt - b.createdAt));
    return {
      count: tops.length,
      imp: tops.some((t) => anyFlagged(t.id)),
      items: tops.map((t) => {
        const base = byId.current[t.id] ?? t;
        const occurrenceKey = recurringOccurrenceRoot(base, key) ? key : null;
        return { id: t.id, title: t.title, kind: t.kind, important: t.important, needsAttention: !!t.needsAttention, done: !!t.done, tagIds: t.tagIds ?? [], time: t.time ?? null, endTime: t.endTime ?? null, endDate: t.endDate ?? null, date: t.date ?? null, occurrenceKey, isGoogle: t.externalSource?.provider === 'google', googleColor: t.externalSource ? importById[t.externalSource.importId]?.color ?? '#4285f4' : null };
      }),
    };
  }

  async function updateItemDate(id: string, toKey: string | null, occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (!item) return;
    const root = recurringOccurrenceRoot(item, occurrenceKey);
    if (root && occurrenceKey) {
      const scope = await chooseOccurrenceScope('Move this item', occurrenceKey);
      if (scope === 'cancel') return;
      if (scope === 'occurrence') {
        if (!toKey) return;
        await materializeOccurrenceEdit(id, occurrenceKey, { date: toKey });
        setSelKey(toKey);
        setCursor(parseKey(toKey));
        return;
      }
    }
    const updated = await store.update(id, { date: toKey });
    const relativeKids = toKey
      ? (() => {
          const descendantIds = new Set<string>();
          const collect = (parentId: string) => {
            items.filter((i) => i.parentId === parentId).forEach((child) => {
              descendantIds.add(child.id);
              collect(child.id);
            });
          };
          collect(id);
          return items.filter((item) => descendantIds.has(item.id) && item.kind === 'task' && item.dueOffsetDays !== null && item.dueOffsetDays !== undefined);
        })()
      : [];
    const updatedChildren = await Promise.all(relativeKids.map((item) => store.update(item.id, {
      date: relativeDueDate(toKey, item.dueOffsetDays ?? null),
    })));
    const updatedById = new Map(updatedChildren.map((item) => [item.id, item]));
    setItems((p) => p.map((i) => (i.id === id ? updated : updatedById.get(i.id) ?? i)));
  }

  async function moveItem(id: string, toKey: string, occurrenceKey?: string | null) {
    const item = byId.current[id];
    if (recurringOccurrenceRoot(item, occurrenceKey) && occurrenceKey) {
      await updateItemDate(id, toKey, occurrenceKey);
      return;
    }
    if (item?.parentId && item.kind === 'task') {
      const updated = await store.update(id, { date: toKey, dueOffsetDays: null });
      setItems((p) => p.map((i) => (i.id === id ? updated : i)));
      return;
    }
    await updateItemDate(id, toKey);
  }

  function monthMeta(y: number, mm: number) {
    const prefix = `${y}-${pad(mm + 1)}-`;
    const tops = visibleItems.filter((i) => !i.parentId && i.date?.startsWith(prefix));
    const impCount = visibleItems.filter((i) => i.important && (rootDateOf(i) ?? '').startsWith(prefix)).length;
    return { imp: impCount, total: tops.length };
  }

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const visibleMonthSpan: MonthSpan = isPortrait ? 1 : monthSpan;
  const nextMonthDate = new Date(y, m + 1, 1);
  const monthNavTitle = visibleMonthSpan === 1
    ? `${MONTHS[m]} ${y}`
    : nextMonthDate.getFullYear() === y
      ? `${MONTHS[m]} - ${MONTHS[nextMonthDate.getMonth()]} ${y}`
      : `${MONTHS[m]} ${y} - ${MONTHS[nextMonthDate.getMonth()]} ${nextMonthDate.getFullYear()}`;
  const weekNavTitle = (() => {
    const sun = new Date(cursor); sun.setDate(cursor.getDate() - cursor.getDay());
    const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
    return sun.getMonth() === sat.getMonth()
      ? `${MON_ABBR[sun.getMonth()]} ${sun.getDate()} – ${sat.getDate()}, ${sun.getFullYear()}`
      : `${MON_ABBR[sun.getMonth()]} ${sun.getDate()} – ${MON_ABBR[sat.getMonth()]} ${sat.getDate()}, ${sat.getFullYear()}`;
  })();
  const navTitle = view === 'home' ? 'Home' : view === 'day' ? prettyDate(selKey) : view === 'week' ? weekNavTitle : view === 'month' ? monthNavTitle : view === 'year' ? `${y}` : view === 'tasks' ? 'Tasks' : view === 'lists' ? 'Lists' : view === 'notes' ? 'Sticky Notes' : view === 'organize' ? 'Organize' : 'Settings';

  function timeButtonLabel(value: string | null | undefined) {
    return value ? fmtTime(value) : 'No time';
  }

  function renderTimePopout({
    value,
    endValue,
    showEnd = false,
    onCommit,
    onClear,
  }: {
    value: string;
    endValue?: string;
    showEnd?: boolean;
    onCommit: (value: string, endValue?: string) => void;
    onClear: () => void;
  }) {
    return (
      <div className="pl-popout pl-time-popout" role="dialog" aria-label="Set time">
        <div className="pl-poptitle">Set time</div>
        <div className="pl-pophelp">This item will be sorted and shown with this time.</div>
        <form className="pl-poprow" onSubmit={(e) => {
          e.preventDefault();
          const form = new FormData(e.currentTarget);
          onCommit(String(form.get('time') ?? ''), showEnd ? String(form.get('endTime') ?? '') : undefined);
        }}>
          <input type="time" name="time" autoFocus defaultValue={value} aria-label={showEnd ? 'Start time' : 'Time'} />
          {showEnd && (
            <>
              <span style={{fontSize:12,color:'var(--ink-soft)'}}>to</span>
              <input type="time" name="endTime" defaultValue={endValue ?? ''} aria-label="End time" />
            </>
          )}
          <button className="pl-addbtn sm" type="submit">Done</button>
        </form>
        <button className="pl-linkbtn" onClick={onClear}>Leave untimed</button>
      </div>
    );
  }

  function TagStripe({ tagIds: ids, width = 12, height = 12, radius = 3 }: { tagIds: string[]; width?: number; height?: number; radius?: number }) {
    const colors = (ids ?? []).map((id) => tagById[id]?.color).filter(Boolean) as string[];
    if (!colors.length) return null;
    const pct = 100 / colors.length;
    const stops = colors.map((c, i) => `${c} ${i * pct}% ${(i + 1) * pct}%`).join(', ');
    return (
      <span style={{ display: 'inline-block', width, height, borderRadius: radius, background: `linear-gradient(90deg, ${stops})`, flexShrink: 0, verticalAlign: 'middle' }} />
    );
  }

  function EyeToggleIcon({ hidden }: { hidden: boolean }) {
    return (
      <svg className="pl-eye-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M3.4 12c2.2-3.1 5.1-4.7 8.6-4.7s6.4 1.6 8.6 4.7c-2.2 3.1-5.1 4.7-8.6 4.7S5.6 15.1 3.4 12Z"
          fill="none"
          stroke="var(--ink)"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="2.8" fill="var(--ink)" />
        {!hidden && <circle cx="13.15" cy="10.8" r=".85" fill="var(--mark)" />}
        {hidden && (
          <line
            x1="5.8"
            y1="18.2"
            x2="18.2"
            y2="5.8"
            stroke="var(--ink)"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        )}
      </svg>
    );
  }

  function tagRailBackground(ids: string[]): string | null {
    const colors = (ids ?? []).map((id) => tagById[id]?.color).filter(Boolean) as string[];
    if (!colors.length) return null;
    const pct = 100 / colors.length;
    return colors.map((c, i) => `${c} ${i * pct}% ${(i + 1) * pct}%`).join(', ');
  }

  function taskWhen(entry: TaskEntry): string {
    const date = entry.rootDate ? dateLabel(entry.rootDate) : 'No date';
    const time = taskTimeLabel(entry);
    return `${date}${time}`;
  }

  function homeWhen(entry: HomeEntry): string {
    const date = dateLabel(entry.rootDate);
    const time = homeTimeLabel(entry);
    return `${date}${time}`;
  }

  function dateLabel(dateKey: string): string {
    if (dateKey === today) return 'Today';
    if (dateKey === tomorrow) return 'Tomorrow';
    return shortDate(dateKey);
  }

  function dateLabelFrom(dateKey: string, referenceKey: string): string {
    if (dateKey === referenceKey) return 'today';
    const next = addDaysToKey(referenceKey, 1);
    if (dateKey === next) return 'tomorrow';
    return shortDate(dateKey);
  }

  function nestedTaskDateBadge(item: Item): string | null {
    if (item.kind !== 'task' || !item.parentId || !item.date) return null;
    const parentDate = parentRootDateOf(item);
    if (item.date === selKey && parentDate !== selKey) return 'Due today';
    if (item.dueOffsetDays !== null && item.dueOffsetDays !== undefined) return dueOffsetLabel(item.dueOffsetDays);
    return `Due ${dateLabelFrom(item.date, selKey)}`;
  }

  function homeTimeLabel(entry: HomeEntry): string {
    if ((entry.item.kind !== 'event' && entry.item.kind !== 'task') || !entry.item.time) return '';
    return ` ${entry.item.kind === 'event' ? eventTimeLabel(entry.item) : fmtTime(entry.item.time)}`;
  }

  function taskTimeLabel(entry: TaskEntry): string {
    if (!entry.item.time) return '';
    return ` ${entry.item.kind === 'event' ? eventTimeLabel(entry.item) : fmtTime(entry.item.time)}`;
  }

  function heroFollowFromHome(entry: HomeEntry): HeroFollowItem {
    return {
      id: homeEntryKey(entry),
      title: entry.item.title,
      kind: entry.item.kind,
      when: homeWhen(entry),
      dateKey: entry.rootDate,
      dateLabel: dateLabel(entry.rootDate),
      timeLabel: homeTimeLabel(entry).trim(),
      tagIds: entry.item.tagIds ?? [],
      homeEntry: entry,
    };
  }

  function heroFollowFromTask(entry: TaskEntry): HeroFollowItem {
    return {
      id: entry.item.id,
      title: entry.item.title,
      kind: entry.item.kind,
      when: taskWhen(entry),
      dateKey: entry.rootDate,
      dateLabel: entry.rootDate ? dateLabel(entry.rootDate) : 'No date',
      timeLabel: taskTimeLabel(entry).trim(),
      tagIds: entry.item.tagIds ?? [],
      taskEntry: entry,
    };
  }

  function openHeroFollow(item: HeroFollowItem) {
    if (item.taskEntry) {
      openTask(item.taskEntry);
      return;
    }
    if (item.homeEntry) navTo(item.homeEntry.rootDate, item.homeEntry.ancestors.map((a) => a.id));
  }

  function openTask(entry: TaskEntry) {
    if (entry.rootDate) {
      navTo(entry.rootDate, entry.ancestors.map((a) => a.id));
      return;
    }
    if (entry.list) {
      setView('lists');
      expandIds([entry.list.id]);
      return;
    }
    setView('tasks');
  }

  function openNotificationTarget(params: URLSearchParams) {
    const itemId = params.get('item');
    const targetView = params.get('view');
    const targetDate = params.get('date');
    const item = itemId ? byId.current[itemId] : undefined;

    if (targetView === 'tasks' || item?.kind === 'task') {
      setView('tasks');
    } else if ((targetView === 'day' || item?.kind === 'event' || targetDate) && (targetDate || item?.date)) {
      const key = targetDate || item?.date;
      if (key) {
        setSelKey(key);
        setCursor(parseKey(key));
        setView('day');
      }
    }

    window.history.replaceState({}, document.title, window.location.pathname);
  }

  function infoTip(label: string, text: string): React.ReactNode {
    return (
      <button className="pl-info" type="button" aria-label={label} title={text}>
        i
        <span className="pl-info-pop" role="tooltip">{text}</span>
      </button>
    );
  }

  function flagHelp(): React.ReactNode {
    return infoTip('What Flagged means', 'Flagged is a visual marker for anything important: events, lists, notes, or tasks. It does not mean the item is urgent.');
  }

  function attentionHelp(): React.ReactNode {
    return infoTip('What Needs Attention means', 'Needs Attention is task-only and urgent. It stays high on the home card and counts toward the app badge until the task is complete.');
  }

  function renderTaskRow(entry: TaskEntry): React.ReactNode {
    const item = entry.item;
    const editable = canEditItem(item);
    const deletable = canDeleteSubtree(item);
    const path = entry.ancestors.map((a) => a.title).join(' > ');
    return (
      <li key={item.id} className="pl-task-row" data-attention={item.needsAttention && !item.done} data-done={item.done}>
        {editable
          ? <button className="pl-check" data-done={item.done} aria-pressed={item.done} onClick={() => toggleDone(item.id)} aria-label={item.done ? 'Mark not done' : 'Mark done'}>{item.done ? '✓' : ''}</button>
          : <span className="pl-check" data-done={item.done} aria-hidden="true">{item.done ? '✓' : ''}</span>}
        <span className="pl-task-main">
          {titleEditing === item.id ? (
            <input
              className="pl-title-input"
              data-done={item.done}
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => saveTitle(item.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') { skipTitleSave.current = true; setTitleEditing(null); }
              }}
              aria-label="Task title"
            />
          ) : (
            editable
              ? <button className="pl-task-name" data-done={item.done} onClick={() => { setTitleEditing(item.id); setTitleDraft(item.title); }} title="Edit task">{item.title}</button>
              : <span className="pl-task-name" data-done={item.done}>{item.title}</span>
          )}
          <span className="pl-task-meta">
            {item.needsAttention && !item.done && <span className="pl-task-badge attention">Needs attention</span>}
            {item.important && <span className="pl-task-badge">Flagged</span>}
            <span className="pl-task-badge">{taskWhen(entry)}</span>
            {path && <span>{path}</span>}
            {entry.list && <span>List: {entry.list.title}</span>}
          </span>
        </span>
        <span className="pl-task-actions">
          <button className="pl-linkbtn" onClick={() => openTask(entry)}>
            {entry.rootDate ? 'Open day' : entry.list ? 'Open list' : 'No date'}
          </button>
          <span className="pl-action-with-info">
            <button className="pl-flag" aria-pressed={item.needsAttention} onClick={() => toggleNeedsAttention(item.id)} title={item.needsAttention ? 'Remove Needs Attention' : 'Mark Needs Attention'}>
              Attention
            </button>
            {attentionHelp()}
          </span>
          <span className="pl-action-with-info">
            <button className="pl-flag" aria-pressed={item.important} onClick={() => toggleImportant(item.id)} title={item.important ? 'Remove flag' : 'Flag task'}>
              {item.important ? 'Flagged' : 'Flag'}
            </button>
            {flagHelp()}
          </span>
          {deletable && <button className="pl-del" onClick={() => remove(item.id)} aria-label="Delete task">×</button>}
        </span>
      </li>
    );
  }

  function renderListItem(item: Item, dragCtx?: { scopeId: string; ids: string[]; onCommit: (order: string[]) => void }): React.ReactNode {
    const editable = canEditItem(item);
    const canDrag = Boolean(editable && dragCtx && dragCtx.ids.length > 1);
    const dragging = dragCtx ? listDrag.isDragging(dragCtx.scopeId, item.id) : false;
    return (
      <li
        key={item.id}
        className="pl-node"
        ref={dragCtx ? listDrag.registerRow(dragCtx.scopeId, item.id) : undefined}
        {...(canDrag && dragCtx ? listDrag.rowDragProps(dragCtx.scopeId, item.id, dragCtx.ids, dragCtx.onCommit, 'y', item.title) : {})}
      >
        <div className="pl-row pl-list-row" data-dragging={dragging || undefined}>
          <span className="pl-rowmain" data-kind="list-task">
            <span className="pl-kindcell">
              {editable
                ? <button className="pl-taskkind list-only" data-no-drag data-done={item.done} aria-pressed={item.done} onClick={() => toggleDone(item.id)} aria-label={item.done ? 'Mark task not done' : 'Mark task done'} title={item.done ? 'Mark not done' : 'Mark done'}>
                    <span className="pl-taskkind-box" aria-hidden="true">{item.done ? '✓' : ''}</span>
                  </button>
                : <span className="pl-taskkind list-only" data-done={item.done} aria-label={item.done ? 'Done' : 'Not done'}>
                    <span className="pl-taskkind-box" aria-hidden="true">{item.done ? '✓' : ''}</span>
                  </span>}
            </span>
            <span className="pl-titlewrap">
              {titleEditing === item.id ? (
                <input
                  className="pl-title-input"
                  data-done={item.done}
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={() => saveTitle(item.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') { skipTitleSave.current = true; setTitleEditing(null); }
                  }}
                  aria-label="List item title"
                />
              ) : (
                editable
                  ? <button className="pl-title" data-done={item.done} onClick={() => { setTitleEditing(item.id); setTitleDraft(item.title); }} title="Edit">
                      {item.important ? <span className="hl">{item.title}</span> : item.title}
                    </button>
                  : <span className="pl-title" data-done={item.done}>{item.important ? <span className="hl">{item.title}</span> : item.title}</span>
              )}
            </span>
          </span>
          <span className="pl-acts">
            {canDrag && dragCtx && (
              <span data-no-drag {...listDrag.dragHandleProps(dragCtx.scopeId, item.id, dragCtx.ids, dragCtx.onCommit, 'y', item.title)}>⠿</span>
            )}
            <button className="pl-iconbtn" data-no-drag aria-pressed={item.important} onClick={() => toggleImportant(item.id)} title={item.important ? 'Remove priority mark' : 'Mark important'} aria-label={item.important ? 'Remove priority mark' : 'Mark important'}>!</button>
            <button className="pl-info" data-no-drag type="button" onClick={() => setHistoryItemId(item.id)} aria-label={`Show history for ${item.title}`} title="History">i</button>
            {editable && <button className="pl-del" data-no-drag onClick={() => archiveListItem(item)} aria-label="Remove from active list" title="Remove from active list">×</button>}
          </span>
        </div>
      </li>
    );
  }

  function renderNode(baseItem: Item, occurrenceKey?: string | null): React.ReactNode {
    const item = effectiveItem(baseItem, occurrenceKey);
    const kids = visibleChildrenOf(baseItem.id, occurrenceKey);
    const hasKids = kids.length > 0;
    const open = !collapsed.has(item.id);
    const taskKids = kids.filter((k) => k.kind === 'task').map((k) => effectiveItem(k, occurrenceKey));
    const doneCount = taskKids.filter((k) => k.done).length;
    const editable = canEditItem(baseItem);
    const deletable = canDeleteSubtree(baseItem);
    const scheduleDate = scheduleDateForItem(item, occurrenceKey);
    const canSetReminder = !item.externalSource && (item.kind === 'event' || item.kind === 'task') && Boolean(scheduleDate && item.time);
    const reminderPreset = canSetReminder ? reminderPresetForItem(item, occurrenceKey) : 'off';
    const shownReminderPreset = remCustomFor === item.id ? 'custom' : reminderPreset;
    const customReminderValue = item.reminder ?? scheduledDateTimeValue(scheduleDate, item.time, 0) ?? '';
    return (
      <li key={item.id} className="pl-node">
        <div className="pl-row">
          <span className="pl-rowmain" data-kind={item.kind}>
            <span className="pl-kindcell">
              {hasKids && <button className="pl-disc" onClick={() => toggleCollapse(item.id)} aria-label={open ? 'Collapse' : 'Expand'} aria-expanded={open}>{open ? '▾' : '▸'}</button>}
              {item.kind === 'task'
                ? editable
                  ? <button className="pl-taskkind" data-done={item.done} aria-pressed={item.done} onClick={() => toggleDone(item.id, occurrenceKey)} aria-label={item.done ? 'Mark task not done' : 'Mark task done'}>
                      <span className="pl-taskkind-box" aria-hidden="true">{item.done ? '✓' : ''}</span>
                      Task
                    </button>
                  : <span className="pl-taskkind" data-done={item.done}>
                      <span className="pl-taskkind-box" aria-hidden="true">{item.done ? '✓' : ''}</span>
                      Task
                    </span>
                : <span className="pl-kindtag">{KIND_LABEL[item.kind]}</span>}
            </span>
            <span className="pl-titlewrap">
              {titleEditing === item.id ? (
                <input
                  className="pl-title-input"
                  data-done={item.kind === 'task' && item.done}
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={() => saveTitle(item.id, occurrenceKey)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') {
                      skipTitleSave.current = true;
                      setTitleEditing(null);
                    }
                  }}
                  aria-label={`${KIND_LABEL[item.kind]} title`}
                />
              ) : (
                editable
                  ? <button
                      className="pl-title"
                      data-done={item.kind === 'task' && item.done}
                      onClick={() => { setTitleEditing(item.id); setTitleDraft(item.title); }}
                      title="Edit title"
                    >
                      {item.important ? <span className="hl">{item.title}</span> : item.title}
                    </button>
                  : <span className="pl-title" data-done={item.kind === 'task' && item.done}>{item.important ? <span className="hl">{item.title}</span> : item.title}</span>
              )}
              {(item.kind === 'event' || item.kind === 'task') && item.time && <span className="pl-time">{item.kind === 'event' ? eventTimeLabel(item) : `at ${fmtTime(item.time)}`}</span>}
              {nestedTaskDateBadge(item) && <span className="pl-recur-badge">{nestedTaskDateBadge(item)}</span>}
              {(item.kind === 'event' || item.kind === 'task') && item.recurrence && <span className="pl-recur-badge">↻ {item.recurrence}</span>}
              {item.externalSource && (
                item.externalSource.eventUrl
                  ? <a className="pl-recur-badge" href={item.externalSource.eventUrl} target="_blank" rel="noopener noreferrer">Google</a>
                  : <span className="pl-recur-badge">Google</span>
              )}
              {item.location && (
                <a className="pl-location" href={`https://maps.google.com/maps?q=${encodeURIComponent(item.location)}`} target="_blank" rel="noopener noreferrer" title="Open in Google Maps">
                  📍 {item.location}
                </a>
              )}
              {hasKids && !open && taskKids.length > 0 && <span className="pl-prog">{doneCount}/{taskKids.length}</span>}
              {item.reminder && (
                <span className="pl-rem">⏰ {fmtReminder(item.reminder)}
                  <button onClick={() => setReminder(item.id, '', occurrenceKey)} aria-label="Clear reminder">×</button>
                </span>
              )}
              {(item.tagIds ?? []).length > 0 && (
                <span className="pl-label-badge" style={{display:'inline-flex',alignItems:'center',gap:5,background:'var(--paper-raised)',border:'1px solid var(--line)',color:'var(--ink)',paddingLeft:4}} title={(item.tagIds ?? []).map((id) => tagById[id]?.name).filter(Boolean).join(', ')}>
                  <TagStripe tagIds={item.tagIds ?? []} width={10} height={10} radius={2} />
                  {(item.tagIds ?? []).map((id) => tagById[id]?.name).filter(Boolean).join(', ')}
                </span>
              )}
              </span>
          </span>
          <span className="pl-acts">
            {!item.externalSource && (
              <span className="pl-action-with-info">
                <button className="pl-flag" aria-pressed={item.important} onClick={() => toggleImportant(item.id, occurrenceKey)} title={item.important ? 'Remove flag' : 'Flag as important'}>
                  {item.important ? 'Flagged' : 'Flag'}
                </button>
                {flagHelp()}
              </span>
            )}
            {editable && (item.kind === 'event' || item.kind === 'task') && (
              <span className="pl-timepick">
                <button
                  className="pl-ibtn pl-timebtn"
                  data-empty={!item.time}
                  aria-pressed={timeEditing === item.id}
                  onClick={() => setTimeEditing(timeEditing === item.id ? null : item.id)}
                  title="Set time"
                >
                  ◷ {timeButtonLabel(item.time)}
                </button>
                {timeEditing === item.id && renderTimePopout({
                  value: item.time ?? '',
                  endValue: item.endTime ?? '',
                  showEnd: item.kind === 'event',
                  onCommit: async (value, endValue) => {
                    await setItemTimeFromPicker(item.id, value, endValue, occurrenceKey);
                    setTimeEditing(null);
                  },
                  onClear: async () => { await setItemTimeFromPicker(item.id, '', '', occurrenceKey); setTimeEditing(null); },
                })}
              </span>
            )}
            {!item.externalSource && (item.kind === 'event' || item.kind === 'task') && (
              <button
                className="pl-ibtn"
                aria-pressed={remEditing === item.id}
                disabled={!canSetReminder}
                onClick={() => {
                  const next = remEditing === item.id ? null : item.id;
                  setRemEditing(next);
                  setRemCustomFor(next && reminderPreset === 'custom' ? item.id : null);
                }}
                title={canSetReminder ? 'Set reminder' : 'Add a date and time to set a reminder'}
              >
                ⏰
              </button>
            )}
            {editable && <button className="pl-ibtn" onClick={() => openAdd(item.id)} title="Add item inside">＋</button>}
            {editable && <span className="pl-listpick">
              <button className="pl-ibtn" aria-pressed={addToListFor === item.id} onClick={() => { if (addToListFor === item.id) { setAddToListFor(null); } else { setAddToListFor(item.id); setLocDraft(item.location ?? ''); } }} title="More actions" aria-label="More actions">☰</button>
              {addToListFor === item.id && (
                <div className="pl-backdrop" onClick={() => setAddToListFor(null)} />
              )}
              {addToListFor === item.id && (
                <div className="pl-popout" role="dialog" aria-label="More actions" style={{minWidth:'210px',right:0,maxHeight:'80vh',overflowY:'auto'}}>
                  <div className="pl-poptitle">Change type</div>
                  {(['note','task','event'] as Kind[]).filter((k) => k !== item.kind).map((k) => (
                    <button key={k} className="pl-linkbtn" style={{display:'block',width:'100%',textAlign:'left'}} onClick={async () => {
                      const root = recurringOccurrenceRoot(baseItem, occurrenceKey);
                      if (root && occurrenceKey) {
                        const scope = await chooseOccurrenceScope('Change type', occurrenceKey);
                        if (scope === 'cancel') return;
                        if (scope === 'occurrence') {
                          await materializeOccurrenceEdit(baseItem.id, occurrenceKey, { kind: k });
                          setAddToListFor(null);
                          return;
                        }
                      }
                      const updated = await store.update(item.id, { kind: k });
                      setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
                      setAddToListFor(null);
                    }}>
                      → {KIND_LABEL[k]}
                    </button>
                  ))}
                  <div style={{borderTop:'1px solid var(--line)',margin:'6px 0'}} />
                  <div className="pl-poptitle">Details</div>
                  {spaces.length > 0 && (
                    item.parentId ? (
                      <div className="pl-pophelp">Calendar: {(() => {
                        const parent = item.parentId ? byId.current[item.parentId] : undefined;
                        return parent?.spaceId ? spaces.find((s) => s.id === parent.spaceId)?.name ?? 'Shared' : 'Personal';
                      })()} via parent</div>
                    ) : (
                      <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,padding:'3px 0'}}>
                        <span style={{color:'var(--ink-soft)',minWidth:16}}>●</span>
                        <select
                          className="pl-recur sm"
                          style={{flex:1}}
                          value={item.spaceId ?? ''}
                          onChange={async (e) => {
                            const nextSpaceId = e.target.value || null;
                            await changeItemCalendar(item, nextSpaceId);
                            setAddToListFor(null);
                          }}
                          aria-label="Calendar"
                        >
                          {calendars.map((c) => <option key={c.id ?? 'personal'} value={c.id ?? ''}>{c.name}</option>)}
                        </select>
                      </label>
                    )
                  )}
                  {item.kind === 'task' && item.parentId && (
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,padding:'3px 0'}}>
                      <span style={{color:'var(--ink-soft)',minWidth:16}}>⏪</span>
                      <select
                        className="pl-recur sm"
                        style={{flex:1}}
                        value={item.dueOffsetDays !== null && item.dueOffsetDays !== undefined ? String(item.dueOffsetDays) : item.date ? 'custom' : ''}
                        onChange={async (e) => {
                          const value = e.target.value;
                          if (value === '') {
                            const updated = await store.update(item.id, { date: null, dueOffsetDays: null });
                            setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
                            return;
                          }
                          if (value === 'custom') {
                            const updated = await store.update(item.id, { dueOffsetDays: null });
                            setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
                            return;
                          }
                          const offsetDays = Number(value);
                          const nextDate = relativeDueDate(nearestAnchorDate(byId.current[item.parentId ?? '']), offsetDays);
                          const updated = await store.update(item.id, { date: nextDate, dueOffsetDays: offsetDays });
                          setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
                        }}
                        aria-label="Relative task due date"
                      >
                        {RELATIVE_DUE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </label>
                  )}
                  {((!item.parentId && item.date) || item.kind === 'task') && (
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,padding:'3px 0'}}>
                      <span style={{color:'var(--ink-soft)',minWidth:16}}>📅</span>
                      <input type="date" className="pl-recur sm" style={{flex:1}} value={item.parentId ? item.date ?? '' : occurrenceKey ?? item.date ?? ''} onChange={async (e) => { const nextDate = e.target.value || null; if (!nextDate && !item.parentId) return; if (item.parentId) { const updated = await store.update(item.id, { date: nextDate, dueOffsetDays: null }); setItems((p) => p.map((i) => (i.id === item.id ? updated : i))); } else { await updateItemDate(item.id, nextDate, occurrenceKey); } if (nextDate) { setSelKey(nextDate); setCursor(parseKey(nextDate)); } }} aria-label={item.parentId ? 'Task due date' : 'Change date'} />
                    </label>
                  )}
                  {(item.kind === 'event' || (item.kind === 'task' && !item.parentId && item.date)) && (
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,padding:'3px 0'}}>
                      <span style={{color:'var(--ink-soft)',minWidth:16}}>↻</span>
                      <select className="pl-recur sm" style={{flex:1}} value={baseItem.recurrence ?? ''} onChange={async (e) => { const updated = await store.update(item.id, { recurrence: e.target.value || null, recurrenceEnd: e.target.value ? baseItem.recurrenceEnd : null }); setItems((p) => p.map((i) => (i.id === item.id ? updated : i))); }} aria-label="Repeat">
                        <option value="">No repeat</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </label>
                  )}
                  {(item.kind === 'event' || (item.kind === 'task' && !item.parentId && item.date)) && baseItem.recurrence && (
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,padding:'3px 0'}}>
                      <span style={{color:'var(--ink-soft)',minWidth:16}}>⤶</span>
                      <input type="date" className="pl-recur sm" style={{flex:1}} value={baseItem.recurrenceEnd ?? ''} onChange={async (e) => { const updated = await store.update(item.id, { recurrenceEnd: e.target.value || null }); setItems((p) => p.map((i) => (i.id === item.id ? updated : i))); }} aria-label="Repeat until" />
                    </label>
                  )}
                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,padding:'3px 0'}}>
                    <span style={{color:'var(--ink-soft)',minWidth:16}}>📍</span>
                    <PlacesInput
                      value={locDraft}
                      onChange={setLocDraft}
                      onBlur={async () => { await updateItemForScope(item.id, { location: locDraft || null }, locDraft ? 'Change location' : 'Clear location', occurrenceKey); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                      placeholder="Add location…"
                      aria-label="Location"
                      style={{flex:1}}
                    />
                  </label>
                  <div style={{borderTop:'1px solid var(--line)',margin:'6px 0'}} />
                  <div className="pl-poptitle" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <span>Tags</span>
                    <button className="pl-linkbtn" style={{fontSize:10}} onClick={() => { setView('organize'); setAddToListFor(null); }}>manage →</button>
                  </div>
                  {tags.length === 0
                    ? <div className="pl-pophelp">No tags yet. <button className="pl-linkbtn" onClick={() => { setView('organize'); setAddToListFor(null); }}>Create one →</button></div>
                    : <div style={{display:'flex',flexDirection:'column',gap:3,padding:'2px 0'}}>
                        {tags.map((tag) => {
                          const active = (item.tagIds ?? []).includes(tag.id);
                          return (
                            <button key={tag.id} className="pl-linkbtn" style={{display:'flex',alignItems:'center',gap:7,textAlign:'left',fontWeight: active ? 700 : 400}}
                              onClick={async () => {
                                const current = item.tagIds ?? [];
                                const newTagIds = active ? current.filter((id) => id !== tag.id) : [...current, tag.id];
                                await updateItemForScope(item.id, { tagIds: newTagIds }, active ? 'Remove tag' : 'Add tag', occurrenceKey);
                              }}>
                              <span style={{width:10,height:10,borderRadius:'50%',background:tag.color,flexShrink:0,display:'inline-block'}} />
                              {active ? '✓ ' : ''}{tag.name}
                            </button>
                          );
                        })}
                        {(item.tagIds ?? []).length > 0 && (
                          <button className="pl-linkbtn" style={{fontSize:11,color:'var(--ink-soft)',marginTop:2}} onClick={async () => {
                            await updateItemForScope(item.id, { tagIds: [] }, 'Remove tags', occurrenceKey);
                          }}>✕ remove all tags</button>
                        )}
                      </div>
                  }
                  <div style={{borderTop:'1px solid var(--line)',margin:'6px 0'}} />
                  <div className="pl-poptitle">Add to list</div>
                  {sortedLists.length === 0 ? (
                    <div className="pl-pophelp">No lists yet.</div>
                  ) : (
                    sortedLists.map((list) => (
                      <button key={list.id} className="pl-linkbtn" style={{display:'block',width:'100%',textAlign:'left'}} onClick={async () => { await addItemToList(list.id, item.id); setAddToListFor(null); }}>
                        {item.listId === list.id ? '✓ ' : ''}{list.title}{list.hidden ? ' (hidden)' : ''}
                      </button>
                    ))
                  )}
                  <div style={{borderTop:'1px solid var(--line)',margin:'6px 0'}} />
                  <button className="pl-linkbtn" style={{display:'block',width:'100%',textAlign:'left'}} onClick={() => { setView('lists'); setAddToListFor(null); }}>Manage lists →</button>
                </div>
              )}
            </span>}
            {deletable && <button className="pl-del" onClick={() => remove(item.id, occurrenceKey)} aria-label="Delete">×</button>}
          </span>
        </div>

        {remEditing === item.id && canSetReminder && (
          <div className="pl-inline">
            <select
              className="pl-recur sm"
              value={shownReminderPreset}
              onChange={(e) => setReminderPreset(item.id, e.target.value, occurrenceKey)}
              aria-label="Reminder"
            >
              {REMINDER_PRESETS.map((preset) => (
                <option key={preset.value} value={preset.value}>{preset.label}</option>
              ))}
            </select>
            {shownReminderPreset === 'custom' && (
              <input type="datetime-local" value={customReminderValue} onChange={(e) => setReminder(item.id, e.target.value, occurrenceKey)} aria-label="Custom reminder time" />
            )}
            <button className="pl-addbtn sm" onClick={() => { setRemEditing(null); setRemCustomFor(null); }}>Done</button>
          </div>
        )}

        {addingTo === item.id && (
          <div className="pl-inline">
            <input
              type="text" autoFocus value={childDraft}
              onChange={(e) => setChildDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addChild(baseItem.id, occurrenceKey); if (e.key === 'Escape') setAddingTo(null); }}
              placeholder="Add inside…" aria-label="New nested item"
            />
            <div className="pl-kinds sm" role="group" aria-label="Item type">
              {(['note', 'task', 'event'] as Kind[]).map((k) => (
                <button key={k} aria-pressed={childKind === k} onClick={() => setChildKind(k)}>{k}</button>
              ))}
            </div>
            {(childKind === 'event' || childKind === 'task') && (
              <span className="pl-timepick">
                <button
                  className="pl-ibtn pl-timebtn"
                  data-empty={!childTime}
                  aria-pressed={childTimeOpen}
                  onClick={() => setChildTimeOpen(!childTimeOpen)}
                  title="Set time"
                >
                  ◷ {timeButtonLabel(childTime)}
                </button>
                {childTimeOpen && renderTimePopout({
                  value: childTime,
                  endValue: childEndTime,
                  showEnd: childKind === 'event',
                  onCommit: (value, endValue) => { setChildTime(value); setChildEndTime(endValue ?? ''); setChildTimeOpen(false); },
                  onClear: () => { setChildTime(''); setChildEndTime(''); setChildTimeOpen(false); },
                })}
              </span>
            )}
            {childKind === 'task' && (
              <>
                <select
                  className="pl-recur sm"
                  value={childDueMode}
                  onChange={(e) => {
                    const value = e.target.value;
                    setChildDueMode(value);
                    if (value !== '' && value !== 'custom') {
                      const parent = byId.current[baseItem.id];
                      const nextDate = relativeDueDate(nearestAnchorDate(parent, occurrenceKey), Number(value));
                      setChildDate(nextDate ?? '');
                    }
                  }}
                  aria-label="Task due date"
                  title="Task due date"
                >
                  {RELATIVE_DUE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                {childDueMode === 'custom' && (
                  <input
                    type="date"
                    className="pl-recur sm"
                    value={childDate}
                    onChange={(e) => setChildDate(e.target.value)}
                    aria-label="Custom task due date"
                    title="Custom task due date"
                  />
                )}
              </>
            )}
            {childKind === 'event' && (
              <select className="pl-recur sm" value={childRecurrence} onChange={(e) => setChildRecurrence(e.target.value)} aria-label="Repeat">
                <option value="">No repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            )}
            <button className="pl-addbtn sm" onClick={() => addChild(baseItem.id, occurrenceKey)}>Add</button>
            <button
              className="pl-del"
              onClick={() => {
                setAddingTo(null);
                setChildDraft('');
                setChildTime('');
                setChildDate('');
                setChildDueMode('');
                setChildTimeOpen(false);
              }}
              aria-label="Close nested item composer"
              title="Close"
            >
              ×
            </button>
          </div>
        )}

        {hasKids && open && <ul className="pl-children">{kids.map((k) => renderNode(k, occurrenceKey))}</ul>}
      </li>
    );
  }

  const historyItem = historyItemId ? byId.current[historyItemId] ?? null : null;
  const historyRows = historyItem?.completionHistory ?? [];
  const priceRows = historyItem?.priceHistory ?? [];
  const libraryRemoveItem = libraryRemoveItemId ? byId.current[libraryRemoveItemId] ?? null : null;

  return (
    <div className="pl">
      <style>{STYLES}</style>
      {historyItem && (
        <div className="pl-modal-backdrop" role="presentation" onMouseDown={() => setHistoryItemId(null)}>
          <div className="pl-modal" role="dialog" aria-modal="true" aria-label={`${historyItem.title} history`} onMouseDown={(e) => e.stopPropagation()}>
            <div className="pl-modal-title">{historyItem.title}</div>
            <p className="pl-modal-copy">
              {historyItem.lastCompletedAt
                ? `Last checked off ${fmtCompletionDateTime(historyItem.lastCompletedAt)}. ${historyItem.completedCount ?? historyRows.length} total.`
                : 'No checkoff history yet. The next time this list item is checked, WingMate will remember it.'}
            </p>
            <div className="pl-history-section">
              <div className="pl-history-heading">
                <div className="pl-history-title">
                  {historyRows.length > 0 ? (
                    <button
                      className="pl-history-toggle"
                      onClick={() => setCheckoffHistoryOpen((open) => !open)}
                      aria-expanded={checkoffHistoryOpen}
                      aria-label={`${checkoffHistoryOpen ? 'Collapse' : 'Expand'} checkoff history`}
                      title={`${checkoffHistoryOpen ? 'Collapse' : 'Expand'} checkoff history`}
                    >
                      {checkoffHistoryOpen ? '⌄' : '›'}
                    </button>
                  ) : (
                    <span className="pl-history-spacer" aria-hidden="true" />
                  )}
                  <span>Checkoff history</span>
                </div>
                <small>{historyRows.length} date{historyRows.length === 1 ? '' : 's'}</small>
              </div>
              {checkoffHistoryOpen && historyRows.length > 0 && (
                <ul className="pl-history-list">
                  {historyRows.map((row, idx) => (
                    <li key={`${row.at}-${idx}`}>
                      <input
                        className="pl-history-date"
                        type="datetime-local"
                        defaultValue={timestampToLocalInput(row.at)}
                        onBlur={(e) => void editCompletionHistoryAt(historyItem, idx, e.target.value)}
                        aria-label={`History date ${idx + 1}`}
                      />
                      <button
                        className="pl-history-remove"
                        onClick={() => void removeCompletionHistoryAt(historyItem, idx)}
                        aria-label="Remove history date"
                        title="Remove date"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="pl-history-section">
              <div className="pl-history-heading">
                <div className="pl-history-title">
                  {priceRows.length > 0 ? (
                    <button
                      className="pl-history-toggle"
                      onClick={() => {
                        const nextOpen = !priceHistoryOpen;
                        setPriceHistoryOpen(nextOpen);
                        if (!nextOpen) setPriceAddOpen(false);
                      }}
                      aria-expanded={priceHistoryOpen}
                      aria-label={`${priceHistoryOpen ? 'Collapse' : 'Expand'} price history`}
                      title={`${priceHistoryOpen ? 'Collapse' : 'Expand'} price history`}
                    >
                      {priceHistoryOpen ? '⌄' : '›'}
                    </button>
                  ) : (
                    <span className="pl-history-spacer" aria-hidden="true" />
                  )}
                  <span>Price history</span>
                  <button
                    className="pl-history-add"
                    onClick={() => {
                      setPriceHistoryOpen(true);
                      setPriceAddOpen((open) => !open);
                    }}
                    aria-expanded={priceAddOpen}
                    aria-label={`${priceAddOpen ? 'Hide' : 'Add'} price`}
                    title={`${priceAddOpen ? 'Hide' : 'Add'} price`}
                  >
                    +
                  </button>
                </div>
                <small>{priceRows.length > 0 ? `Latest ${formatPrice(priceRows[0].amountCents)}` : 'Optional'}</small>
              </div>
              {priceAddOpen && (
                <div className="pl-price-add">
                  <input
                    className="pl-history-date"
                    type="datetime-local"
                    value={priceDateDraft}
                    onChange={(e) => setPriceDateDraft(e.target.value)}
                    aria-label="Price date"
                  />
                  <input
                    className="pl-history-price"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={priceDraft}
                    onChange={(e) => setPriceDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void addPriceHistory(historyItem); }}
                    placeholder="$"
                    aria-label="Price amount"
                  />
                  <button onClick={() => void addPriceHistory(historyItem)} disabled={parsePriceCents(priceDraft) === null}>Add</button>
                </div>
              )}
              {priceHistoryOpen && priceRows.length > 0 && (
                <ul className="pl-history-list">
                  {priceRows.map((row, idx) => (
                    <li key={`${row.at}-${row.amountCents}-${idx}`}>
                      <input
                        className="pl-history-date"
                        type="datetime-local"
                        defaultValue={timestampToLocalInput(row.at)}
                        onBlur={(e) => void editPriceHistoryDateAt(historyItem, idx, e.target.value)}
                        aria-label={`Price date ${idx + 1}`}
                      />
                      <input
                        className="pl-history-price"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        defaultValue={centsToInput(row.amountCents)}
                        onBlur={(e) => void editPriceHistoryAmountAt(historyItem, idx, e.target.value)}
                        aria-label={`Price amount ${idx + 1}`}
                      />
                      <button
                        className="pl-history-remove"
                        onClick={() => void removePriceHistoryAt(historyItem, idx)}
                        aria-label="Remove price"
                        title="Remove price"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="pl-modal-actions">
              <button className="cancel" autoFocus onClick={() => setHistoryItemId(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {libraryRemoveItem && (
        <div className="pl-modal-backdrop" role="presentation" onMouseDown={() => setLibraryRemoveItemId(null)}>
          <div className="pl-modal" role="dialog" aria-modal="true" aria-label={`Remove ${libraryRemoveItem.title} from library`} onMouseDown={(e) => e.stopPropagation()}>
            <div className="pl-modal-title">Remove from Library?</div>
            <p className="pl-modal-copy">This permanently deletes "{libraryRemoveItem.title}" and its checkoff and price history.</p>
            <div className="pl-modal-actions">
              <button className="primary" autoFocus onClick={() => void permanentlyRemoveLibraryItem(libraryRemoveItem)}>Remove</button>
              <button className="cancel" onClick={() => setLibraryRemoveItemId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {occurrencePrompt && (
        <div className="pl-modal-backdrop" role="presentation" onMouseDown={() => resolveOccurrencePrompt('cancel')}>
          <div className="pl-modal" role="dialog" aria-modal="true" aria-label={`${occurrencePrompt.action} recurring item`} onMouseDown={(e) => e.stopPropagation()}>
            <div className="pl-modal-title">{occurrencePrompt.action}</div>
            <p className="pl-modal-copy">This item is part of a recurring series on {shortDate(occurrencePrompt.occurrenceKey)}.</p>
            <div className="pl-modal-actions">
              {(() => {
                const isDelete = occurrencePrompt.action.startsWith('Delete');
                return occurrencePrompt.mode === 'future' ? (
                  <>
                    <button className="primary" autoFocus onClick={() => resolveOccurrencePrompt('future')}>This and future occurrences</button>
                    <button onClick={() => resolveOccurrencePrompt('occurrence')}>Just this occurrence</button>
                  </>
                ) : (
                  <>
                    <button className={isDelete ? 'safe' : 'primary'} autoFocus onClick={() => resolveOccurrencePrompt('occurrence')}>{isDelete ? 'Delete only this occurrence' : 'This occurrence'}</button>
                    <button onClick={() => resolveOccurrencePrompt('series')}>{isDelete ? 'Delete entire series' : 'Apply to entire series'}</button>
                  </>
                );
              })()}
              <button className="cancel" onClick={() => resolveOccurrencePrompt('cancel')}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div className={`pl-shell${view === 'month' && visibleMonthSpan === 2 ? ' wide-month' : ''}`}>
        <div className="pl-top">
          <div className="pl-brand">
            <img
              className="pl-logo"
              src={
                __APP_ENV__ === 'staging'
                  ? '/brand-assets/logo-set/wingmate-horizontal-black-transparent-4x.png'
                  : '/brand-assets/wingmate-logo-horizontal-gray-baseline-gap30-transparent.png'
              }
              alt=""
              aria-hidden="true"
            />
            <span className="sr-only">WingMate</span>
          </div>
          <div className="pl-user">
            <span className="pl-user-email">{email}</span>
            <button className={`pl-refresh${refreshing ? ' spinning' : ''}`} onClick={handleRefresh} aria-label="Refresh" title="Refresh">
              <span>↻</span>
            </button>
            <span className="pl-sync-status" data-state={syncError ? 'error' : 'ok'} title={syncError || undefined}>
              {syncError ? 'Sync failed' : lastSyncedAt ? `Synced ${fmtSyncTime(lastSyncedAt)}` : 'Syncing'}
            </span>
            <button className="pl-signout" onClick={onSignOut}>Sign out</button>
          </div>
          <div ref={searchRef} className="pl-search-wrap">
            <span className="pl-search-icon">⌕</span>
            <input
              className="pl-search-input"
              type="search"
              placeholder="Search…"
              value={searchQuery}
              aria-label="Search items"
              onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
              onFocus={() => { if (searchQuery) setSearchOpen(true); }}
            />
            {searchQuery && (
              <button className="pl-search-clear" onClick={() => { setSearchQuery(''); setSearchOpen(false); }} aria-label="Clear search">×</button>
            )}
            {searchOpen && searchQuery && (
              <div className="pl-search-results" role="listbox" aria-label="Search results">
                {searchResults.length === 0 ? (
                  <div className="pl-search-empty">No items match "{searchQuery}"</div>
                ) : (
                  <>
                    <div className="pl-search-header">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</div>
                    {searchResults.map(({ item, entry, listId }) => {
                      const hasDate = !!entry;
                      const hasList = !!listId;
                      const showBoth = hasDate && hasList;
                      const isTask = item.kind === 'task';
                      const isOrphan = !hasDate && !hasList && !item.parentId && item.kind !== 'note' && !isTask;
                      const isStickyNote = item.kind === 'note' && !item.date && !item.parentId && !item.listId;
                      return (
                        <div key={item.id} className="pl-search-item" style={{display:'flex',flexDirection:'column',gap:0,padding:0}}>
                          <button
                            style={{width:'100%',textAlign:'left',border:'none',background:'transparent',padding:'9px 14px',display:'grid',gridTemplateColumns:'auto 1fr auto',gap:'8px',alignItems:'center',cursor:(hasDate || isStickyNote || isTask) ? 'pointer' : 'default',opacity:(hasDate || isStickyNote || isTask || isOrphan) ? 1 : 0.5}}
                            role="option"
                            onClick={() => {
                              if (isStickyNote) { setView('notes'); setSearchOpen(false); setSearchQuery(''); return; }
                              if (isTask && !hasDate) { setView('tasks'); setSearchOpen(false); setSearchQuery(''); return; }
                              if (!hasDate) return;
                              navTo(entry!.rootDate, entry!.ancestors.map((a) => a.id));
                              setSearchOpen(false);
                              setSearchQuery('');
                            }}
                          >
                            <span className="pl-search-kind">{isStickyNote ? '📌' : KIND_LABEL[item.kind]}</span>
                            <span className="pl-search-text">
                              <strong>{item.title}</strong>
                              {entry && entry.ancestors.length > 0 && <small>{entry.ancestors.map((a) => a.title).join(' › ')}</small>}
                              {isStickyNote && <small>Sticky note</small>}
                              {isTask && !hasDate && <small>{hasList ? 'List task' : 'No date task'}</small>}
                              {isOrphan && <small style={{color:'var(--mark)'}}>Not attached to any date or list</small>}
                            </span>
                            <span className="pl-search-when">
                              {hasDate ? (entry!.rootDate === today ? 'Today' : shortDate(entry!.rootDate)) : isTask ? 'Tasks' : '—'}
                            </span>
                          </button>
                          {showBoth && (
                            <div style={{display:'flex',gap:6,padding:'0 14px 8px',borderTop:'1px solid var(--line-soft)'}}>
                              <button className="pl-linkbtn" style={{fontSize:11}} onClick={() => { navTo(entry!.rootDate, entry!.ancestors.map((a) => a.id)); setSearchOpen(false); setSearchQuery(''); }}>→ Go to calendar day</button>
                              <button className="pl-linkbtn" style={{fontSize:11}} onClick={() => { setView('lists'); setSearchOpen(false); setSearchQuery(''); }}>→ Go to list</button>
                            </div>
                          )}
                          {!hasDate && hasList && (
                            <div style={{padding:'0 14px 8px'}}>
                              <button className="pl-linkbtn" style={{fontSize:11}} onClick={() => { setView('lists'); setSearchOpen(false); setSearchQuery(''); }}>→ Go to list</button>
                            </div>
                          )}
                          {isOrphan && (
                            <div style={{display:'flex',gap:6,padding:'0 14px 8px',borderTop:'1px solid var(--line-soft)'}}>
                              <button className="pl-linkbtn" style={{fontSize:11}} onClick={async () => {
                                const updated = await store.update(item.id, { kind: 'note' });
                                setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
                                setView('notes');
                                setSearchOpen(false);
                                setSearchQuery('');
                              }}>📌 Save as sticky note</button>
                              <button className="pl-linkbtn" style={{fontSize:11,color:'var(--mark)'}} onClick={async () => {
                                await remove(item.id);
                                setSearchOpen(false);
                                setSearchQuery('');
                              }}>✕ Delete</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="pl-views" role="group" aria-label="Switch view">
            {(['home', 'day', 'week', 'month', 'year', 'tasks', 'lists', 'notes', 'settings'] as View[]).map((v) => (
              <button
                key={v}
                aria-pressed={view === v}
                onClick={() => { setView(v); if (v === 'settings') setSettingsPane('home'); }}
                aria-label={v === 'settings' ? 'Settings' : undefined}
                title={v === 'settings' ? 'Settings' : undefined}
              >
                {v === 'settings' ? '⚙' : v}
              </button>
            ))}
          </div>
        </div>

        {pendingInvite && (
          <div className="pl-invite-banner" role="status" aria-live="polite">
            <div>
              <strong>{pendingInvite.invitedByEmail} invited you to {pendingInvite.spaceName}</strong>
              <small>{invites.length > 1 ? `${invites.length} shared calendar invites pending` : 'Shared calendar invite'}</small>
              {spaceError && <small style={{ color: 'var(--mark)' }}>{spaceError}</small>}
            </div>
            <div className="pl-invite-actions">
              <button className="pl-today" disabled={spaceBusy} onClick={() => void handleAcceptInvite(pendingInvite.spaceId)}>Join</button>
              <button className="pl-linkbtn" disabled={spaceBusy} onClick={() => void handleDeclineInvite(pendingInvite.spaceId)}>Decline</button>
              {invites.length > 1 && (
                <button className="pl-linkbtn" onClick={() => { setView('settings'); setSettingsPane('shared'); setSpaceError(''); }}>View all</button>
              )}
            </div>
          </div>
        )}

        {(spaces.length > 0 || calendarImports.length > 0) && ['home', 'day', 'month', 'year'].includes(view) && (
          <div className="pl-cal-legend" role="group" aria-label="Show or hide calendars">
            {calendars.map((c) => {
              const visible = isCalendarVisible(c.id);
              return (
                <button
                  key={c.id ?? 'personal'}
                  className="pl-cal-chip"
                  data-off={!visible}
                  onClick={() => toggleCalendar(c.id)}
                  aria-pressed={visible}
                  title={visible ? `Hide ${c.name}` : `Show ${c.name}`}
                >
                  <span className="pl-cal-chip-dot" style={{ background: c.color, opacity: visible ? 1 : 0.25 }} />
                  {c.name}
                </button>
              );
            })}
            {calendarImports.map((source) => {
              const key = importCalKey(source.id);
              const visible = !hiddenCalendars.has(key);
              return (
                <button
                  key={key}
                  className="pl-cal-chip"
                  data-off={!visible}
                  onClick={() => toggleCalendarKey(key)}
                  aria-pressed={visible}
                  title={visible ? `Hide ${source.calendarName}` : `Show ${source.calendarName}`}
                >
                  <span className="pl-cal-chip-dot" style={{ background: source.color, opacity: visible ? 1 : 0.25 }} />
                  {source.calendarName}
                </button>
              );
            })}
          </div>
        )}

        {!['home', 'tasks', 'lists', 'notes', 'organize', 'settings'].includes(view) && (
          <div className={`pl-nav${view === 'month' ? ' has-month-span' : ''}`}>
            <button className="pl-step" onClick={() => step(-1)} aria-label="Previous">‹</button>
            <button className="pl-step" onClick={() => step(1)} aria-label="Next">›</button>
            <button className="pl-today" onClick={goToday}>Today</button>
            <div className="title">{navTitle}</div>
            {view === 'month' && (
              <div className="pl-month-span" role="group" aria-label="Month display">
                {([1, 2] as MonthSpan[]).map((span) => (
                  <button
                    key={span}
                    aria-pressed={monthSpan === span}
                    onClick={() => setMonthSpan(span)}
                  >
                    {span} mo
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="pl-main">
          <div className="pl-panel">
            {view === 'home' && (
              <div className="pl-home">
                <div className="pl-home-hero">
                  <div className="pl-home-intro">
                    <div>
                      <div className="pl-home-date">{prettyDate(today)}</div>
                      <div className="pl-home-title">
                        {heroAttention ? 'Needs attention' : nextUp ? 'Next up' : todayRemaining.length === 0 ? 'All clear' : "What's left today"}
                      </div>
                    </div>
                    <div className="pl-home-sub">
                      {heroAttention
                        ? <div className="pl-hero-stack">
                            <div className="pl-hero-primary">
                              {(heroAttention.item.tagIds ?? []).length > 0 && (
                                <TagStripe tagIds={heroAttention.item.tagIds ?? []} width={8} height={8} radius={4} />
                              )}
                              <strong data-kind={heroAttention.item.kind}>{heroAttention.item.title}</strong>
                              <span>{taskWhen(heroAttention)}</span>
                              <button className="pl-linkbtn" onClick={() => toggleDone(heroAttention.item.id)}>Done</button>
                              <button className="pl-linkbtn" onClick={() => setView('tasks')}>{needsAttentionTasks.length} needs attention</button>
                            </div>
                            {heroFollowItems.length > 0 && (
                              <ul className="pl-hero-more" aria-label="Coming up next">
                                {heroFollowItems.map((item, idx) => {
                                  const previous = heroFollowItems[idx - 1];
                                  const showDayBreak = Boolean(item.dateKey && item.dateKey !== today && item.dateKey !== previous?.dateKey);
                                  return (
                                    <li key={item.id}>
                                      {showDayBreak && <div className="pl-hero-daybreak">{item.dateLabel}</div>}
                                      <div className="pl-hero-more-row">
                                        {(item.tagIds ?? []).length > 0 && (
                                          <TagStripe tagIds={item.tagIds} width={6} height={6} radius={3} />
                                        )}
                                        <button data-kind={item.kind} onClick={() => openHeroFollow(item)} title={item.title}>{item.title}</button>
                                        {(item.dateKey && item.dateKey !== today ? item.timeLabel : item.when) && (
                                          <span>{item.dateKey && item.dateKey !== today ? item.timeLabel : item.when}</span>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        : nextUp
                        ? <div className="pl-hero-stack">
                            <div className="pl-hero-primary">
                              {(nextUp.item.tagIds ?? []).length > 0 && (
                                <TagStripe tagIds={nextUp.item.tagIds ?? []} width={8} height={8} radius={4} />
                              )}
                              <strong data-kind={nextUp.item.kind}>{nextUp.item.title}</strong>
                              <span className="pl-hero-when">{homeWhen(nextUp)}</span>
                              {nextUp.item.location && <span>📍 {nextUp.item.location}</span>}
                            </div>
                            {heroPrimaryNested.length > 0 && (
                              <ul className="pl-hero-nested" aria-label="Related next up items">
                                {heroPrimaryNested.map((item) => (
                                  <li key={item.id}>
                                    {(item.tagIds ?? []).length > 0 && (
                                      <TagStripe tagIds={item.tagIds} width={6} height={6} radius={3} />
                                    )}
                                    <button data-kind={item.kind} onClick={() => openHeroFollow(item)} title={item.title}>{item.title}</button>
                                    <span>{item.when}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {heroFollowItems.length > 0 && (
                              <ul className="pl-hero-more" aria-label="Coming up next">
                                {heroFollowItems.map((item, idx) => {
                                  const previous = heroFollowItems[idx - 1];
                                  const showDayBreak = Boolean(item.dateKey && item.dateKey !== today && item.dateKey !== previous?.dateKey);
                                  return (
                                    <li key={item.id}>
                                      {showDayBreak && <div className="pl-hero-daybreak">{item.dateLabel}</div>}
                                      <div className="pl-hero-more-row">
                                        {(item.tagIds ?? []).length > 0 && (
                                          <TagStripe tagIds={item.tagIds} width={6} height={6} radius={3} />
                                        )}
                                        <button data-kind={item.kind} onClick={() => openHeroFollow(item)} title={item.title}>{item.title}</button>
                                        {(item.dateKey && item.dateKey !== today ? item.timeLabel : item.when) && (
                                          <span>{item.dateKey && item.dateKey !== today ? item.timeLabel : item.when}</span>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        : todayRemaining.length === 0
                          ? <span style={{color:'var(--ink-soft)'}}>Nothing else on the schedule.</span>
                          : <span style={{color:'var(--ink-soft)'}}>{todayRemaining.length} item{todayRemaining.length !== 1 ? 's' : ''} remaining</span>
                      }
                    </div>
                  </div>
                  <div className="pl-home-stats" aria-label="Today summary">
                    <button className="pl-stat" onClick={() => setView('tasks')} style={{textAlign:'left'}}><b>{needsAttentionTasks.length}</b><span>needs attention</span></button>
                    <button className="pl-stat" onClick={() => setView('tasks')} style={{textAlign:'left'}}><b>{openTaskEntries.length}</b><span>open tasks</span></button>
                    <div className="pl-stat"><b>{todayEvents.length}</b><span>events today</span></div>
                  </div>
                </div>

                <div className="pl-home-grid">
                  <section className="pl-quickadd">
                    <div className="pl-quickadd-head">
                      <h3>Quick Add</h3>
                      <span className="pl-quickadd-badge">AI-powered</span>
                      {infoTip('How Quick Add works', 'Type naturally and WingMate turns it into a note, task, event, list item, or update. No date usually becomes a sticky note unless it sounds like a task.')}
                    </div>
                    <div className="pl-quickadd-row">
                      <textarea
                        className="pl-quickadd-input"
                        placeholder={'e.g. "dentist appt Friday 3pm" or "call insurance sometime" — or paste a screenshot / attach a file'}
                        value={qaText}
                        onChange={e => setQaText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleQaParse(); }}
                        onPaste={handleQaPaste}
                        rows={2}
                        disabled={qaLoading}
                      />
                      <div className="pl-quickadd-btns">
                        {dictation.supported && (
                          <button
                            className={`pl-quickadd-attach pl-quickadd-mic${dictation.listening ? ' is-recording' : ''}`}
                            title={dictation.listening ? 'Stop dictation' : 'Dictate with microphone'}
                            aria-label={dictation.listening ? 'Stop dictation' : 'Dictate with microphone'}
                            aria-pressed={dictation.listening}
                            onClick={() => dictation.toggle(qaText)}
                            disabled={qaLoading}
                          >{dictation.listening ? '⏹' : '🎤'}</button>
                        )}
                        <button
                          className="pl-quickadd-attach"
                          title="Attach image, PDF, or spreadsheet"
                          onClick={() => qaFileRef.current?.click()}
                          disabled={qaLoading}
                        >📎</button>
                        <button className="pl-quickadd-btn" onClick={handleQaParse} disabled={qaLoading || (!qaText.trim() && !qaAttachment)}>
                          {qaLoading ? 'On it…' : 'Add It'}
                        </button>
                      </div>
                    </div>
                    <input
                      ref={qaFileRef}
                      type="file"
                      accept="image/*,.pdf,.csv,.xlsx,.xls,text/plain,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      style={{ display: 'none' }}
                      onChange={handleQaFileChange}
                    />
                    {qaAttachment && (
                      <div className="pl-quickadd-attachment">
                        {qaAttachment.previewUrl
                          ? <img className="pl-quickadd-thumb" src={qaAttachment.previewUrl} alt="attachment preview" />
                          : <span className="pl-quickadd-file-icon">📄</span>
                        }
                        <span className="pl-quickadd-file-name">{qaAttachment.name}</span>
                        <button className="pl-quickadd-remove" onClick={() => setQaAttachment(null)} title="Remove">✕</button>
                      </div>
                    )}
                    {dictation.listening && <p className="pl-quickadd-listening">🎙 Listening… speak now, tap ⏹ to stop.</p>}
                    {dictation.error && <p className="pl-quickadd-error">{dictation.error}</p>}
                    {qaError && <p className="pl-quickadd-error">{qaError}</p>}
                    {qaSuccess && <p className="pl-quickadd-success">{qaSuccess}</p>}
                    {qaPreview && (
                      <div className="pl-quickadd-preview">
                        {qaPreview.map((item, i) => {
                          const effectiveKind = qaKindOverrides[i] ?? item.kind;
                          const isSticky = effectiveKind === 'note' && !item.date;
                          const kindCycle: Record<string, string> = { note: 'task', task: 'event', event: 'note' };
                          const listName = item.action !== 'update' ? item.listName?.trim() : undefined;
                          const listExists = !!(listName && (item.listId || items.some((x) => x.kind === 'list' && !x.parentId && x.title.trim().toLowerCase() === listName.toLowerCase())));
                          return (
                            <div key={i} className={`pl-quickadd-preview-item${item.action === 'update' ? ' is-update' : ''}`}>
                              {item.action === 'update' ? (
                                <span className="pl-quickadd-preview-kind update">update</span>
                              ) : listName ? (
                                <span className="pl-quickadd-preview-kind" style={{fontFamily:'var(--mono)'}}>📋 list</span>
                              ) : (
                                <button
                                  className="pl-quickadd-preview-kind"
                                  title="Click to change type"
                                  style={{cursor:'pointer',userSelect:'none',fontFamily:'var(--mono)'}}
                                  onClick={() => setQaKindOverrides((prev) => ({ ...prev, [i]: kindCycle[effectiveKind] ?? 'note' }))}
                                >
                                  {isSticky ? '📌 sticky' : effectiveKind} ⇄
                                </button>
                              )}
                              <span className="pl-quickadd-preview-title">{item.title}</span>
                              {listName && <span className="pl-quickadd-preview-date">→ {listName}{listExists ? '' : ' (new list)'}</span>}
                              {item.changes && <span className="pl-quickadd-preview-changes">{item.changes}</span>}
                              {item.needsAttention && <span className="pl-quickadd-preview-kind">needs attention</span>}
                              {item.important && <span className="pl-quickadd-preview-kind">flagged</span>}
                              {!item.changes && item.date && <span className="pl-quickadd-preview-date">{item.date}{item.time ? ` ${item.time}${item.endTime ? `-${item.endTime}` : ''}` : ''}</span>}
                              {!item.changes && item.location && <span className="pl-quickadd-preview-date">📍 {item.location}</span>}
                              {item.parentId && <span className="pl-quickadd-preview-date">→ sub-item</span>}
                              {(item.tagIds ?? []).length > 0 && (
                                <span className="pl-quickadd-preview-date" style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                  {(item.tagIds ?? []).map((id) => { const tag = tagById[id]; return tag ? <span key={id} style={{display:'inline-flex',alignItems:'center',gap:3}}><span style={{width:8,height:8,borderRadius:'50%',background:tag.color,display:'inline-block'}} />{tag.name}</span> : null; })}
                                </span>
                              )}
                            </div>
                          );
                        })}
                        <div className="pl-quickadd-actions">
                          <button className="pl-quickadd-confirm" onClick={handleQaConfirm}>
                            {qaPreview.every(i => i.action === 'update') ? 'Update' : qaPreview.every(i => i.action !== 'update') ? `Add ${qaPreview.length} item${qaPreview.length !== 1 ? 's' : ''}` : 'Apply changes'}
                          </button>
                          <button className="pl-quickadd-cancel" onClick={() => { setQaPreview(null); setQaText(''); setQaError(''); setQaKindOverrides({}); }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </section>
                  {stickyNotes.length > 0 && (
                    <section className="pl-home-section">
                      <div className="pl-home-head">
                        <h3>Sticky Notes</h3>
                        <button className="pl-linkbtn" onClick={() => setView('notes')}>{stickyNotes.length} total →</button>
                      </div>
                      <div className="pl-sticky-home">
                        {stickyNotes.slice(0, 6).map((note) => (
                          <button key={note.id} className="pl-sticky-home-card" data-imp={note.important} onClick={() => setView('notes')} aria-label={note.title}>
                            <span className="pl-sticky-home-text" style={note.done ? {textDecoration:'line-through',opacity:0.6} : undefined}>{note.title}</span>
                            {note.important && <span style={{fontSize:10,color:'#b8860b',fontWeight:700}}>★ flagged</span>}
                          </button>
                        ))}
                      </div>
                    </section>
                  )}
                  <section className="pl-home-section">
                    <div className="pl-home-head">
                      <h3>Lists</h3>
                      <button className="pl-linkbtn" onClick={() => setView('lists')}>manage →</button>
                    </div>
                    {!loaded ? (
                      <div className="pl-home-empty">Loading...</div>
                    ) : allLists.length === 0 ? (
                      sortedLists.length > 0 ? (
                        <div className="pl-home-empty">No visible lists. <button className="pl-linkbtn" onClick={() => setView('lists')}>Manage hidden →</button></div>
                      ) : (
                      <div className="pl-home-empty">No lists yet. <button className="pl-linkbtn" onClick={() => setView('lists')}>Create one →</button></div>
                      )
                    ) : (
                      <div className="pl-lists-home">
                        {allLists.map((list) => {
                          const kids = listItemsOf(list.id);
                          const doneCount = kids.filter((k) => k.done).length;
                          const remaining = kids.filter((k) => !k.done);
                          return (
                            <button key={list.id} className="pl-lists-home-card" onClick={() => setView('lists')} aria-label={`${list.title} list`}>
                              <div className="pl-lists-home-head">
                                <span className="pl-lists-home-name">{list.title}</span>
                                <span className="pl-lists-home-prog">{doneCount}/{kids.length}</span>
                              </div>
                              <ul className="pl-lists-home-items">
                                {remaining.slice(0, 4).map((kid) => (
                                  <li key={kid.id} className="pl-lists-home-item" data-imp={kid.important}>
                                    {kid.important ? '★ ' : ''}{kid.title}
                                  </li>
                                ))}
                                {remaining.length > 4 && <li className="pl-lists-home-more">+{remaining.length - 4} more</li>}
                                {remaining.length === 0 && kids.length > 0 && <li className="pl-lists-home-more">All done!</li>}
                                {kids.length === 0 && <li className="pl-lists-home-more">Empty</li>}
                              </ul>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </section>
                </div>
              </div>
            )}
            {view === 'day' && (
              <div>
                <div className="pl-dayhead">
                  <div className="pl-daynum">{parseKey(selKey).getDate()}</div>
                  <div className="pl-daysub">
                    {parseKey(selKey).toLocaleDateString(undefined, { weekday: 'long' })}<br />
                    {parseKey(selKey).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                  </div>
                </div>
                <div className="pl-add">
                  <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addTop(); }} placeholder="Add a note, task, or event…" aria-label="New item" />
                  <div className="pl-kinds" role="group" aria-label="Item type">
                    {(['note', 'task', 'event'] as Kind[]).map((k) => (
                      <button key={k} aria-pressed={draftKind === k} onClick={() => setDraftKind(k)}>{k}</button>
                    ))}
                  </div>
                  {(draftKind === 'event' || draftKind === 'task') && (
                    <span className="pl-timepick">
                      <button
                        className="pl-ibtn pl-timebtn"
                        data-empty={!draftTime}
                        aria-pressed={draftTimeOpen}
                        onClick={() => setDraftTimeOpen(!draftTimeOpen)}
                        title="Set time"
                      >
                        ◷ {timeButtonLabel(draftTime)}
                      </button>
                      {draftTimeOpen && renderTimePopout({
                        value: draftTime,
                        endValue: draftEndTime,
                        showEnd: draftKind === 'event',
                        onCommit: (value, endValue) => { setDraftTime(value); setDraftEndTime(endValue ?? ''); setDraftTimeOpen(false); },
                        onClear: () => { setDraftTime(''); setDraftEndTime(''); setDraftTimeOpen(false); },
                      })}
                    </span>
                  )}
                  {(draftKind === 'event' || draftKind === 'task') && (
                    <select className="pl-recur" value={draftRecurrence} onChange={(e) => setDraftRecurrence(e.target.value)} aria-label="Repeat">
                      <option value="">No repeat</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  )}
                  {spaces.length > 0 && (
                    <select className="pl-cal-picker" value={draftSpaceId ?? ''} onChange={(e) => setDraftSpaceId(e.target.value || null)} aria-label="Calendar">
                      {calendars.map((c) => <option key={c.id ?? 'personal'} value={c.id ?? ''}>{c.name}</option>)}
                    </select>
                  )}
                  <button className="pl-addbtn" onClick={addTop}>Add</button>
                </div>
                {!loaded ? (
                  <div className="pl-empty">Loading…</div>
                ) : dayTops.length === 0 ? (
                  <div className="pl-empty">Nothing here yet. Add an event, then use ＋ to nest the tasks and lists it needs.</div>
                ) : (
                  <ul className="pl-list">{dayTops.map((t) => renderNode(t, t.recurrence ? selKey : null))}</ul>
                )}
              </div>
            )}
            {view === 'week' && <WeekView cursor={cursor} dayMeta={dayMeta} tagRailBackground={tagRailBackground} onPick={(key) => navTo(key)} onMoveItem={moveItem} weekStartHour={weekStartHour} />}
            {view === 'month' && <MonthView y={y} m={m} span={visibleMonthSpan} dayMeta={dayMeta} tagById={tagById} tagRailBackground={tagRailBackground} onPick={(key) => navTo(key)} onMoveItem={moveItem} />}
            {view === 'year' && <YearView y={y} monthMeta={monthMeta} onPick={(mm) => { setCursor(new Date(y, mm, 1)); setView('month'); }} />}
            {view === 'tasks' && (
              <div className="pl-task-page">
                <div className="pl-task-head">
                  <div>
                    <div className="pl-task-eyebrow">Tasks</div>
                    <div className="pl-task-title">Everything to do</div>
                    <p className="pl-task-copy">Tasks can have dates, live inside events, or have no date. List items stay with their lists.</p>
                  </div>
                  <div className="pl-task-stat" aria-label={`${openTaskEntries.length} open tasks`}>
                    <b>{openTaskEntries.length}</b>
                    <span>open</span>
                  </div>
                </div>
                <div className="pl-task-add">
                  <input
                    value={taskDraft}
                    onChange={(e) => setTaskDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addNoDateTask(); }}
                    placeholder="Add a no-date task..."
                    aria-label="New no-date task"
                  />
                  <span className="pl-task-create-actions">
                    <span className="pl-task-helpwrap">
                      <button
                        className="pl-task-toggle"
                        aria-pressed={taskDraftImportant}
                        onClick={() => setTaskDraftImportant((v) => !v)}
                        title={taskDraftImportant ? 'Remove Needs Attention' : 'Mark Needs Attention'}
                      >
                        <span className="pl-task-toggle-dot" />
                        <span className="pl-task-toggle-label">Needs<br />attention</span>
                      </button>
                      {attentionHelp()}
                    </span>
                    <span className="pl-task-helpwrap">
                      <button
                        className="pl-task-toggle"
                        aria-pressed={taskDraftFlagged}
                        onClick={() => setTaskDraftFlagged((v) => !v)}
                        title={taskDraftFlagged ? 'Remove flag' : 'Flag task'}
                      >
                        <span className="pl-task-toggle-dot" />
                        <span className="pl-task-toggle-label">Flag</span>
                      </button>
                      {flagHelp()}
                    </span>
                    <button className="pl-addbtn" onClick={addNoDateTask} disabled={!taskDraft.trim()}>Add</button>
                  </span>
                </div>
                {!loaded ? (
                  <div className="pl-empty">Loading...</div>
                ) : openTaskEntries.length === 0 ? (
                  <div className="pl-empty">No open tasks. List items live in Lists.</div>
                ) : (
                  <ul className="pl-task-list">{openTaskEntries.map((entry) => renderTaskRow(entry))}</ul>
                )}
              </div>
            )}
            {view === 'lists' && (
              <div className="pl-lists">
                {listDrag.preview && (
                  <div
                    className="pl-drag-preview"
                    style={{
                      width: listDrag.preview.width,
                      height: listDrag.preview.height,
                      transform: `translate(${listDrag.preview.x - listDrag.preview.grabX * listDrag.preview.width}px, ${listDrag.preview.y - listDrag.preview.grabY * listDrag.preview.height}px)`,
                    }}
                  >
                    {listDrag.preview.label}
                  </div>
                )}
                <div className="pl-lists-bar">
                  <input
                    className="pl-add"
                    style={{flex:'1',minWidth:'160px',border:'1px solid var(--line)',borderRadius:'9px',padding:'9px 12px',fontSize:'14px',color:'var(--ink)',background:'var(--paper)'}}
                    value={newListDraft}
                    onChange={(e) => setNewListDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') createList(newListDraft); }}
                    placeholder="New list name…"
                    aria-label="New list name"
                  />
                  <div className="pl-kinds" role="group" aria-label="Quick-create list">
                    {LIST_PRESETS.map((preset) => (
                      <button key={preset} onClick={() => createList(preset)}>{preset}</button>
                    ))}
                  </div>
                  <button className="pl-addbtn" onClick={() => createList(newListDraft)}>Create</button>
                </div>
                {!loaded ? (
                  <div className="pl-empty">Loading…</div>
                ) : sortedLists.length === 0 ? (
                  <div className="pl-empty">No lists yet. Type a name above and hit Create, or pick a quick preset.</div>
                ) : (
                  <>
                    {allLists.length === 0 ? (
                      <div className="pl-empty">All lists are hidden.</div>
                    ) : (
                      <div className="pl-lists-grid">
                        {listDrag.getOrder('__lists__', allLists.map((l) => l.id)).map((listId) => {
                          const list = byId.current[listId];
                          if (!list) return null;
                          const kids = listItemsOf(list.id);
                          const hideChecked = hiddenCheckedLists.has(list.id);
                          const visibleKids = hideChecked ? kids.filter((k) => !k.done) : kids;
                          const doneCount = kids.filter((k) => k.done).length;
                          const open = !collapsed.has(list.id);
                          const listEditable = canEditItem(list);
                          const listDeletable = canDeleteSubtree(list);
                          const listDragging = listDrag.isDragging('__lists__', list.id);
                          const listIds = allLists.map((l) => l.id);
                          return (
                            <div
                              key={list.id}
                              className="pl-list-card"
                              data-dragging={listDragging || undefined}
                              ref={listDrag.registerRow('__lists__', list.id)}
                              {...(listIds.length > 1 ? listDrag.rowDragProps('__lists__', list.id, listIds, persistOrder, 'grid', list.title) : {})}
                            >
                              <div className="pl-list-card-head">
                                <div className="pl-list-card-controls">
                                  {listIds.length > 1 && (
                                    <span data-no-drag {...listDrag.dragHandleProps('__lists__', list.id, listIds, persistOrder, 'grid', list.title)}>⠿</span>
                                  )}
                                  <button className="pl-disc" data-no-drag onClick={() => toggleCollapse(list.id)} aria-label={open ? 'Collapse' : 'Expand'} aria-expanded={open}>{open ? '▾' : '▸'}</button>
                                </div>
                                <div className="pl-list-card-title">
                                  {titleEditing === list.id ? (
                                    <input
                                      className="pl-title-input"
                                      autoFocus
                                      value={titleDraft}
                                      onChange={(e) => setTitleDraft(e.target.value)}
                                      onBlur={() => saveTitle(list.id)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.currentTarget.blur();
                                        if (e.key === 'Escape') { skipTitleSave.current = true; setTitleEditing(null); }
                                      }}
                                      aria-label="List name"
                                    />
                                  ) : (
                                    listEditable
                                      ? <button className="pl-list-name" onClick={() => { setTitleEditing(list.id); setTitleDraft(list.title); }} title="Rename list">
                                          {list.title}
                                        </button>
                                      : <span className="pl-list-name">{list.title}</span>
                                  )}
                                  {kids.length > 0 && <span className="pl-prog">{doneCount}/{kids.length}</span>}
                                </div>
                                <div className="pl-list-card-actions">
                                  {kids.length > 0 && (
                                    <button
                                      className="pl-iconbtn pl-eye-toggle"
                                      data-no-drag
                                      aria-pressed={hideChecked}
                                      onClick={() => toggleHideCheckedForList(list.id)}
                                      title={hideChecked ? 'Show checked items' : 'Hide checked items'}
                                      aria-label={hideChecked ? `Show checked items in ${list.title}` : `Hide checked items in ${list.title}`}
                                    >
                                      <EyeToggleIcon hidden={hideChecked} />
                                    </button>
                                  )}
                                  {listEditable && doneCount > 0 && (
                                    <button className="pl-list-edit-btn" data-no-drag onClick={() => uncheckListItems(list.id)} title="Uncheck all items">Reset</button>
                                  )}
                                  {listEditable && (
                                    <button className="pl-list-edit-btn" data-no-drag onClick={() => setListHidden(list, true)} title="Hide list">Hide</button>
                                  )}
                                  {listEditable && editingListId !== list.id && (
                                    <button className="pl-list-edit-btn" data-no-drag onClick={() => openListEditor(list)} title="Edit as text">Edit</button>
                                  )}
                                  {listDeletable && <button className="pl-del" data-no-drag onClick={() => remove(list.id)} aria-label="Delete list">×</button>}
                                </div>
                              </div>
                              {open && (
                                editingListId === list.id ? (
                                  <div className="pl-list-edit">
                                    <textarea
                                      className="pl-list-textarea"
                                      autoFocus
                                      value={listText}
                                      onChange={(e) => setListText(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') cancelListEditor();
                                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveListEditor(list.id);
                                      }}
                                      rows={Math.max(4, listText.split('\n').length + 1)}
                                      placeholder={'One item per line…\nMilk\nEggs\nBread'}
                                      aria-label={`Edit ${list.title} as text`}
                                    />
                                    <div className="pl-list-edit-actions">
                                      <button className="pl-addbtn sm" onClick={() => saveListEditor(list.id)}>Done</button>
                                      <button className="pl-linkbtn" onClick={cancelListEditor}>Cancel</button>
                                      <span className="pl-list-edit-hint">One item per line · ⌘↵ to finish</span>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {visibleKids.length > 0 && (
                                      <ul className="pl-list">
                                        {listDrag.getOrder(list.id, visibleKids.map((k) => k.id)).map((kidId) => {
                                          const kid = byId.current[kidId];
                                          return kid ? renderListItem(kid, { scopeId: list.id, ids: visibleKids.map((k) => k.id), onCommit: (order) => persistListItemOrder(list.id, order) }) : null;
                                        })}
                                      </ul>
                                    )}
                                    {visibleKids.length === 0 && kids.length > 0 && <div className="pl-empty">Checked items are hidden.</div>}
                                    {listEditable && (() => {
                                      const draft = quickAddDrafts[list.id] ?? '';
                                      const suggestions = listLibrarySuggestions(list.id, draft);
                                      return (
                                        <div className="pl-list-quickadd-wrap">
                                          <input
                                            className="pl-list-quickadd"
                                            type="text"
                                            enterKeyHint="done"
                                            value={draft}
                                            onChange={(e) => setQuickAddDrafts((d) => ({ ...d, [list.id]: e.target.value }))}
                                            onKeyDown={(e) => { if (e.key === 'Enter') addQuickItem(list.id); }}
                                            onBlur={() => addQuickItem(list.id)}
                                            placeholder="＋ Add an item…"
                                            aria-label={`Add item to ${list.title}`}
                                          />
                                          {suggestions.length > 0 && (
                                            <div className="pl-list-suggestions" role="listbox" aria-label={`${list.title} item library`}>
                                              {suggestions.map((suggestion) => (
                                                <button
                                                  key={suggestion.id}
                                                  className="pl-list-suggestion"
                                                  type="button"
                                                  role="option"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => restoreListItem(suggestion, list.id)}
                                                >
                                                  <strong>{suggestion.title}</strong>
                                                  <small>
                                                    {(suggestion.completedCount ?? 0) > 0
                                                      ? `${suggestion.completedCount}x${suggestion.lastCompletedAt ? ` · ${shortDate(new Date(suggestion.lastCompletedAt).toISOString().slice(0, 10))}` : ''}`
                                                      : 'Past item'}
                                                  </small>
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </>
                                )
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {hiddenLists.length > 0 && (
                      <div className="pl-list-section">
                        <div className="pl-list-section-head">
                          <h3>Hidden</h3>
                          <span>{hiddenLists.length} list{hiddenLists.length === 1 ? '' : 's'}</span>
                        </div>
                        <div className="pl-lists-grid">
                          {hiddenLists.map((list) => {
                            const kids = listItemsOf(list.id);
                            const doneCount = kids.filter((k) => k.done).length;
                            const listEditable = canEditItem(list);
                            const listDeletable = canDeleteSubtree(list);
                            return (
                              <div key={list.id} className="pl-list-card" data-hidden="true">
                                <div className="pl-list-card-head">
                                  <div className="pl-list-card-title">
                                    <span className="pl-list-name">{list.title}</span>
                                    {kids.length > 0 && <span className="pl-prog">{doneCount}/{kids.length}</span>}
                                  </div>
                                  <div className="pl-list-card-actions">
                                    {listEditable && doneCount > 0 && (
                                      <button className="pl-list-edit-btn" onClick={() => uncheckListItems(list.id)} title="Uncheck all items">Reset</button>
                                    )}
                                    {listEditable && (
                                      <button className="pl-list-edit-btn" onClick={() => setListHidden(list, false)} title="Show list">Restore</button>
                                    )}
                                    {listDeletable && <button className="pl-del" onClick={() => remove(list.id)} aria-label="Delete hidden list">×</button>}
                                  </div>
                                </div>
                                <div className="pl-empty">{kids.length === 0 ? 'Empty' : `${kids.length - doneCount} left`}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {view === 'notes' && (
              <div>
                <div className="pl-sticky-bar">
                  <input
                    value={stickyDraft}
                    onChange={(e) => setStickyDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addStickyNote(stickyDraft); }}
                    placeholder="Jot a quick note…  e.g. Pick up dry cleaning"
                    aria-label="New sticky note"
                  />
                  <button className="pl-addbtn" onClick={() => addStickyNote(stickyDraft)} disabled={!stickyDraft.trim()}>Add Note</button>
                </div>
                {!loaded ? (
                  <div className="pl-empty">Loading…</div>
                ) : stickyNotes.length === 0 ? (
                  <div className="pl-empty" style={{textAlign:'center',padding:'32px 16px'}}>
                    No sticky notes yet. Type something above and hit Add Note.
                  </div>
                ) : (
                  <div className="pl-sticky-grid">
                    {stickyNotes.map((note) => {
                      if (undoNote?.id === note.id) {
                        return (
                          <div key={note.id} className="pl-sticky-card" style={{opacity:0.55,background:'#fef9c3',borderColor:'#e9d92a'}}>
                            <div className="pl-sticky-tab" style={{background:'#f9e73a',borderColor:'#e9d92a'}}>
                              <span style={{fontSize:11,color:'#7a6000',fontFamily:'var(--mono)',letterSpacing:'.04em',flex:1,paddingLeft:2}}>deleted</span>
                            </div>
                            <div className="pl-sticky-body" style={{display:'flex',flexDirection:'column',gap:8,alignItems:'flex-start'}}>
                              <span style={{fontSize:14,lineHeight:1.5,color:'#7a6000',textDecoration:'line-through',wordBreak:'break-word'}}>{note.title}</span>
                              <button
                                style={{border:'1px solid #b8a840',background:'#fef9c3',color:'#7a6000',borderRadius:6,padding:'4px 12px',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}
                                onClick={undoDeleteStickyNote}
                              >
                                Undo
                              </button>
                            </div>
                          </div>
                        );
                      }
                      const noteEditable = canEditItem(note);
                      const noteDeletable = canDeleteSubtree(note);
                      return (
                      <div key={note.id} className="pl-sticky-card" data-imp={note.important}>
                        <div className="pl-sticky-tab">
                          <button
                            className="pl-sticky-flag"
                            aria-pressed={note.important}
                            onClick={() => toggleImportant(note.id)}
                            title={note.important ? 'Remove flag' : 'Flag as important'}
                          >
                            {note.important ? '★' : '☆'}
                          </button>
                          {noteEditable && (
                            <button
                              className="pl-sticky-flag"
                              onClick={() => { setStickyEditing(note.id); setStickyEditDraft(note.title); }}
                              title="Edit note"
                              aria-label="Edit note"
                            >✎</button>
                          )}
                          {noteDeletable && <button className="pl-sticky-del" onClick={() => deleteStickyNote(note)} aria-label="Delete note">×</button>}
                        </div>
                        <div className="pl-sticky-body">
                          {stickyEditing === note.id ? (
                            <textarea
                              className="pl-sticky-text"
                              autoFocus
                              value={stickyEditDraft}
                              onChange={(e) => setStickyEditDraft(e.target.value)}
                              onBlur={() => saveStickyNote(note.id, stickyEditDraft)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') setStickyEditing(null);
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) (e.target as HTMLTextAreaElement).blur();
                              }}
                              aria-label="Edit note"
                            />
                          ) : (
                            noteEditable
                              ? <button
                                  style={{border:'none',background:'transparent',padding:0,textAlign:'left',width:'100%',cursor:'pointer',font:'inherit',fontSize:14,lineHeight:1.5,color: note.done ? '#a08000' : '#3b2f00',whiteSpace:'pre-wrap',wordBreak:'break-word',textDecoration: note.done ? 'line-through' : 'none',opacity: note.done ? 0.6 : 1,transition:'all .15s'}}
                                  onClick={() => toggleDone(note.id)}
                                  title={note.done ? 'Click to unmark' : 'Click to mark done'}
                                >
                                  {note.title}
                                </button>
                              : <span style={{display:'block',fontSize:14,lineHeight:1.5,color: note.done ? '#a08000' : '#3b2f00',whiteSpace:'pre-wrap',wordBreak:'break-word',textDecoration: note.done ? 'line-through' : 'none',opacity: note.done ? 0.6 : 1}}>
                                  {note.title}
                                </span>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {view === 'organize' && (
              <div className="pl-organize">
                <div className="pl-organize-main">
                  <div className="pl-organize-head">
                    <div>
                      <div className="pl-organize-eyebrow">Organize</div>
                      <div className="pl-organize-title">Tags & categories</div>
                      <p className="pl-organize-copy">Create color-coded tags that can be assigned to events, tasks, and notes. Calendar items show every tag color as a compact stripe.</p>
                    </div>
                    <div className="pl-organize-stat" aria-label={`${tags.length} tags`}>
                      <b>{tags.length}</b>
                      <span>tag{tags.length === 1 ? '' : 's'}</span>
                    </div>
                  </div>

                  <div className="pl-tag-list">
                    {tags.length === 0 && <div className="pl-empty">No tags yet. Create your first one below.</div>}
                    {tags.map((tag) => {
                      const taggedCount = items.filter((i) => (i.tagIds ?? []).includes(tag.id)).length;
                      return (
                        <div key={tag.id} className="pl-tag-row" data-editing={editingTagId === tag.id}>
                          {editingTagId === tag.id ? (
                            <>
                              <div className="pl-color-grid" aria-label="Tag color">
                                {TAG_COLORS.map((c) => (
                                  <button
                                    key={c.value}
                                    className="pl-color-swatch"
                                    title={c.name}
                                    aria-label={c.name}
                                    aria-pressed={editTagColor === c.value}
                                    onClick={() => setEditTagColor(c.value)}
                                    style={{'--swatch': c.value} as React.CSSProperties}
                                  />
                                ))}
                              </div>
                              <input
                                type="text"
                                className="pl-recur sm"
                                style={{flex:'1 1 180px',minWidth:0}}
                                value={editTagName}
                                onChange={(e) => setEditTagName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Escape') setEditingTagId(null); }}
                                autoFocus
                                aria-label="Tag name"
                              />
                              <button className="pl-addbtn sm" onClick={async () => {
                                if (!editTagName.trim()) return;
                                const updated = await store.updateTag(tag.id, { name: editTagName.trim(), color: editTagColor });
                                setTags((p) => p.map((t) => (t.id === tag.id ? updated : t)));
                                setEditingTagId(null);
                              }}>Save</button>
                              <button className="pl-del" onClick={() => setEditingTagId(null)} aria-label="Cancel">×</button>
                            </>
                          ) : (
                            <>
                              <span className="pl-tag-dot" style={{background:tag.color}} />
                              <span className="pl-tag-name">{tag.name}</span>
                              <span className="pl-tag-count">
                                {taggedCount} item{taggedCount === 1 ? '' : 's'}
                              </span>
                              <button className="pl-ibtn" title="Edit" onClick={() => { setEditingTagId(tag.id); setEditTagName(tag.name); setEditTagColor(tag.color); }}>✎</button>
                              <button className="pl-del" aria-label="Delete tag" onClick={async () => {
                                await store.removeTag(tag.id);
                                setTags((p) => p.filter((t) => t.id !== tag.id));
                              }}>×</button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="pl-tag-new">
                    <div className="pl-tag-new-title">New tag</div>
                    <div className="pl-color-grid" aria-label="New tag color">
                      {TAG_COLORS.map((c) => (
                        <button
                          key={c.value}
                          className="pl-color-swatch"
                          title={c.name}
                          aria-label={c.name}
                          aria-pressed={newTagColor === c.value}
                          onClick={() => setNewTagColor(c.value)}
                          style={{'--swatch': c.value} as React.CSSProperties}
                        />
                      ))}
                    </div>
                    <div className="pl-tag-new-row">
                      <span className="pl-tag-dot" style={{background:newTagColor}} />
                      <input
                        type="text"
                        className="pl-recur"
                        style={{flex:1}}
                        value={newTagName}
                        onChange={(e) => { setNewTagName(e.target.value); setTagError(''); }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && newTagName.trim()) {
                            try {
                              const created = await store.createTag(newTagName.trim(), newTagColor);
                              setTags((p) => [...p, created]);
                              setNewTagName('');
                              setNewTagColor(TAG_COLORS[0].value);
                              setTagError('');
                            } catch (err) {
                              setTagError((err as Error).message ?? 'Failed to create tag');
                            }
                          }
                        }}
                        placeholder="Tag name, e.g. Mommy, Daddy, Charlotte..."
                        aria-label="New tag name"
                      />
                      <button className="pl-addbtn" disabled={!newTagName.trim()} onClick={async () => {
                        if (!newTagName.trim()) return;
                        try {
                          const created = await store.createTag(newTagName.trim(), newTagColor);
                          setTags((p) => [...p, created]);
                          setNewTagName('');
                          setNewTagColor(TAG_COLORS[0].value);
                          setTagError('');
                        } catch (err) {
                          setTagError((err as Error).message ?? 'Failed to create tag');
                        }
                      }}>Add</button>
                    </div>
                    {tagError && <div style={{fontSize:12,color:'#c0392b',marginTop:6}}>{tagError}</div>}
                  </div>
                </div>

                <aside className="pl-organize-side">
                  <h3>Planner structure</h3>
                  <p>Lists stay as active workspaces. Tags and categories live here as the organizing layer for filtering, grouping, and color.</p>
                  <div className="pl-organize-menu">
                    <button aria-pressed="true">Tags & categories</button>
                  </div>
                </aside>
              </div>
            )}
            {view === 'settings' && (
              <div className="pl-settings">
                <div className="pl-settings-head">
                  <div>
                    <div className="pl-settings-eyebrow">Settings</div>
                    <div className="pl-settings-title">{settingsPane === 'notifications' ? 'Notifications' : settingsPane === 'library' ? 'List Items Library' : settingsPane === 'shared' ? 'Shared calendars' : settingsPane === 'imports' ? 'Calendar imports' : 'WingMate'}</div>
                  </div>
                  {settingsPane !== 'home'
                    ? <button className="pl-settings-back" onClick={() => setSettingsPane('home')}>Back</button>
                    : <div className="pl-settings-email">{email}</div>}
                </div>

                {settingsPane === 'home' ? (
                  <div className="pl-settings-list">
                    <button className="pl-settings-row pl-settings-button" onClick={() => setSettingsPane('notifications')}>
                      <div>
                        <strong>Notifications</strong>
                        <small>
                          {alertsEnabled
                            ? notificationPermission === 'granted'
                              ? pushSubscribed ? 'Badges on · push alerts on' : 'Badges on · device not subscribed'
                              : notificationPermission === 'denied'
                                ? 'Badges on · alerts blocked'
                                : 'Badges on'
                            : 'Off'}
                        </small>
                      </div>
                      <span className="pl-settings-chevron">›</span>
                    </button>
                    <button className="pl-settings-row pl-settings-button" onClick={() => { setSettingsPane('shared'); setSpaceError(''); void loadSpaces(); }}>
                      <div>
                        <strong>Shared calendars</strong>
                        <small>
                          {spaces.length === 0 ? 'Share with family' : `${spaces.length} calendar${spaces.length === 1 ? '' : 's'}`}
                          {invites.length ? ` · ${invites.length} invite${invites.length === 1 ? '' : 's'}` : ''}
                        </small>
                      </div>
                      <span className="pl-settings-chevron">›</span>
                    </button>
                    <button className="pl-settings-row pl-settings-button" onClick={() => { setSettingsPane('imports'); setImportError(''); void loadCalendarImports(); }}>
                      <div>
                        <strong>Calendar imports</strong>
                        <small>{calendarImports.length === 0 ? 'Import from Google' : `${calendarImports.length} Google calendar${calendarImports.length === 1 ? '' : 's'}`}</small>
                      </div>
                      <span className="pl-settings-chevron">›</span>
                    </button>
                    <button className="pl-settings-row pl-settings-button" onClick={() => setSettingsPane('library')}>
                      <div>
                        <strong>List Items Library</strong>
                        <small>{archivedListItems.length} list item{archivedListItems.length === 1 ? '' : 's'} archived</small>
                      </div>
                      <span className="pl-settings-chevron">›</span>
                    </button>
                    <button className="pl-settings-row pl-settings-button" onClick={() => setView('organize')}>
                      <div>
                        <strong>Tags/Categories</strong>
                        <small>{tags.length} tag{tags.length === 1 ? '' : 's'}</small>
                      </div>
                      <span className="pl-settings-chevron">›</span>
                    </button>
                    <div className="pl-settings-row pl-theme-row">
                      <div>
                        <strong>Theme</strong>
                        <small>{themePreference === 'system' ? 'Follow device setting' : `${themePreference[0].toUpperCase()}${themePreference.slice(1)} mode`}</small>
                      </div>
                      <div className="pl-theme-options" role="group" aria-label="Theme preference">
                        {(['system', 'light', 'dark'] as ThemePreference[]).map((pref) => (
                          <button
                            key={pref}
                            type="button"
                            aria-pressed={themePreference === pref}
                            onClick={() => setThemePreference(pref)}
                          >
                            {pref}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="pl-settings-row">
                      <div>
                        <strong>Week view starts at</strong>
                        <small>First hour shown when opening week view</small>
                      </div>
                      <select
                        value={weekStartHour}
                        onChange={(e) => {
                          const h = parseInt(e.target.value, 10);
                          setWeekStartHour(h);
                          try { localStorage.setItem(WEEK_START_HOUR_KEY, String(h)); } catch {}
                        }}
                        style={{ fontFamily: 'var(--mono)', fontSize: 13, padding: '4px 8px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)' }}
                      >
                        {[0,5,6,7,8,9,10].map((h) => (
                          <option key={h} value={h}>{h === 0 ? 'Midnight' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : settingsPane === 'notifications' ? (
                  <div className="pl-settings-list">
                    <div className="pl-settings-row">
                      <div>
                        <strong>Alerts & app badge</strong>
                        <small>
                          {alertsEnabled
                            ? notificationPermission === 'granted'
                              ? pushSubscribed ? 'Badges on · push alerts on' : 'Badges on · device not subscribed'
                              : notificationPermission === 'denied'
                                ? 'Badges on · alerts blocked'
                                : notificationPermission === 'unsupported'
                                  ? 'Badges on where supported'
                                  : 'Badges on'
                            : 'Off'}
                        </small>
                      </div>
                      <button
                        className="pl-switch"
                        role="switch"
                        aria-checked={alertsEnabled}
                        disabled={pushBusy}
                        onClick={() => void setAlertsPreference(!alertsEnabled)}
                        aria-label={alertsEnabled ? 'Disable alerts and app badge' : 'Enable alerts and app badge'}
                        title={
                          alertsEnabled
                            ? 'Disable alerts and clear the app badge'
                            : notificationPermission === 'denied'
                              ? 'Enable app badge. Alerts are blocked in iPhone settings.'
                              : notificationPermission === 'unsupported'
                                ? 'Enable app badge where supported. Alerts are unavailable here.'
                                : 'Enable alerts and app badge'
                        }
                      >
                        <span />
                      </button>
                    </div>
                    {pushError && <div className="pl-settings-row" style={{ color: 'var(--mark)' }}>{pushError}</div>}
                    {!pushNotificationsSupported() && (
                      <div className="pl-note">On iPhone, push notifications require opening WingMate from the Home Screen.</div>
                    )}
                  </div>
                ) : settingsPane === 'library' ? (
                  <div className="pl-settings-list">
                    {archivedListItems.length === 0 ? (
                      <div className="pl-empty">No archived list items yet.</div>
                    ) : (
                      <div className="pl-library-list">
                        {libraryGroups.map((group) => (
                          <div key={group.listId} className="pl-library-group">
                            <div className="pl-library-group-head">
                              <strong>{group.list ? group.list.title : 'Missing list'}</strong>
                              <span>{group.entries.length} item{group.entries.length === 1 ? '' : 's'}{group.list?.hidden ? ' · hidden list' : ''}</span>
                            </div>
                            <ul className="pl-library-items">
                              {group.entries.map((item) => (
                                <li key={item.id} className="pl-library-item">
                                  <span className="pl-library-main">
                                    <strong>{item.title}</strong>
                                    <small>
                                      {(item.completedCount ?? 0) > 0
                                        ? `${item.completedCount} checkoff${item.completedCount === 1 ? '' : 's'}${item.lastCompletedAt ? ` · last ${fmtCompletionDateTime(item.lastCompletedAt)}` : ''}`
                                        : 'No checkoff history'}
                                    </small>
                                  </span>
                                  <span className="pl-library-actions">
                                    <button className="pl-info" type="button" onClick={() => setHistoryItemId(item.id)} aria-label={`Show history for ${item.title}`} title="History">i</button>
                                    {group.list && canEditItem(item) && (
                                      <button className="pl-list-edit-btn" onClick={() => restoreListItem(item, group.list!.id)} title="Restore to list">Restore</button>
                                    )}
                                    {canDeleteSubtree(item) && (
                                      <button className="pl-library-remove" onClick={() => setLibraryRemoveItemId(item.id)} title="Permanently remove from library">Remove</button>
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : settingsPane === 'imports' ? (
                  <div className="pl-settings-list">
                    {importError && <div className="pl-settings-row" style={{ color: 'var(--mark)' }}>{importError}</div>}

                    <div className="pl-settings-eyebrow" style={{ marginTop: 4 }}>Google Calendar</div>
                    <div className="pl-note" style={{ marginTop: -2 }}>Google calendars are imported one-way into Wingmate. Imported events stay read-only, and Wingmate does not write events back to Google.</div>

                    {googleAccountEmails.length > 0 && (
                      <>
                        <div className="pl-settings-eyebrow" style={{ marginTop: 4 }}>Connected accounts</div>
                        {googleAccountEmails.map((accountEmail) => (
                          <div key={accountEmail} className="pl-settings-row">
                            <div style={{ minWidth: 0 }}>
                              <strong>{accountEmail}</strong>
                              <small>{calendarImports.filter((source) => source.accountEmail === accountEmail).length} imported calendar{calendarImports.filter((source) => source.accountEmail === accountEmail).length === 1 ? '' : 's'}</small>
                            </div>
                            <button className="pl-linkbtn" style={{ color: 'var(--mark)' }} disabled={importBusy} onClick={() => void handleDisconnectGoogleAccount(accountEmail)}>Disconnect</button>
                          </div>
                        ))}
                      </>
                    )}

                    {googleCalendarCandidates.length > 0 && (
                      <>
                        <div className="pl-settings-eyebrow" style={{ marginTop: 4 }}>Available calendars</div>
                        {googleCalendarCandidates.map((candidate) => (
                          <div key={`${candidate.accountEmail}:${candidate.calendarId}`} className="pl-settings-row">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                              <span className="pl-cal-chip-dot" style={{ background: candidate.color }} />
                              <div style={{ minWidth: 0 }}>
                                <strong>{candidate.calendarName}</strong>
                                <small>{candidate.accountEmail}</small>
                              </div>
                            </div>
                            {candidate.selected
                              ? <span className="pl-status-pill">Imported</span>
                              : <button className="pl-today" disabled={importBusy} onClick={() => void handleCreateGoogleCalendarImport(candidate)}>Import</button>}
                          </div>
                        ))}
                      </>
                    )}

                    {calendarImports.length > 0 && <div className="pl-settings-eyebrow" style={{ marginTop: 4 }}>Imported calendars</div>}
                    {calendarImports.map((source) => (
                      <div key={source.id} className="pl-settings-row" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 9 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                              <span className="pl-cal-chip-dot" style={{ background: source.color }} />
                              <strong style={{ minWidth: 0 }}>{source.calendarName}</strong>
                            </div>
                            <span className="pl-status-pill" style={{ background: source.status === 'error' ? 'var(--mark)' : 'var(--brand-teal)' }}>{source.status.replace('_', ' ')}</span>
                          </div>
                          <small style={{ paddingLeft: 25 }}>
                            {source.accountEmail ?? 'Google Calendar'}
                            {source.lastSyncedAt ? ` · synced ${fmtSyncTime(source.lastSyncedAt)}` : ''}
                            {source.status === 'error' ? ' · needs attention' : ''}
                          </small>
                        </div>

                        {source.lastError && <div className="pl-note" style={{ color: 'var(--mark)' }}>{source.lastError}</div>}

                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12 }}>
                          <span>Event details</span>
                          <select
                            className="pl-recur sm"
                            value={source.privacy}
                            disabled={importBusy}
                            onChange={(e) => void handleUpdateCalendarImport(source.id, { privacy: e.target.value === 'busy' ? 'busy' : 'details' })}
                            aria-label="Imported event privacy"
                          >
                            <option value="details">Show details</option>
                            <option value="busy">Show as busy</option>
                          </select>
                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12 }}>
                          <span>All-day events</span>
                          <input type="checkbox" checked={source.includeAllDay} disabled={importBusy} onChange={(e) => void handleUpdateCalendarImport(source.id, { includeAllDay: e.target.checked })} />
                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12 }}>
                          <span>Declined events</span>
                          <input type="checkbox" checked={source.includeDeclined} disabled={importBusy} onChange={(e) => void handleUpdateCalendarImport(source.id, { includeDeclined: e.target.checked })} />
                        </label>

                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <button className="pl-linkbtn" disabled={importBusy} onClick={() => void handleSyncCalendarImport(source.id)}>Sync now</button>
                          <button className="pl-linkbtn" style={{ color: 'var(--mark)' }} disabled={importBusy} onClick={() => void handleRemoveCalendarImport(source.id)}>Disconnect</button>
                        </div>
                      </div>
                    ))}

                    {calendarImports.length === 0 && importsLoaded && <div className="pl-note">No outside calendars are connected yet.</div>}
                    {!importsLoaded && <div className="pl-note">Loading…</div>}

                    <button className="pl-today" disabled={importBusy} onClick={() => void handleConnectGoogleCalendar()}>
                      {importBusy ? 'Connecting…' : 'Connect Google Calendar'}
                    </button>
                  </div>
                ) : (
                  <div className="pl-settings-list">
                    {spaceError && <div className="pl-settings-row" style={{ color: 'var(--mark)' }}>{spaceError}</div>}

                    {invites.length > 0 && (
                      <>
                        <div className="pl-settings-eyebrow" style={{ marginTop: 4 }}>Invitations</div>
                        {invites.map((inv) => (
                          <div key={inv.spaceId} className="pl-settings-row">
                            <div>
                              <strong>{inv.spaceName}</strong>
                              <small>from {inv.invitedByEmail}</small>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="pl-today" disabled={spaceBusy} onClick={() => void handleAcceptInvite(inv.spaceId)}>Join</button>
                              <button className="pl-linkbtn" disabled={spaceBusy} onClick={() => void handleDeclineInvite(inv.spaceId)}>Decline</button>
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    <div className="pl-settings-eyebrow" style={{ marginTop: 4 }}>Your calendars</div>
                    <div className="pl-note" style={{ marginTop: -2 }}>All your calendars overlay together. Use the colored chips above the calendar to show or hide each one, and the calendar picker when adding an item to choose where it goes.</div>

                    {spaces.map((sp) => (
                      <div key={sp.id} className="pl-settings-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="pl-cal-chip-dot" style={{ background: sp.color }} />
                            <div>
                              <strong>{sp.name}</strong>
                              <small>{sp.members.length} member{sp.members.length === 1 ? '' : 's'}{sp.role === 'owner' ? ' · owner' : ''}</small>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          {sp.members.map((m) => (
                            <div key={m.userId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                              <span className="pl-build-env" style={{ fontSize: 10 }}>{m.email || m.userId}{m.role !== 'member' ? ` · ${m.role}` : ''}</span>
                              {(sp.role === 'owner' || sp.role === 'admin') && m.role !== 'owner' && (
                                <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {sp.role === 'owner' && m.role === 'member' && (
                                    <button className="pl-linkbtn" style={{ fontSize: 11 }} disabled={spaceBusy} onClick={() => void handleSetMemberRole(sp.id, m.userId, 'admin')}>Make admin</button>
                                  )}
                                  {sp.role === 'owner' && m.role === 'admin' && (
                                    <button className="pl-linkbtn" style={{ fontSize: 11 }} disabled={spaceBusy} onClick={() => void handleSetMemberRole(sp.id, m.userId, 'member')}>Remove admin</button>
                                  )}
                                  {(sp.role === 'owner' || m.role === 'member') && (
                                    <button className="pl-linkbtn" style={{ color: 'var(--mark)', fontSize: 11 }} disabled={spaceBusy} onClick={() => void handleRemoveMember(sp.id, m.userId)}>Remove</button>
                                  )}
                                </span>
                              )}
                            </div>
                          ))}
                          {(sp.pendingInvites ?? []).map((inv) => (
                            <div key={inv.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                              <span className="pl-build-env" style={{ fontSize: 10, background: 'var(--ink-soft)' }}>{inv.email} · pending</span>
                              {(sp.role === 'owner' || sp.role === 'admin') && (
                                <button className="pl-linkbtn" style={{ color: 'var(--mark)', fontSize: 11 }} disabled={spaceBusy} onClick={() => void handleRevokeInvite(sp.id, inv.email)}>Revoke</button>
                              )}
                            </div>
                          ))}
                        </div>
                        {invitingSpaceId === sp.id ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input className="pl-search-input" style={{ flex: 1 }} type="email" placeholder="name@email.com" value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') void handleInvite(sp.id); }} />
                            <button className="pl-today" disabled={spaceBusy || !inviteEmail.trim()} onClick={() => void handleInvite(sp.id)}>Send</button>
                            <button className="pl-linkbtn" onClick={() => { setInvitingSpaceId(null); setInviteEmail(''); }}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 12 }}>
                            {(sp.role === 'owner' || sp.role === 'admin') && (
                              <button className="pl-linkbtn" onClick={() => { setInvitingSpaceId(sp.id); setInviteEmail(''); setSpaceError(''); }}>+ Invite someone</button>
                            )}
                            {sp.role === 'owner'
                              ? <button className="pl-linkbtn" style={{ color: 'var(--mark)' }} disabled={spaceBusy} onClick={() => void handleDeleteSpace(sp.id)}>Delete</button>
                              : <button className="pl-linkbtn" style={{ color: 'var(--mark)' }} disabled={spaceBusy} onClick={() => void handleLeaveSpace(sp.id)}>Leave</button>}
                          </div>
                        )}
                      </div>
                    ))}

                    <div className="pl-settings-eyebrow" style={{ marginTop: 4 }}>New shared calendar</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="pl-search-input" style={{ flex: 1 }} type="text" placeholder="e.g. Family" value={newSpaceName}
                        onChange={(e) => setNewSpaceName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void handleCreateSpace(); }} />
                      <button className="pl-today" disabled={spaceBusy || !newSpaceName.trim()} onClick={() => void handleCreateSpace()}>Create</button>
                    </div>
                    {!spacesLoaded && <div className="pl-note">Loading…</div>}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="pl-note" style={{textAlign:'center',paddingBottom:8}}>Synced to the cloud · {__APP_ENV__ !== 'prod' && <span className="pl-build-env">{__APP_ENV__}</span>}{__GIT_HASH__}</div>
        </div>
      </div>
    </div>
  );
}

type DayItem = { id: string; title: string; kind: string; important: boolean; needsAttention: boolean; done: boolean; tagIds: string[]; time: string | null; endTime: string | null; endDate: string | null; date: string | null; occurrenceKey: string | null; isGoogle: boolean; googleColor: string | null };
type WeekTimedItem = {
  item: DayItem;
  top: number;
  height: number;
  titleTime: string;
  column: number;
  columns: number;
};

const WEEK_HOUR_H = 52;
const WEEK_HOURS = Array.from({ length: 24 }, (_, i) => i);

function weekTimedItemBase(item: DayItem): Omit<WeekTimedItem, 'column' | 'columns'> {
  const startM = minutesOfTime(item.time) ?? 0;
  const endM = item.endTime ? minutesOfTime(item.endTime) : null;
  const crossesDate = Boolean(item.endDate && item.endDate !== item.date);
  const sameDayEnd = endM !== null && !crossesDate && endM > startM;
  const durationM = crossesDate ? 1440 - startM : sameDayEnd ? endM - startM : 60;
  const top = (startM / 60) * WEEK_HOUR_H;
  const height = item.kind === 'task' ? 30 : Math.max(28, (durationM / 60) * WEEK_HOUR_H - 4);
  const titleTime = item.kind === 'event' ? eventTimeLabel(item) : fmtTime(item.time!);
  return { item, top, height, titleTime };
}

function layoutWeekTimedItems(items: DayItem[]): WeekTimedItem[] {
  const bases = items
    .map(weekTimedItemBase)
    .sort((a, b) => a.top - b.top || (b.height - a.height) || a.item.title.localeCompare(b.item.title));
  const laidOut: WeekTimedItem[] = [];
  let group: Array<Omit<WeekTimedItem, 'column' | 'columns'>> = [];
  let groupEnd = -1;

  const flushGroup = () => {
    if (group.length === 0) return;
    const colEnds: number[] = [];
    const groupLayout = group.map((entry) => {
      const firstOpen = colEnds.findIndex((end) => end <= entry.top);
      const column = firstOpen === -1 ? colEnds.length : firstOpen;
      colEnds[column] = entry.top + entry.height;
      return { ...entry, column, columns: 1 };
    });
    const columns = Math.max(1, colEnds.length);
    laidOut.push(...groupLayout.map((entry) => ({ ...entry, columns })));
    group = [];
    groupEnd = -1;
  };

  for (const entry of bases) {
    const entryEnd = entry.top + entry.height;
    if (group.length > 0 && entry.top >= groupEnd) flushGroup();
    group.push(entry);
    groupEnd = Math.max(groupEnd, entryEnd);
  }
  flushGroup();

  return laidOut.sort((a, b) => a.top - b.top || a.column - b.column || a.item.title.localeCompare(b.item.title));
}

function weekTimedItemStyle(entry: WeekTimedItem): React.CSSProperties {
  if (entry.columns <= 1) {
    return { top: entry.top, minHeight: entry.height, height: entry.height };
  }
  const width = 100 / entry.columns;
  return {
    top: entry.top,
    minHeight: entry.height,
    height: entry.height,
    left: `calc(${entry.column * width}% + 2px)`,
    right: 'auto',
    width: `calc(${width}% - 4px)`,
  };
}

function WeekView({
  cursor, dayMeta, tagRailBackground, onPick, onMoveItem, weekStartHour,
}: {
  cursor: Date;
  dayMeta: (k: string) => { count: number; imp: boolean; items: DayItem[] };
  tagRailBackground: (ids: string[]) => string | null;
  onPick: (key: string) => void;
  onMoveItem: (id: string, toKey: string, occurrenceKey?: string | null) => void;
  weekStartHour: number;
}) {
  const sun = new Date(cursor);
  sun.setDate(cursor.getDate() - cursor.getDay());
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sun);
    d.setDate(sun.getDate() + i);
    return d;
  });
  const tk = todayKey();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [nowY, setNowY] = useState<number | null>(null);
  const weekContainsToday = days.some((d) => keyOfDate(d) === tk);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const target = weekStartHour * WEEK_HOUR_H;
    if (el.scrollHeight > el.clientHeight) {
      el.scrollTop = target;
    } else {
      const main = el.closest('.pl-main') as HTMLElement | null;
      if (main) main.scrollTop = target;
    }
  }, [weekStartHour]);

  useEffect(() => {
    if (!weekContainsToday) { setNowY(null); return; }
    function tick() {
      const n = new Date();
      setNowY((n.getHours() + n.getMinutes() / 60) * WEEK_HOUR_H);
    }
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, [weekContainsToday]);

  const weekData = days.map((d) => {
    const key = keyOfDate(d);
    const { items } = dayMeta(key);
    return { key, d, timed: items.filter((i) => i.time), untimed: items.filter((i) => !i.time) };
  });

  const hasUntimed = weekData.some((wd) => wd.untimed.length > 0);
  const totalH = 24 * WEEK_HOUR_H;

  return (
    <div className="pl-week-outer">
      {/* Sticky header group: day labels + all-day strip */}
      <div className="pl-week-sticky">
        <div className="pl-week-head">
          <div className="pl-week-gutter" />
          {weekData.map(({ key, d }) => {
            const isToday = key === tk;
            return (
              <button key={key} className="pl-week-dh" data-today={isToday} onClick={() => onPick(key)} aria-label={prettyDate(key)}>
                <span className="pl-week-dletter">{DOW[d.getDay()][0]}</span>
                <span className="pl-week-dnum-wrap">
                  <span className="pl-week-dnum">{d.getDate()}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* All-day / untimed strip */}
        {hasUntimed && (
          <div className="pl-week-allday">
            <div className="pl-week-allday-label">all day</div>
            {weekData.map(({ key, untimed }) => (
              <div key={key} className="pl-week-allday-col" onClick={() => onPick(key)}>
                {untimed.slice(0, 3).map((item) => {
                  const tagRail = tagRailBackground(item.tagIds);
                  return (
                    <span
                      key={item.id}
                      className="pl-week-adc"
                      data-kind={item.kind}
                      data-imp={item.important}
                      data-attention={item.needsAttention}
                      data-done={item.done}
                      title={item.title}
                    >
                      {item.kind === 'task' && <span className="pl-week-task-check" data-done={item.done} aria-hidden="true">{item.done ? '✓' : ''}</span>}
                      <span className="pl-week-adc-title">{item.isGoogle && <span className="pl-google-badge" style={{ '--google-cal-color': item.googleColor ?? '#4285f4' } as React.CSSProperties} title="Google Calendar">G</span>} {item.title}</span>
                      {tagRail && <span className="pl-tag-rail" style={{ background: `linear-gradient(180deg,${tagRail})` }} />}
                    </span>
                  );
                })}
                {untimed.length > 3 && <span className="pl-week-adc-more">+{untimed.length - 3}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scrollable time grid */}
      <div className="pl-week-scroll" ref={scrollRef}>
        <div className="pl-week-timegrid" style={{ height: totalH }}>
          {/* Hour labels column */}
          <div className="pl-week-timelabels">
            {WEEK_HOURS.map((h) => (
              <div key={h} className="pl-week-hlabel" style={{ height: WEEK_HOUR_H }}>
                {h === 0 ? '' : new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric' })}
              </div>
            ))}
          </div>
          {/* Day columns */}
          <div className="pl-week-daycols" style={{ height: totalH }}>
            {/* Hour lines */}
            {WEEK_HOURS.map((h) => (
              <div key={h} className="pl-week-hline" style={{ top: h * WEEK_HOUR_H }} />
            ))}
            {/* Half-hour lines */}
            {WEEK_HOURS.map((h) => (
              <div key={`h${h}`} className="pl-week-hline-half" style={{ top: h * WEEK_HOUR_H + WEEK_HOUR_H / 2 }} />
            ))}
            {/* Current time indicator */}
            {nowY !== null && <div className="pl-week-now" style={{ top: nowY }} />}
            {/* Per-day columns */}
            {weekData.map(({ key, timed }) => {
              const isToday = key === tk;
              const timedLayout = layoutWeekTimedItems(timed);
              return (
                <div
                  key={key}
                  className="pl-week-dc"
                  data-today={isToday}
                  data-dragover={dragOver === key}
                  onClick={() => onPick(key)}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(null);
                    const id = e.dataTransfer.getData('text/plain');
                    const occurrenceKey = e.dataTransfer.getData('application/x-wingmate-occurrence-key') || null;
                    if (id) onMoveItem(id, key, occurrenceKey);
                  }}
                >
                  {timedLayout.map((entry) => {
                    const { item, titleTime } = entry;
                    const tagRail = tagRailBackground(item.tagIds);
                    return (
                      <div
                        key={item.id}
                        className="pl-week-ev"
                        data-imp={item.important}
                        data-done={item.done}
                        data-kind={item.kind}
                        data-attention={item.needsAttention}
                        data-overlap={entry.columns > 1}
                        style={weekTimedItemStyle(entry)}
                        onClick={(e) => { e.stopPropagation(); onPick(key); }}
                        title={`${titleTime} — ${item.title}`}
                      >
                        {item.kind === 'task' && <span className="pl-week-task-check" data-done={item.done} aria-hidden="true">{item.done ? '✓' : ''}</span>}
                        <span className="pl-week-ev-time">{titleTime}</span>
                        <span className="pl-week-ev-title">{item.isGoogle && <span className="pl-google-badge" style={{ '--google-cal-color': item.googleColor ?? '#4285f4' } as React.CSSProperties} title="Google Calendar">G</span>} {item.title}</span>
                        {tagRail && <span className="pl-tag-rail" style={{ background: `linear-gradient(180deg,${tagRail})` }} />}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function MonthView({
  y, m, span, dayMeta, tagById, tagRailBackground, onPick, onMoveItem,
}: {
  y: number;
  m: number;
  span: MonthSpan;
  dayMeta: (k: string) => { count: number; imp: boolean; items: DayItem[] };
  tagById: Record<string, { name: string; color: string }>;
  tagRailBackground: (ids: string[]) => string | null;
  onPick: (key: string) => void;
  onMoveItem: (id: string, toKey: string, occurrenceKey?: string | null) => void;
}) {
  const tk = todayKey();

  const [dragOver, setDragOver] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const MAX_CHIPS = span === 2 ? 2 : 3;

  function renderMonth(offset: number) {
    const monthDate = new Date(y, m + offset, 1);
    const monthYear = monthDate.getFullYear();
    const monthIndex = monthDate.getMonth();
    const startDow = monthDate.getDay();
    const daysInMonth = new Date(monthYear, monthIndex + 1, 0).getDate();
    const cells: (number | null)[] = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <div key={`${monthYear}-${monthIndex}`} className="pl-month-block">
        <h3 className="pl-month-block-title">{MONTHS[monthIndex]} {monthYear}</h3>
        <div className="pl-dowrow">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
        <div className="pl-grid">
          {cells.map((d, idx) => {
            if (d === null) return <div key={idx} className="pl-cell" data-empty="true" aria-hidden="true" />;
            const key = keyOf(monthYear, monthIndex, d);
            const { count, items: dayItems } = dayMeta(key);
            const visible = dayItems.slice(0, MAX_CHIPS);
            const extra = count - visible.length;
            return (
              <div
                key={idx}
                className="pl-cell"
                data-today={key === tk}
                data-dragover={dragOver === key}
                role="button"
                tabIndex={0}
                aria-label={`${prettyDate(key)}, ${count} item${count === 1 ? '' : 's'}`}
                onClick={() => onPick(key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPick(key); }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(null);
                  const id = e.dataTransfer.getData('text/plain');
                  const occurrenceKey = e.dataTransfer.getData('application/x-wingmate-occurrence-key') || null;
                  if (id) onMoveItem(id, key, occurrenceKey);
                }}
              >
                <span className="n">{d}</span>
                <div className="pl-cell-chips" onClick={(e) => e.stopPropagation()}>
                  {visible.map((item) => {
                    const tagRail = tagRailBackground(item.tagIds);
                    return (
                      <span
                        key={item.id}
                        className={'pl-chip' + (draggingId === item.id ? ' dragging' : '')}
                        data-kind={item.kind}
                        data-imp={item.important}
                        data-attention={item.needsAttention}
                        data-done={item.done}
                        draggable
                        title={item.tagIds.length > 0 ? `${item.title} · ${item.tagIds.map((id) => tagById[id]?.name).filter(Boolean).join(', ')}` : item.title}
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', item.id);
                          if (item.occurrenceKey) e.dataTransfer.setData('application/x-wingmate-occurrence-key', item.occurrenceKey);
                          e.dataTransfer.effectAllowed = 'move';
                          setDraggingId(item.id);
                        }}
                        onDragEnd={() => setDraggingId(null)}
                        onClick={(e) => { e.stopPropagation(); onPick(key); }}
                      >
                        <span className="pl-chip-title">
                          {item.kind === 'task' && <span className="pl-task-glyph" data-done={item.done} aria-hidden="true">{item.done ? '✓' : ''}</span>}
                          {item.isGoogle && <span className="pl-google-badge" style={{ '--google-cal-color': item.googleColor ?? '#4285f4' } as React.CSSProperties} title="Google Calendar">G</span>}
                          {item.title}
                        </span>
                        {tagRail && <span className="pl-tag-rail" style={{ background: `linear-gradient(180deg,${tagRail})` }} />}
                      </span>
                    );
                  })}
                  {extra > 0 && <span className="pl-cell-more">+{extra} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="pl-months" data-span={span}>
      {Array.from({ length: span }, (_, offset) => renderMonth(offset))}
    </div>
  );
}

function YearView({ y, monthMeta, onPick }: { y: number; monthMeta: (y: number, m: number) => { imp: number; total: number }; onPick: (mm: number) => void }) {
  return (
    <div className="pl-year">
      {MONTHS.map((name, mm) => {
        const { imp, total } = monthMeta(y, mm);
        const filled = Math.min(imp, 6);
        return (
          <button key={mm} className="pl-mini" onClick={() => onPick(mm)} aria-label={`${name}, ${imp} flagged of ${total}`}>
            <span className="mn">{MON_ABBR[mm]}</span>
            <span className="pl-bars">{Array.from({ length: 6 }).map((_, k) => <i key={k} className={k < filled ? 'on' : ''} />)}</span>
            <span className="stat"><b>{imp}</b> flagged · {total} total</span>
          </button>
        );
      })}
    </div>
  );
}
